import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { Platform } from "react-native";
import db, { analytics, storage } from "./FirestoreConfig";
import Block from "./Block";
import Workout from "./Workout";
import Exercise from "./Exercise";
import Serie from "./Serie";
import { Client } from "./Client";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  limit,
  doc,
  writeBatch,
  updateDoc,
  arrayUnion,
  setDoc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
  deleteField,
  orderBy,
  deleteDoc,
  collectionGroup,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import Trac from "./Trac";
import Bodyweight from "./Bodyweight";
import PredefinedExercise from "./PredefinedExercise";
import Attribute from "./Attribute";
import { useTranslation } from "react-i18next";
import i18n from "../../Localization/i18next";
import Note from "./Note";
import { ProductPrices } from "../CoachViews/Stripe/StripePayment";
import Competition from "./Competition";
import FormSection from "./FormSection";
import Question from "./Question";
import Form from "./Form";

import {
  ProtocolTitles,
  SyncStatus,
  MealNames,
} from "../../Resources/Constants";
import CoachingApplication, {
  ApplicationQuestion,
  ApplicationSection,
} from "./CoachingApplication";
import { startOfWeek, endOfWeek } from "../utils/dateUtils";
import CoachFirestoreListener from "./CoachFirestoreListener";
import NetworkManager from "../Classes/NetworkManager";
import { CoachSyncStatusManager } from "../Classes/CoachSyncStatusManager";
import MediaManager from "../Classes/MediaManager";
import NutritionPlan from "./NutritionPlan";
import Meal from "./Meal";
import FoodOption from "./FoodOption";
import Food from "./Food";
import { getAppVersion } from "../utils/version";

export class CoachStore {
  /* CLIENT MEMORY WINDOW */
  // Tracks which clients currently have heavy fetched data in-memory (LRU order: oldest -> newest)
  fetchedClientIdsInMemory = [];
  // Upper bound for clients whose blocks/forms/etc are kept in memory at once.
  // Tune as needed; kept as a property so it can be adjusted per-coach instance.
  maxFetchedClientsInMemory = 8;

  /* CHAT */
  showingChatList = false;
  selectedChatId = "";
  chatIsExpanded = false;

  /* TEAM COACHES */
  teamCoaches = [];
  teamCoach = null;
  headCoach = null;
  team = null;
  isTeamCoach = false;

  /* LOGO */
  logoUrl = null;
  logoAspectRatioWH = null;
  logoAspectRatioHW = null;
  isLogoUploading = false;

  /* COACH */
  id = null;
  sessionId = uuidv4();
  isFetching = true;
  isLoading = false;
  initialLoading = false;
  batchTimer = null;
  name = "";
  surname = "";
  email = "";
  maxClients = 100;
  tracToShow = {
    leg_soreness: false,
    pull_soreness: false,
    push_soreness: false,
    motivation: true,
    technical_comfort: true,
    perceived_recovery: true,
    sleep_quality_nutrition: true,
  };
  lastBackup = null;
  UISections = {};
  lastSeenVersion = "1.0.0";
  showToast = null;
  selectedClientId = "";
  coachLanguage = "en";
  coachSettings = {};
  defaultBlockView = "workouts-string";
  hideBlockDashboard = false;
  highlightedComponent = null;
  networkManager = NetworkManager.getInstance();
  workoutLibrary = [];
  blockLibrary = [];
  competitions = [];
  forms = [];
  clients = [];

  showingCoachView = "CoachDashboard";
  librarySelectedTab = "forms-string";
  sideMenuVisible = false;
  showingInfoView = false;
  showingInfoViewProtocols = false;
  showingUpgradePlan = true;
  showingTutorialPanel = false;
  showingSendFeedback = false;
  showingStripePayment = false;
  showingResetAlert = false;
  showingOfflineAlert = false;
  hasCompletedInitialFetch = false;
  showingAthleteAppOnboarding = false;
  showingWhatsNew = false;
  showingProtocolsGuide = false;
  showingProtocolsGuideBlockView = false;
  testBlockView = false;
  useLegacyPlanView = false;
  useLegacyPlanViewSelection = true;

  /* SETTINGS & STRIPE */
  exerciseUnit = false;
  rirOrRpe = false;
  showAthleteExerciseStats = true;
  showSetPriority = true;
  showOnlyProtocolSlots = false;
  customPredefinedExercises = [];
  customPredefinedAttributes = [];
  subscriptionStatus = "active";
  trialPeriodStartDate = null;
  allowedAccount = false;
  selectedRate = "upTo5Athletes-string";
  showingSubscriptionEnded = false;
  showingPaymentFailed = false;
  invoicePaymentLink = "";
  showingTerms = false;
  onboardingShowAthleteApp = false;
  onboardingShowBlock = false;
  onboardingShowInviteClient = false;
  sidebarConfig = null;

  /* BLOCKS */
  selectedEquipment = "";
  selectedTempo = "";
  selectedROM = "";
  selectedStanceGrip = "";
  selectedLoadAccomodation = "";
  selectedAccessories = "";
  selectedOther = "";

  /* FLOATING SEARCH */
  activeFloatingSearchId = null;
  floatingSearchCloseCallback = null;
  floatingSearchInitialText = "";

  // Floating search configuration - all props needed for ExerciseSearchFloating
  floatingSearchConfig = null; // { referenceElement, onSelectExercise, suggestionContext, crossAxisOffset, slotExercise, primarySlotExercise, block, protocolSlot, currentWeek, suggestionsMode, currentWorkout, exerciseIndex

  /* COACH ARRAYS */
  coachTransactionWorkouts = [];
  coachDeletionWorkouts = [];
  coachTransactionBlocks = [];
  coachDeletionBlocks = [];
  coachTransactionCompetitions = [];
  coachDeletionCompetitions = [];
  coachTransactionForms = [];
  coachDeletionForms = [];
  coachTransactionExercises = [];
  coachDeletionExercises = [];
  coachTransactionAttributes = [];
  coachDeletionAttributes = [];

  /* FORMS */
  weeklyFormId = "";
  blockFormId = "";
  formName = "";
  formType = "- - -";
  formDay = 0;
  formSections = [];
  formSelectedClients = [];
  formDesSelectedClients = [];
  showingEditForm = false;

  /* COMPETITIONS */
  competitionName = "";
  competitionCategory = "";
  competitionLevel = "";
  competitionDate = new Date();
  competitionSelectedClients = [];
  selectedWeightClass = "";
  showingCustomCategory = false;
  showingCustomLevel = false;

  /* APPLICATIONS */
  coachDirectory = "";
  coachDirectoryAux = "";
  applicationForm = null;
  applicationFormSections = [];
  applications = [];

  /* NUTRITION */

  allowedNutrition = false;

  foods = [];
  nutritionPlans = [];
  auxNutritionPlan = null;

  coachTransactionFoods = [];
  coachDeletionFoods = [];

  coachTransactionNutritionPlans = [];
  coachDeletionNutritionPlans = [];

  constructor() {
    makeAutoObservable(this);
    this.syncStatusManager = new CoachSyncStatusManager(this);
    this.firestoreListener = new CoachFirestoreListener(this);
    reaction(
      () => ({
        coachLanguage: this.coachLanguage,
        exerciseUnit: this.exerciseUnit,
        rirOrRpe: this.rirOrRpe,
      }),
      () => {
        //Keep this only for some weeks until athletes update with new settings
        const data = {
          coach_language: this.coachLanguage ? this.coachLanguage : "es",
          coach_exercise_unit: this.exerciseUnit,
          coach_rir_or_rpe: this.rirOrRpe,
          exercise_unit: this.exerciseUnit,
          rir_or_rpe: this.rirOrRpe,
          language: this.coachLanguage ? this.coachLanguage : "es",
        };
        this.activeClients.forEach((client) => {
          client.uploadData(data);
        });
      }
    );
    reaction(
      () => ({
        showingUpgradePlan: this.showingUpgradePlan,
      }),
      () => {
        const data = {
          showing_upgrade_plan: this.showingUpgradePlan,
        };
        this.uploadData(data);
      }
    );

    reaction(
      () => ({
        coachDirectory: this.coachDirectory,
      }),
      () => {
        const data = {
          coach_directory: this.coachDirectory,
        };
        if (this && this.id != null) {
          //Al fer logout no s'ha d'activar reaction
          this.uploadData(data);
        }
      }
    );

    reaction(
      () => ({
        exerciseCount: this.customPredefinedExercises.length,
        attributeCount: this.customPredefinedAttributes.length,
      }),
      () => {
        // Invalidate search index when exercises or modifiers are added/deleted
        const {
          getExerciseSearchService,
        } = require("../Exercise/Search/ExerciseSearchService");
        const searchService = getExerciseSearchService();
        searchService.invalidateIndex();
      }
    );
  }

  // #region /* CHATS */
  searchTermChats = "";

  get selectedChatClient() {
    if (this.selectedChatId) {
      return this.activeClients.find(
        (client) => client.id === this.selectedChatId
      );
    } else {
      return null;
    }
  }

  get totalNewMessages() {
    return this.activeClients.reduce(
      (sum, client) => sum + (client.unreadCount || 0),
      0
    );
  }

  get totalNewChats() {
    return this.activeClients.reduce(
      (sum, client) =>
        sum + (client.unreadCount > 0 || client.unreadMark ? 1 : 0),
      0
    );
  }

  getFilteredChatClients() {
    const searchTermLower = this.searchTermChats.toLowerCase();
    let filteredClients = this.activeClients;

    // Filter by search term
    if (this.searchTermChats) {
      filteredClients = filteredClients.filter((client) => {
        const combinedString = (
          client.nameForCoach +
          " " +
          client.surnameForCoach
        ).toLowerCase();
        return combinedString.includes(searchTermLower);
      });
    }

    // Sort by last-message timestamp (descending: newest first)
    filteredClients.sort(
      (a, b) => (b.messageLastTimestamp || 0) - (a.messageLastTimestamp || 0)
    );

    return filteredClients;
  }
  // #endregion

  // #region /* FETCHES */
  async fetchInit(
    id = this.id,
    displayName = this.name,
    email = this.email,
    t,
    justCompletedOnboarding = false,
    showToast = null,
    isInitialFetch = false
  ) {
    // Set showToast if provided
    if (showToast) {
      this.showToast = showToast;
    }

    if (id) {
      await this.syncStatusManager.safeOperation("fetchCoach", () =>
        this.fetchCoachFromCloud(id, displayName, email)
      );

      await this.syncStatusManager.safeOperation("fetchCoachPublic", () =>
        this.fetchCoachPublicFromCloud()
      );

      await this.syncStatusManager.safeOperation("listenerClients", () =>
        this.firestoreListener.setupClientsListener(this)
      );

      await this.syncStatusManager.safeOperation(
        "fetchFutureCompetitions",
        () => this.fetchCompetitionsFromCloud(true)
      );

      await this.syncStatusManager.safeOperation("fetchForms", () =>
        this.fetchFormsFromCloud()
      );

      await this.syncStatusManager.safeOperation("fetchBlockLibrary", () =>
        this.fetchBlockLibraryFromCloud()
      );

      await this.syncStatusManager.safeOperation("fetchWorkoutLibrary", () =>
        this.fetchWorkoutLibraryFromCloud()
      );

      if (this.allowedNutrition) {
        await this.syncStatusManager.safeOperation("fetchFoodLibrary", () =>
          this.fetchFoodLibraryFromCloud()
        );

        await this.syncStatusManager.safeOperation("fetchNutritionPlans", () =>
          this.fetchNutritionPlansFromCloud()
        );
      }

      await this.syncStatusManager.safeOperation("fetchSubscriptions", () =>
        this.fetchSubscriptionFromCloud()
      );

      await this.syncStatusManager.safeOperation("fetchLogo", () =>
        this.fetchLogoFromCloud()
      );

      await this.fetchUnreadApplicationsFromCloud();

      await this.syncStatusManager.safeOperation("listenerInvitations", () =>
        this.firestoreListener.setupInvitationsListener(this)
      );

      await this.syncStatusManager.safeOperation("listenerChats", () =>
        this.firestoreListener.setupChatsListener(this)
      );

      await this.syncStatusManager.resolveErrors();
    }

    this.setProperty("hasCompletedInitialFetch", true);
  }

  async fetchCoachFromCloud(id, displayName, email) {
    this.id = id;

    const nameParts = displayName ? displayName.split(" ") : "";
    const familyName = nameParts ? nameParts.pop() : "";
    const givenName = nameParts ? nameParts.join(" ") : "";

    /* FETCH COACH INFO */
    const coachDocRef = doc(db, "coaches", id);
    const coachSnapshot = await getDoc(coachDocRef);

    if (coachSnapshot.exists()) {
      runInAction(() => {
        const dataCoach = coachSnapshot.data();
        this.name = dataCoach.name ? dataCoach.name : givenName;
        this.surname = dataCoach.hasOwnProperty("surname")
          ? dataCoach.surname
          : "";
        this.email = dataCoach.email ? dataCoach.email : email;
        this.team = dataCoach.team ? dataCoach.team : null;
        this.maxClients = 100;
        this.exerciseUnit =
          dataCoach.exercise_unit !== null ? dataCoach.exercise_unit : false;
        this.rirOrRpe =
          dataCoach.rir_or_rpe !== null ? dataCoach.rir_or_rpe : false;
        this.coachLanguage = dataCoach.coach_language
          ? dataCoach.coach_language
          : "en";
        i18n.changeLanguage(this.coachLanguage);

        this.coachDirectory = dataCoach.coach_directory
          ? dataCoach.coach_directory
          : "";

        this.showingUpgradePlan =
          dataCoach.showing_upgrade_plan !== undefined
            ? dataCoach.showing_upgrade_plan
            : true;

        this.tracToShow = dataCoach.trac_to_show
          ? dataCoach.trac_to_show
          : {
              leg_soreness: false,
              pull_soreness: false,
              push_soreness: false,
              motivation: true,
              technical_comfort: true,
              perceived_recovery: true,
              sleep_quality_nutrition: true,
            };

        this.weeklyFormId = dataCoach.weekly_form_id
          ? dataCoach.weekly_form_id
          : "";
        this.blockFormId = dataCoach.block_form_id
          ? dataCoach.block_form_id
          : "";
        this.allowedAccount = dataCoach.allowed_account
          ? dataCoach.allowed_account
          : false;

        this.allowedNutrition = dataCoach.allowed_nutrition
          ? dataCoach.allowed_nutrition
          : false;

        console.log("allowedNutrition", this.allowedNutrition);
        this.sidebarConfig = dataCoach.sidebar_config
          ? dataCoach.sidebar_config
          : null;

        if (dataCoach.trial_period_start_date) {
          this.trialPeriodStartDate =
            dataCoach.trial_period_start_date.toDate();
        }

        // this.coachSettings = {
        //   name: this.name ? this.name : "",
        //   surname: this.surname ? this.surname : "",
        //   exercise_unit: this.exerciseUnit !== null ? this.exerciseUnit : false,
        //   rir_or_rpe: this.rirOrRpe !== null ? this.rirOrRpe : false,
        //   coach_language: this.coachLanguage ? this.coachLanguage : "es",
        // };

        this.UISections = dataCoach.ui_sections ? dataCoach.ui_sections : {};

        this.testBlockView = dataCoach.test_block_view === true ? true : false;
        this.useLegacyPlanView =
          dataCoach.use_legacy_plan_view === true ? true : false;
        this.useLegacyPlanViewSelection =
          dataCoach.use_legacy_plan_view_selection === false ? false : true;

        if (dataCoach.last_seen_version !== undefined) {
          this.lastSeenVersion = dataCoach.last_seen_version;
        }

        if (Array.isArray(dataCoach.custom_predefined_exercises)) {
          this.customPredefinedExercises = [];
          dataCoach.custom_predefined_exercises.forEach(
            (predefinedExercise) => {
              this.fetchPredefinedExercise(predefinedExercise);
            }
          );
        }

        if (Array.isArray(dataCoach.custom_predefined_attributes)) {
          this.customPredefinedAttributes = [];
          dataCoach.custom_predefined_attributes.forEach(
            (predefinedAttribute) => {
              this.fetchPredefinedAttribute(predefinedAttribute);
            }
          );
        }
      });
    }
  }

  async fetchCoachPublicFromCloud() {
    /* FETCH COACH PUBLIC INFO */
    const coachPublicDocRef = doc(db, "coaches_public", this.id);
    const coachPublicSnapshot = await getDoc(coachPublicDocRef);

    console.log("fetchCoachPublicFromCloud");
    if (coachPublicSnapshot.exists()) {
      console.log("fetchCoachPublicFromCloud exists");
      const dataCoach = coachPublicSnapshot.data();
      runInAction(() => {
        if (dataCoach.language !== undefined) {
          this.coachLanguage = dataCoach.language;
          i18n.changeLanguage(this.coachLanguage);
        }
        if (dataCoach.name !== undefined) {
          this.name = dataCoach.name;
        }
        if (dataCoach.surname !== undefined) {
          this.surname = dataCoach.surname;
        }
        if (dataCoach.email !== undefined) {
          this.email = dataCoach.email;
        }
        if (dataCoach.rir_or_rpe !== undefined) {
          this.rirOrRpe = dataCoach.rir_or_rpe;
        }
        if (dataCoach.exercise_unit !== undefined) {
          this.exerciseUnit = dataCoach.exercise_unit;
        }
        if (dataCoach.show_athlete_exercise_stats !== undefined) {
          this.showAthleteExerciseStats = dataCoach.show_athlete_exercise_stats;
        }
        if (dataCoach.show_set_priority !== undefined) {
          this.showSetPriority = dataCoach.show_set_priority;
        }
        if (dataCoach.default_block_view !== undefined) {
          this.defaultBlockView = dataCoach.default_block_view;
        }
        if (dataCoach.onboarding_show_athlete_app !== undefined) {
          this.onboardingShowAthleteApp = dataCoach.onboarding_show_athlete_app;
        }

        if (dataCoach.onboarding_show_block !== undefined) {
          this.onboardingShowBlock = dataCoach.onboarding_show_block;
        }

        if (dataCoach.onboarding_show_invite_client !== undefined) {
          this.onboardingShowInviteClient =
            dataCoach.onboarding_show_invite_client;
        }

        if (dataCoach.hide_block_dashboard !== undefined) {
          this.hideBlockDashboard = dataCoach.hide_block_dashboard;
        }

        if (dataCoach.showing_protocols_guide !== undefined) {
          this.showingProtocolsGuide = dataCoach.showing_protocols_guide;
        }
      });
    }
  }

  async fetchFoodLibraryFromCloud() {
    const foodLibraryDocRef = doc(
      db,
      "coaches",
      this.id,
      "nutrition",
      "nutrition"
    );

    const foodLibrarySnapshot = await getDoc(foodLibraryDocRef);
    if (foodLibrarySnapshot.exists()) {
      runInAction(() => {
        const foodLibraryData = foodLibrarySnapshot.data();
        const foodLibrary = foodLibraryData.foods ?? [];

        foodLibrary.forEach((foodData) => {
          this.fetchFood(foodData);
        });
      });
    }
  }

  async fetchNutritionPlansFromCloud() {
    const nutritionPlansColRef = collection(
      db,
      "coaches",
      this.id,
      "nutrition",
      "nutrition",
      "nutrition_plans"
    );

    const plansSnapshots = await getDocs(nutritionPlansColRef);

    if (!plansSnapshots.empty) {
      runInAction(() => {
        const blockLibrary = [];

        plansSnapshots.forEach((planDoc) => {
          const planData = planDoc.data();
          this.fetchNutritionPlan(planData);
        });
      });
    }
  }

  async fetchWorkoutLibraryFromCloud() {
    const workoutLibraryDocRef = doc(
      db,
      "coaches",
      this.id,
      "library",
      "library"
    );

    const workoutLibrarySnapshot = await getDoc(workoutLibraryDocRef);
    const localIds = new Set(this.workoutLibrary.map((w) => w.id));

    if (workoutLibrarySnapshot.exists()) {
      runInAction(() => {
        const workoutLibraryData = workoutLibrarySnapshot.data();
        const workoutLibrary = workoutLibraryData.workout_library ?? [];

        workoutLibrary.forEach((workoutData) => {
          if (!localIds.has(workoutData.id)) {
            this.fetchWorkout(workoutData);
          }
        });
      });
    }
  }

  async fetchBlockLibraryFromCloud() {
    const blockLibraryColRef = collection(
      db,
      "coaches",
      this.id,
      "library",
      "library",
      "block_library"
    );

    const blockSnapshots = await getDocs(blockLibraryColRef);
    const localIds = new Set((this.blockLibrary || []).map((b) => b.id));

    if (!blockSnapshots.empty) {
      runInAction(() => {
        // const blockLibrary = [];

        blockSnapshots.forEach((blockDoc) => {
          const blockData = blockDoc.data();
          if (!localIds.has(blockData.id)) {
            this.fetchBlock(blockData);
          }
        });
      });
    }
  }

  async fetchLogoFromCloud() {
    const storageRef = ref(storage, `coach_logos/${this.id}`);

    try {
      const url = await getDownloadURL(storageRef);
      this.logoUrl = url;

      const img = new window.Image();
      img.src = url;
      img.onload = () => {
        this.logoAspectRatioWH = img.width / img.height;
        this.logoAspectRatioHW = img.height / img.width;
      };
    } catch (error) {
      if (error.code != "storage/object-not-found") {
        console.error("Error fetching image:", error);
        throw error;
      }
    }
  }

  async fetchCompetitionsFromCloud(fetchFutureCompetitions = true) {
    console.log("fetchCompetitionsFromCloud");

    const competitionsDocRef = doc(
      db,
      "coaches",
      this.id,
      "competitions",
      "competitions"
    );

    const competitionsDoc = await getDoc(competitionsDocRef);

    if (competitionsDoc.exists()) {
      const competitionsDocData = competitionsDoc.data();
      const competitions = competitionsDocData.competitions ?? [];

      const today = new Date();
      const filteredCompetitions = competitions.filter((comp) =>
        fetchFutureCompetitions
          ? comp.date.toDate() >= today
          : comp.date.toDate() < today
      );

      console.log("Filtered competitions", filteredCompetitions.length);

      const tasks = [];
      for (const competitionData of filteredCompetitions) {
        const internalCompetition = this.competitions.find(
          (competition) =>
            competition.id.toString().toLowerCase() ===
            competitionData.id.toLowerCase()
        );
        if (!internalCompetition) {
          tasks.push(this.fetchCompetition(competitionData));
        }
      }
      // wait for all fetchCompetition calls to complete
      await Promise.all(tasks);
    }
  }

  async fetchFormsFromCloud() {
    const formCollectionRef = collection(
      db,
      "coaches",
      this.id,
      "library",
      "library",
      "form_library"
    );

    const formsSnapshot = await getDocs(formCollectionRef);
    runInAction(() => {
      if (!formsSnapshot.empty) {
        formsSnapshot.forEach((doc) => {
          const formData = doc.data();
          this.fetchForm(formData);
        });
      }

      if (this.forms.filter((f) => f.type === "Inicial").length === 0) {
        this.createInitialForm();
      }
    });
  }

  async fetchTeamCoachesFromCloud() {
    const coachesRef = collection(db, "coaches");
    const qCoachesOfTeam = query(
      coachesRef,
      where("team", "==", this.team),
      limit(50)
    );

    await getDocs(qCoachesOfTeam)
      .then(async (querySnapshot) => {
        if (querySnapshot.docs.length > 0) {
          querySnapshot.forEach((doc) => {
            // console.log("foreach");
            const coachOfTeam = doc.data();

            const coachDataToAdd = {
              name: coachOfTeam.name,
              surname: coachOfTeam.surname,
              id: coachOfTeam.id,
              email: coachOfTeam.email,
            };
            if (coachOfTeam.id !== this.id) {
              this.teamCoaches.push(coachDataToAdd);
            }
          });
        }
      })
      .catch((error) => {
        console.error("Error getting documents of coaches of team: ", error);
      });
  }

  //Falta query composta
  async fetchSubscriptionFromCloud() {
    if (this.allowedAccount) {
      return;
    }

    try {
      // fetch subscriptions with the same statuses
      const subscriptionsRef = collection(
        db,
        "customers",
        this.id,
        "subscriptions"
      );
      const subQuery = query(
        subscriptionsRef,
        where("status", "in", ["incomplete", "active", "past_due"])
      );
      const snapshot = await getDocs(subQuery);

      let subDoc;
      if (snapshot.size > 1) {
        // pick by status priority
        const priority = ["active", "past_due", "incomplete"];
        for (const status of priority) {
          const match = snapshot.docs.find(
            (doc) => doc.data().status === status
          );
          if (match) {
            subDoc = match;
            break;
          }
        }
        // fallback: most recent period end
        if (!subDoc) {
          subDoc = snapshot.docs.sort(
            (a, b) =>
              b.data().current_period_end.toDate().getTime() -
              a.data().current_period_end.toDate().getTime()
          )[0];
        }
      } else if (snapshot.size === 1) {
        subDoc = snapshot.docs[0];
      }
      if (subDoc) {
        const sub = subDoc.data();
        const priceRef = sub.price;
        const priceId = priceRef.id;
        this.subscriptionStatus = sub.status;
        console.log("subscriptionDoc found", priceId, sub.status);

        // hard-coded price → maxClients
        if (
          priceId === ProductPrices.HASTA5 ||
          priceId === "price_1Os2nbIu8R9ZwWzDj9BQqpqB"
        ) {
          console.log("maxClients 5");
          this.maxClients = 5;
        } else if (
          priceId === ProductPrices.HASTA15 ||
          priceId === "price_1Os2nwIu8R9ZwWzDDbnzam3i"
        ) {
          this.maxClients = 15;
        } else if (
          priceId === ProductPrices.HASTA30 ||
          priceId === "price_1Os2o8Iu8R9ZwWzDLz9q58Wh"
        ) {
          this.maxClients = 30;
        } else if (
          priceId === ProductPrices.HASTA50 ||
          priceId === "price_1Os2oKIu8R9ZwWzD3uh68Rsx"
        ) {
          this.maxClients = 50;
        } else if (
          priceId === ProductPrices.MAS50 ||
          priceId === "price_1Os2oZIu8R9ZwWzD8meoNcr7"
        ) {
          this.maxClients = 200;
        }
      } else {
        console.log("subscriptionDoc not found", this.trialPeriodStartDate);
        // empty snapshot fallback
        const trialPeriodEndDate = new Date(this.trialPeriodStartDate);
        trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);
        if (new Date() <= trialPeriodEndDate) {
          console.log("in trial period");
          this.subscriptionStatus = "trialing";
          this.maxClients = 100;
        } else {
          this.subscriptionStatus = "canceled";
          this.maxClients = 0;
          this.showingSubscriptionEnded = true;
        }
      }

      // handle payment failures
      if (
        this.subscriptionStatus === "past_due" ||
        this.subscriptionStatus === "incomplete"
      ) {
        const subId = subDoc.id;
        const invoicesRef = collection(
          db,
          "customers",
          this.id,
          "subscriptions",
          subId,
          "invoices"
        );
        const invoicesQuery = query(
          invoicesRef,
          where("status", "in", ["open"]),
          orderBy("created", "desc"),
          limit(1)
        );
        const invoicesSnap = await getDocs(invoicesQuery);
        if (!invoicesSnap.empty) {
          this.invoicePaymentLink =
            invoicesSnap.docs[0].data().hosted_invoice_url;
        }
        this.showingPaymentFailed = true;
      }
    } catch (err) {
      console.error("Error fetching subscription:", err);
      throw err;
    }
  }

  async fetchClient(userData) {
    if (!userData) {
      console.error("fetchUser received undefined userData");
      return null;
    }

    let blockWeeks = 0;
    let blockTotalWeeks = 0;
    let blockName = "-";
    let blockCurrentWeekPrepared = true;

    try {
      const blocksRef = collection(db, "users", userData.user_id, "blocks");
      const recentBlocksQuery = query(
        blocksRef,
        orderBy("last_update", "desc"),
        limit(2)
      );
      const blocksSnapshot = await getDocs(recentBlocksQuery);

      if (!blocksSnapshot.empty) {
        const resolvedBlocks = await Promise.all(
          blocksSnapshot.docs.map(async (blockDoc) => {
            const blockData = blockDoc.data();
            const workouts = blockData.workouts ?? [];

            const workoutDates = workouts
              .map((workout) => workout.date?.toDate())
              .filter(Boolean);

            return { blockData, workoutDates };
          })
        );

        const startOfThisWeek = startOfWeek();
        const endOfThisWeek = endOfWeek();

        // 1) Check if there are any workouts this week
        let targetBlock = resolvedBlocks.find(({ workoutDates }) =>
          workoutDates.some(
            (date) => date >= startOfThisWeek && date <= endOfThisWeek
          )
        );

        if (!targetBlock) {
          // 2) No workouts this week, search for workouts before this week
          const blocksWithWorkoutsBeforeThisWeek = resolvedBlocks.filter(
            ({ workoutDates }) =>
              workoutDates.some((date) => date < startOfThisWeek)
          );

          if (blocksWithWorkoutsBeforeThisWeek.length > 0) {
            // 3) Find the most recent workout before this week
            let mostRecentWorkout = null;
            let mostRecentBlock = null;

            for (const block of blocksWithWorkoutsBeforeThisWeek) {
              for (const date of block.workoutDates) {
                if (
                  date < startOfThisWeek &&
                  (!mostRecentWorkout || date > mostRecentWorkout)
                ) {
                  mostRecentWorkout = date;
                  mostRecentBlock = block;
                }
              }
            }

            if (mostRecentBlock) {
              const workouts = mostRecentBlock.blockData?.workouts ?? [];

              // 4) Find the workout with maximum week that has a date
              const workoutsWithDates = workouts.filter(
                (w) =>
                  w.date &&
                  typeof w.date.toDate === "function" &&
                  typeof w.week === "number"
              );

              const workoutWithMaxWeek = workoutsWithDates.reduce(
                (maxWorkout, currentWorkout) => {
                  if (!maxWorkout || currentWorkout.week > maxWorkout.week) {
                    return currentWorkout;
                  }
                  return maxWorkout;
                },
                null
              );

              if (workoutWithMaxWeek) {
                // 5) Calculate pending weeks and estimated end date
                const pendingWeeks =
                  mostRecentBlock.blockData.total_weeks -
                  workoutWithMaxWeek.week;
                const lastWorkoutDate = workoutWithMaxWeek.date.toDate();

                const estimatedEndDate = new Date(lastWorkoutDate);
                estimatedEndDate.setDate(
                  estimatedEndDate.getDate() + pendingWeeks * 7
                );

                // 6) Check if estimated end date falls within the current week or after
                if (estimatedEndDate >= startOfThisWeek) {
                  targetBlock = mostRecentBlock;
                }
              }
            }
          }

          // 7) Check blocks with calendarStartDate before returning null
          // Only consider blocks that have NO workouts with dates
          if (!targetBlock) {
            for (const block of resolvedBlocks) {
              const calendarStartDate =
                block.blockData?.calendar_start_date?.toDate();

              // Only consider this block if it has calendarStartDate AND no workouts with dates
              const hasWorkoutsWithDates = block.workoutDates.length > 0;

              if (calendarStartDate && !hasWorkoutsWithDates) {
                const calendarStartWeek = startOfWeek(calendarStartDate);

                // Check if the calendar start is not in the future
                if (calendarStartWeek <= startOfThisWeek) {
                  // Calculate estimated end date
                  const estimatedBlockEndDate = new Date(calendarStartDate);
                  estimatedBlockEndDate.setDate(
                    estimatedBlockEndDate.getDate() +
                      (block.blockData.total_weeks - 1) * 7
                  );

                  // Check if estimated end date falls within the current week or after
                  if (estimatedBlockEndDate >= startOfThisWeek) {
                    targetBlock = block;
                    break;
                  }
                }
              }
            }
          }
        }

        if (targetBlock) {
          const workouts = targetBlock.blockData?.workouts ?? [];
          blockTotalWeeks = targetBlock.blockData?.total_weeks ?? 0;
          blockName = targetBlock.blockData?.name ?? "-";

          // Calculate current week using same logic as Block.currentWeek()
          const thisWeekWorkouts = workouts.filter((w) => {
            if (!w.date || typeof w.date.toDate !== "function") return false;
            const date = w.date.toDate();
            return date >= startOfThisWeek && date <= endOfThisWeek;
          });

          if (thisWeekWorkouts.length > 0) {
            // Return the highest week number among workouts this week
            blockWeeks = Math.max(...thisWeekWorkouts.map((w) => w.week));
          } else {
            // No workouts this week, find workouts in the past
            const pastWorkouts = workouts.filter((w) => {
              if (!w.date || typeof w.date.toDate !== "function") return false;
              const date = w.date.toDate();
              return date < startOfThisWeek;
            });

            if (pastWorkouts.length === 0) {
              // No past workouts, check for future workouts
              const futureWorkouts = workouts.filter((w) => {
                if (!w.date || typeof w.date.toDate !== "function")
                  return false;
                const date = w.date.toDate();
                return date > endOfThisWeek;
              });

              if (futureWorkouts.length > 0) {
                // Return the minimum week from future workouts
                blockWeeks = Math.min(...futureWorkouts.map((w) => w.week));
              } else if (targetBlock.blockData?.calendar_start_date) {
                // No dated workouts at all, use calendarStartDate
                const calendarStart =
                  targetBlock.blockData.calendar_start_date.toDate();
                const calendarStartWeek = startOfWeek(calendarStart);

                const weeksDiff = Math.floor(
                  (startOfThisWeek - calendarStartWeek) /
                    (7 * 24 * 60 * 60 * 1000)
                );

                blockWeeks = Math.min(
                  Math.max(1, 1 + weeksDiff),
                  blockTotalWeeks
                );
              } else {
                // Fallback: return 1 or highest week among all workouts
                const allWeeks = workouts
                  .filter((w) => typeof w.week === "number")
                  .map((w) => w.week);
                blockWeeks = allWeeks.length > 0 ? Math.max(...allWeeks) : 1;
              }
            } else {
              // Find the most recent past workout
              const mostRecentPastWorkout = pastWorkouts.reduce((latest, w) => {
                const wDate = w.date.toDate();
                return !latest || wDate > latest.date
                  ? { date: wDate, week: w.week }
                  : latest;
              }, null);

              if (mostRecentPastWorkout) {
                // Find the highest week number among past workouts
                const highestPastWeek = Math.max(
                  ...pastWorkouts.map((w) => w.week)
                );

                // Calculate weeks elapsed since the most recent past workout
                const mostRecentWeekStart = startOfWeek(
                  mostRecentPastWorkout.date
                );
                const weeksDiff = Math.floor(
                  (startOfThisWeek - mostRecentWeekStart) /
                    (7 * 24 * 60 * 60 * 1000)
                );

                // Estimate current week
                const estimatedWeek = highestPastWeek + weeksDiff;
                blockWeeks = Math.min(estimatedWeek, blockTotalWeeks);
              } else {
                blockWeeks = 1;
              }
            }
          }

          // Check if current week is prepared (has workouts created for it)
          const maxCreatedWeek = Math.max(
            ...workouts.map((w) => (typeof w.week === "number" ? w.week : 0))
          );
          blockCurrentWeekPrepared = blockWeeks <= maxCreatedWeek;
        }
      }
    } catch (error) {
      console.error("Error fetching blocks and workouts:", error);
    }

    // Create the new client
    const newClient = new Client(
      userData.user_id,
      userData.email,
      userData.user_name ?? "New Client",
      userData.user_surname ?? "",
      userData.name_for_coach ?? userData.user_name ?? "New Client",
      userData.surname_for_coach ?? userData.user_surname ?? "",
      userData.bodyweight_unit,
      userData.exercise_unit,
      userData.last_backup,
      userData.rir_or_rpe,
      userData.language,
      userData.exercises_metrics_pinned,
      userData.is_checked,
      blockTotalWeeks,
      blockWeeks,
      this,
      userData.week_workouts ?? [
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ],
      userData.is_competitor,
      userData.phone ?? "",
      userData.notes,
      undefined,
      undefined,
      userData.fee_history ?? [],
      userData.gender,
      blockName,
      userData.weekly_form_id ?? "",
      userData.block_form_id ?? "",
      userData.profile_squat ?? 0,
      userData.profile_bench_press ?? 0,
      userData.profile_deadlift ?? 0
    );

    newClient.clientSettings = {
      name_for_coach: newClient.nameForCoach ?? "",
      surname_for_coach: newClient.surnameForCoach ?? "",
      notes: newClient.notes ?? "",
      phone: newClient.phone ?? "",
      is_competitor: newClient.isCompetitor ?? false,
      gender: newClient.gender ?? false,
      profile_squat: newClient.profileSquat ?? 0,
      profile_bench_press: newClient.profileBenchPress ?? 0,
      profile_deadlift: newClient.profileDeadlift ?? 0,
    };

    newClient.setProperty("blockCurrentWeekPrepared", blockCurrentWeekPrepared);

    if (
      newClient.exercisesMetricsSelected &&
      userData?.exercises_metrics_pinned?.length > 0
    ) {
      newClient.setProperty("exercisesMetricsSelected", [
        userData.exercises_metrics_pinned[0],
      ]);
    }

    this.clients.push(newClient);

    return newClient;
  }

  async fetchNewClient(clientId, t, language, awaitFetch = false) {
    this.registerClientInMemoryWindow(clientId);

    const foundClient = this.activeClients.find(
      (client) => client.id === clientId
    );

    if (foundClient) {
      if (awaitFetch) {
        await foundClient.fetchInit(language);
      } else {
        foundClient.fetchInit(language);
      }
    }
  }

  registerClientInMemoryWindow(clientId) {
    const max = Math.max(1, Number(this.maxFetchedClientsInMemory) || 1);

    // If this is a new client and we're at capacity, evict first to reduce peak memory.
    if (
      clientId &&
      !this.fetchedClientIdsInMemory.includes(clientId) &&
      this.fetchedClientIdsInMemory.length >= max
    ) {
      // console.log(
      //   `[ClientWindow] capacity ${this.fetchedClientIdsInMemory.length}/${max}; attempting eviction before fetching client=${clientId}`
      // );
      this.evictOldestClientFromMemory({ keepClientId: clientId });
    }

    // Update LRU order: move to the end.
    if (clientId) {
      this.fetchedClientIdsInMemory = this.fetchedClientIdsInMemory.filter(
        (id) => id !== clientId
      );
      this.fetchedClientIdsInMemory.push(clientId);
    }

    // console.log(
    //   `[ClientWindow] inMemory=${this.fetchedClientIdsInMemory.length}/${max} (after register client=${clientId})`
    // );

    // Best-effort enforcement (in case max decreased).
    // Keep evicting until we're within bounds, or eviction is blocked.
    while (this.fetchedClientIdsInMemory.length > max) {
      const didEvict = this.evictOldestClientFromMemory({
        keepClientId: clientId,
      });
      if (!didEvict) {
        // console.log(
        //   `[ClientWindow] no evictable clients (pending writes / chat open); allowing window to exceed limit max=${max}`
        // );
        break;
      }
    }
  }

  evictOldestClientFromMemory({ keepClientId = null } = {}) {
    const max = Math.max(1, Number(this.maxFetchedClientsInMemory) || 1);
    if (this.fetchedClientIdsInMemory.length <= max) return false;

    // Evict the oldest client that isn't the one we're about to load.
    const candidates = this.fetchedClientIdsInMemory.filter(
      (id) => id && id !== keepClientId
    );
    if (candidates.length === 0) return false;

    for (const evictId of candidates) {
      const client = this.clients.find((c) => c.id === evictId);
      if (!client) continue;

      // Never evict if this client's chat is currently open.
      const isChatOpen = client?.coach?.selectedChatId === client?.id;
      if (isChatOpen) {
        // console.log(
        //   `[ClientWindow] skip eviction (chat open) client=${evictId}`
        // );
        continue;
      }

      // Only evict clients with zero pending queued writes.
      if (client?.hasPendingQueuedWrites?.()) {
        // console.log(
        //   `[ClientWindow] skip eviction (pending writes) client=${evictId}`
        // );
        continue;
      }

      // console.log(`[ClientWindow] evicting client=${evictId}`);

      try {
        if (!client?.resetFetchedState) {
          console.warn(
            `[ClientWindow] cannot evict client=${evictId} (resetFetchedState missing)`
          );
          continue;
        }

        const didReset = client.resetFetchedState() !== false;

        if (didReset) {
          this.fetchedClientIdsInMemory = this.fetchedClientIdsInMemory.filter(
            (id) => id !== evictId
          );
          return true;
        }
      } catch (e) {
        console.warn("Failed to evict client from memory:", evictId, e);
      }
    }

    return false;
  }

  async fetchCompetition(competitionData) {
    // console.log("Starting fetchCompetition", competitionData);

    // 1) Add the new Competition locally
    const newCompetition = new Competition(
      competitionData.id,
      competitionData.name,
      competitionData.date ? competitionData.date.toDate() : null,
      this,
      null,
      undefined,
      competitionData.competition_class,
      competitionData.competition_level
    );
    this.competitions.push(newCompetition);
    console.log("Added new competition", newCompetition?.name);

    try {
      // console.log("Fetching competitions from Firestore…");
      const competitionsQuery = query(
        collectionGroup(db, "competitions"),
        where("id", "==", competitionData.id)
      );
      const snapshot = await getDocs(competitionsQuery);
      console.log("Fetched", snapshot.size, "competitions");

      // 2) Build a lookup of clients by userId
      const clientById = new Map(this.clients.map((c) => [c.id, c]));

      // 3) For each doc, extract its owner userId and queue a fetch if needed
      const fetchPromises = snapshot.docs.map((doc) => {
        // Extract userId from path: "/users/{userId}/competitions/{doc.id}"
        const segments = doc.ref.path.split("/");
        const userIdx = segments.indexOf("users");
        if (userIdx === -1) return null;

        const userId = segments[userIdx + 1];
        const client = clientById.get(userId);
        if (!client) {
          console.log(`No client found for userId ${userId}`);
          return null;
        }

        // Skip if this competition is already in that client
        if (client.competitions?.some((c) => c.id === doc.id)) {
          console.log(`Client ${userId} already has competition ${doc.id}`);
          return null;
        }

        // Otherwise, sync via safeOperation
        return client.syncStatusManager.safeOperation("fetchCompetition", () =>
          client.fetchCompetition(doc.data())
        );
      });

      // 4) Await all fetches (skipping nulls)
      await Promise.all(fetchPromises.filter((p) => p));
      console.log("Finished fetching competition data for users.");
    } catch (error) {
      console.error("Error fetching competition data for users:", error);
    }
  }

  fetchWorkout(workoutData) {
    try {
      const newWorkout = new Workout(
        workoutData.id,
        null,
        workoutData.name,
        workoutData.order,
        workoutData.week,
        false,
        null,
        [],
        this,
        workoutData.notes
      );
      workoutData.exercises.forEach((exerciseData) => {
        newWorkout.fetchExercise(exerciseData);
      });
      this.workoutLibrary.push(newWorkout);
      return newWorkout;
    } catch (error) {
      console.error("Error fetching workout from library:", error);
    }
  }

  fetchPredefinedExercise(predefinedExerciseData) {
    try {
      const newPredefinedExercise = new PredefinedExercise(
        predefinedExerciseData.id,
        predefinedExerciseData.name,
        predefinedExerciseData.original_name,
        predefinedExerciseData.order,
        predefinedExerciseData.type,
        predefinedExerciseData.notes,
        predefinedExerciseData.link,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        null
      );
      this.customPredefinedExercises.push(newPredefinedExercise);
      return newPredefinedExercise;
    } catch (error) {
      console.error("Error fetching predefined exercise:", error);
    }
  }

  fetchPredefinedAttribute(attributeData) {
    try {
      const newPredefinedAttribute = new Attribute(
        attributeData.id,
        attributeData.name,
        attributeData.title,
        attributeData.subtitle,
        attributeData.order,
        attributeData.is_predefined,
        false,
        null,
        null
      );
      this.customPredefinedAttributes.push(newPredefinedAttribute);

      return newPredefinedAttribute;
    } catch (error) {
      console.error("Error fetching predefined attribute:", error);
    }
  }

  fetchBlock(blockData) {
    try {
      const newBlock = new Block(
        String(blockData.id),
        blockData.name,
        blockData.notes,
        [],
        false,
        false,
        null,
        blockData.weeks,
        blockData.total_weeks ? blockData.total_weeks : 8,
        "",
        null,
        this
      );

      if (blockData.hidden_weeks !== undefined) {
        newBlock.setProperty("hiddenWeeks", blockData.hidden_weeks);
      }

      if (blockData.week_names !== undefined) {
        newBlock.setProperty("weekNames", blockData.week_names);
      }

      blockData.workouts.forEach((workoutData) => {
        newBlock.fetchWorkout(workoutData);
      });

      if (blockData.protocols) {
        newBlock.fetchBlockProtocols(blockData.protocols);
      } else {
        runInAction(() => {
          newBlock.updateOldProtocols(
            blockData.squat_slots,
            "squat_slots",
            blockData.squat_protocols,
            blockData.squat_secondary_protocols,
            newBlock.name
          );
          newBlock.updateOldProtocols(
            blockData.bench_press_slots,
            "bench_press_slots",
            blockData.bench_press_protocols,
            blockData.bench_press_secondary_protocols,
            newBlock.name
          );
          newBlock.updateOldProtocols(
            blockData.deadlift_slots,
            "deadlift_slots",
            blockData.deadlift_protocols,
            blockData.deadlift_secondary_protocols,
            newBlock.name
          );
        });
      }

      this.blockLibrary.push(newBlock);

      return newBlock;
    } catch (error) {
      console.error("Error creating block:", error);
      throw error;
    }
  }

  fetchForm(formData) {
    try {
      const newForm = new Form(
        formData.id,
        formData.name ? formData.name : "",
        formData.type ? formData.type : "- - -",
        formData.week_day ? formData.week_day : 0,
        formData.date ? formData.date.toDate() : null,
        [],
        null,
        this
      );

      if (formData.sections) {
        formData.sections.forEach((sectionData) => {
          newForm.fetchUserSection(sectionData);
        });
      }

      this.forms.push(newForm);
      return newForm;
    } catch (error) {
      console.error("Error creating form", error);
      throw error;
    }
  }

  fetchFood(foodData) {
    try {
      const newFood = new Food(
        foodData.id,
        foodData.name,
        foodData.order,
        100,
        foodData.calories,
        foodData.carbs,
        foodData.protein,
        foodData.fats,
        foodData.code ?? undefined,
        null,
        null,
        null,
        null,
        this
      );
      this.foods.push(newFood);
      return newFood;
    } catch (error) {
      console.error("Error fetching food from library:", error);
    }
  }

  fetchNutritionPlan(planData) {
    try {
      const newNutritionPlan = new NutritionPlan(
        planData.id,
        planData.name,
        planData.notes,
        planData.calories_goal,
        planData.carbs_goal,
        planData.protein_goal,
        planData.fats_goal,
        null,
        this
      );

      planData?.meals.forEach((meal) => {
        newNutritionPlan.fetchMeal(meal);
      });

      this.nutritionPlans.push(newNutritionPlan);
      return newNutritionPlan;
    } catch (error) {
      console.error("Error fetching nutrition plan from library:", error);
    }
  }

  // async fetchTeamCoach(id, email, name, surname) {
  //   const teamCoach = new CoachStore();
  //   if (id && teamCoach.clients.length === 0) {
  //     teamCoach.setProperty("id", id);

  //     /* FETCH COACH INFO */
  //     const coachDocRef = doc(db, "coaches", id);
  //     const docSnap = await getDoc(coachDocRef);

  //     if (docSnap.exists()) {
  //       const dataCoach = docSnap.data();
  //       teamCoach.setProperty("isTeamCoach", true);
  //       teamCoach.setProperty("headCoach", this);
  //       teamCoach.setProperty("name", name);
  //       teamCoach.setProperty("surname", surname);
  //       teamCoach.setProperty("email", email);
  //       teamCoach.setProperty(
  //         "maxClients",
  //         dataCoach.max_clients ? dataCoach.max_clients : 0
  //       );
  //       teamCoach.setProperty(
  //         "bodyweightUnit",
  //         dataCoach.bodyweight_unit !== null ? dataCoach.bodyweight_unit : false
  //       );
  //       teamCoach.setProperty(
  //         "exerciseUnit",
  //         dataCoach.exercise_unit !== null ? dataCoach.exercise_unit : false
  //       );
  //       teamCoach.setProperty(
  //         "rirOrRpe",
  //         dataCoach.rir_or_rpe !== null ? dataCoach.rir_or_rpe : false
  //       );
  //       teamCoach.setProperty(
  //         "coachLanguage",
  //         dataCoach.coach_language ? dataCoach.coach_language : "en"
  //       );

  //       teamCoach.setProperty(
  //         "coachDirectory",
  //         dataCoach.coach_directory ? dataCoach.coach_directory : ""
  //       );
  //       teamCoach.setProperty("showingUpgradePlan", false);
  //       teamCoach.setProperty(
  //         "tracToShow",
  //         dataCoach.trac_to_show
  //           ? dataCoach.trac_to_show
  //           : {
  //               leg_soreness: false,
  //               pull_soreness: false,
  //               push_soreness: false,
  //               motivation: true,
  //               technical_comfort: true,
  //               perceived_recovery: true,
  //               sleep_quality_nutrition: true,
  //             }
  //       );
  //       teamCoach.setProperty(
  //         "weeklyFormId",
  //         dataCoach.weekly_form_id ? dataCoach.weekly_form_id : ""
  //       );
  //       teamCoach.setProperty(
  //         "blockFormId",
  //         dataCoach.block_form_id ? dataCoach.block_form_id : ""
  //       );

  //       if (dataCoach.trial_period_start_date) {
  //         teamCoach.setProperty(
  //           "trialPeriodStartDate",
  //           dataCoach.trial_period_start_date.toDate()
  //         );
  //       }

  //       teamCoach.setProperty("coachSettings", {
  //         name: teamCoach.name ? teamCoach.name : "",
  //         surname: teamCoach.surname ? teamCoach.surname : "",
  //         bodyweight_unit:
  //           teamCoach.bodyweightUnit !== null
  //             ? teamCoach.bodyweightUnit
  //             : false,
  //         exercise_unit:
  //           teamCoach.exerciseUnit !== null ? teamCoach.exerciseUnit : false,
  //         rir_or_rpe: teamCoach.rirOrRpe !== null ? teamCoach.rirOrRpe : false,
  //         coach_language: teamCoach.coachLanguage
  //           ? teamCoach.coachLanguage
  //           : "es",
  //       });
  //       teamCoach.setProperty("id", id);
  //       teamCoach.setProperty("id", id);
  //       teamCoach.setProperty("id", id);
  //       teamCoach.setProperty("id", id);

  //       if (Array.isArray(dataCoach.custom_predefined_exercises)) {
  //         dataCoach.custom_predefined_exercises.forEach((predefined) => {
  //           let newPredefinedExercise = new PredefinedExercise(
  //             predefined.id,
  //             predefined.name,
  //             predefined.original_name,
  //             predefined.order,
  //             predefined.type,
  //             predefined.notes,
  //             predefined.link,
  //             undefined,
  //             undefined,
  //             undefined,
  //             undefined,
  //             undefined,
  //             undefined,
  //             undefined,
  //             undefined,
  //             null
  //           );
  //           teamCoach.customPredefinedExercises.push(newPredefinedExercise);
  //         });
  //       }

  //       if (Array.isArray(dataCoach.custom_predefined_attributes)) {
  //         dataCoach.custom_predefined_attributes.forEach((attribute) => {
  //           let newPredefinedAttribute = new Attribute(
  //             attribute.id,
  //             attribute.name,
  //             attribute.title,
  //             attribute.subtitle,
  //             attribute.order,
  //             attribute.is_predefined,
  //             false,
  //             null,
  //             null
  //           );
  //           teamCoach.customPredefinedAttributes.push(newPredefinedAttribute);
  //         });
  //       }

  //       const storageRef = ref(storage, `coach_logos/${teamCoach.id}`);

  //       try {
  //         const url = await getDownloadURL(storageRef);

  //         teamCoach.setProperty("logoUrl", url);

  //         // Load image and get its natural width and height
  //         const img = new window.Image();
  //         img.src = url;
  //         img.onload = () => {
  //           teamCoach.setProperty("logoAspectRatioWH", img.width / img.height);
  //           teamCoach.setProperty("logoAspectRatioHW", img.height / img.width);
  //         };
  //       } catch (error) {
  //         if (error.code != "storage/object-not-found") {
  //           console.error("Error fetching image:", error);
  //         }
  //       }
  //     }

  //     /* FETCH CLIENTS INFO */
  //     const usersRef = collection(db, "users");
  //     const invitationsRef = collection(db, "invitations");

  //     const q = query(usersRef, where("coach_id", "==", id), limit(150));
  //     const qInvitations = query(
  //       invitationsRef,
  //       where("coach_id", "==", id),
  //       limit(150)
  //     );

  //     if (teamCoach.clients.length === 0) {
  //       await getDocs(q)
  //         .then(async (querySnapshot) => {
  //           if (querySnapshot.docs.length > 0) {
  //             runInAction(() => {
  //               querySnapshot.forEach(async (docSnap) => {
  //                 const dataClient = docSnap.data();

  //                 const blocksRef = collection(
  //                   db,
  //                   "users",
  //                   docSnap.id,
  //                   "blocks"
  //                 );
  //                 const qActiveBlock = query(
  //                   blocksRef,
  //                   where("is_active", "==", true),
  //                   limit(1)
  //                 );
  //                 const blockSnapshot = await getDocs(qActiveBlock);
  //                 const activeBlock =
  //                   blockSnapshot.docs.length > 0
  //                     ? blockSnapshot.docs[0].data()
  //                     : null;

  //                 const newClient = new Client(
  //                   dataClient.user_id,
  //                   dataClient.email,
  //                   dataClient.user_name ? dataClient.user_name : "Sin nombre",
  //                   dataClient.user_surname ? dataClient.user_surname : "",
  //                   dataClient.name_for_coach
  //                     ? dataClient.name_for_coach
  //                     : dataClient.user_name
  //                     ? dataClient.user_name
  //                     : "Sin nombre",
  //                   dataClient.surname_for_coach
  //                     ? dataClient.surname_for_coach
  //                     : dataClient.user_surname
  //                     ? dataClient.user_surname
  //                     : "",
  //                   dataClient.bodyweight_unit,
  //                   dataClient.exercise_unit,
  //                   dataClient.last_backup,
  //                   dataClient.rir_or_rpe,
  //                   dataClient.language,
  //                   dataClient.exercises_metrics_pinned,
  //                   dataClient.is_checked,
  //                   activeBlock ? activeBlock.total_weeks : 0,
  //                   activeBlock ? activeBlock.weeks : 0,
  //                   teamCoach,
  //                   dataClient.week_workouts
  //                     ? dataClient.week_workouts
  //                     : [false, false, false, false, false, false, false],
  //                   dataClient.is_competitor,
  //                   dataClient.phone ? dataClient.phone : "",
  //                   dataClient.notes,
  //                   undefined,
  //                   undefined,
  //                   dataClient.fee_history ? dataClient.fee_history : [],
  //                   dataClient.gender,
  //                   activeBlock ? activeBlock.name : "",
  //                   dataClient ? dataClient.weekly_form_id : "",
  //                   dataClient ? dataClient.block_form_id : "",
  //                   dataClient.profile_squat !== undefined
  //                     ? dataClient.profile_squat
  //                     : 0,
  //                   dataClient.profile_bench_press !== undefined
  //                     ? dataClient.profile_bench_press
  //                     : 0,
  //                   dataClient.profile_deadlift !== undefined
  //                     ? dataClient.profile_deadlift
  //                     : 0
  //                 );

  //                 newClient.clientSettings = {
  //                   name_for_coach: newClient.nameForCoach
  //                     ? newClient.nameForCoach
  //                     : "",
  //                   surname_for_coach: newClient.surnameForCoach
  //                     ? newClient.surnameForCoach
  //                     : "",
  //                   notes: newClient.notes ? newClient.notes : "",
  //                   phone: newClient.phone ? newClient.phone : "",
  //                   is_competitor: newClient.isCompetitor
  //                     ? newClient.isCompetitor
  //                     : false,
  //                   gender: newClient.gender ? newClient.gender : false,
  //                   profile_squat: newClient.profileSquat
  //                     ? newClient.profileSquat
  //                     : 0,
  //                   profile_bench_press: newClient.profileBenchPress
  //                     ? newClient.profileBenchPress
  //                     : 0,
  //                   profile_deadlift: newClient.profileDeadlift
  //                     ? newClient.profileDeadlift
  //                     : 0,
  //                 };

  //                 if (
  //                   newClient.exercisesMetricsSelected &&
  //                   dataClient &&
  //                   dataClient.exercises_metrics_pinned &&
  //                   dataClient.exercises_metrics_pinned.length > 0
  //                 ) {
  //                   newClient.setProperty("exercisesMetricsSelected", [
  //                     dataClient.exercises_metrics_pinned[0],
  //                   ]);
  //                 }

  //                 const competitionsColRef = collection(
  //                   db,
  //                   "users",
  //                   docSnap.id,
  //                   "competitions"
  //                 );

  //                 try {
  //                   const querySnapshot = await getDocs(competitionsColRef);

  //                   querySnapshot.forEach((doc) => {
  //                     const data = doc.data();

  //                     const newCompetition = new Competition(
  //                       data.id,
  //                       data.name,
  //                       data.date.toDate(),
  //                       null,
  //                       newClient,
  //                       data.weight_class,
  //                       data.competition_class,
  //                       data.competition_level,
  //                       "",
  //                       data.weight_in,
  //                       data.hour,
  //                       data.goals,
  //                       data.notes,
  //                       data.top_end,
  //                       data.minimum_mark,
  //                       data.squat_attempts,
  //                       data.squat_warmups,
  //                       data.bench_press_attempts,
  //                       data.bench_press_warmups,
  //                       data.deadlift_attempts,
  //                       data.deadlift_warmups,
  //                       data.squat_results,
  //                       data.bench_press_results,
  //                       data.deadlift_results,
  //                       data.recap_notes
  //                     );

  //                     newClient.competitions.push(newCompetition);

  //                     console.log("Added competition:", newCompetition);
  //                   });
  //                 } catch (error) {
  //                   console.error(
  //                     "Error fetching competition documents:",
  //                     error
  //                   );
  //                 }
  //                 teamCoach.clients.push(newClient);
  //               });
  //             });
  //           }
  //         })
  //         .catch((error) => {
  //           console.error("Error getting documents: ", error);
  //         });
  //       await getDocs(qInvitations)
  //         .then(async (querySnapshot) => {
  //           if (querySnapshot.docs.length > 0) {
  //             querySnapshot.forEach((doc) => {
  //               // console.log("foreach");
  //               const invitationClient = doc.data();
  //               const newClient = new Client(
  //                 uuidv4(),
  //                 invitationClient.new_client_email,
  //                 invitationClient.new_client_email,
  //                 "",
  //                 invitationClient.new_client_email,
  //                 "",
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 undefined,
  //                 teamCoach,
  //                 undefined,
  //                 false,
  //                 undefined,
  //                 undefined,
  //                 "pending-string",
  //                 doc.id
  //               );
  //               teamCoach.clients.push(newClient);
  //             });
  //           }
  //         })
  //         .catch((error) => {
  //           console.error("Error getting documents: ", error);
  //         });
  //     }
  //   }

  //   // const subscriptionsRef = collection(
  //   //   db,
  //   //   "customers",
  //   //   teamCoach.id,
  //   //   "subscriptions"
  //   // );
  //   // const q = query(
  //   //   subscriptionsRef,
  //   //   where("status", "in", ["incomplete", "active", "past_due"])
  //   // );

  //   // const qSnapshot = await getDocs(q);

  //   // if (!qSnapshot.empty) {
  //   //   const doc = qSnapshot.docs[0];
  //   //   // teamCoach.subscriptionStatus = doc.data().status;
  //   //   teamCoach.setProperty("subscriptionStatus", doc.data().status);
  //   //   if (
  //   //     doc.data().price.id === ProductPrices.HASTA5 ||
  //   //     doc.data().price.id === "price_1Os2nbIu8R9ZwWzDj9BQqpqB"
  //   //   ) {
  //   //     teamCoach.setProperty("maxClients", 5);
  //   //   } else if (
  //   //     doc.data().price.id === ProductPrices.HASTA15 ||
  //   //     doc.data().price.id === "price_1Os2nwIu8R9ZwWzDDbnzam3i"
  //   //   ) {
  //   //     teamCoach.setProperty("maxClients", 15);
  //   //   } else if (
  //   //     doc.data().price.id === ProductPrices.HASTA30 ||
  //   //     doc.data().price.id === "price_1Os2o8Iu8R9ZwWzDLz9q58Wh"
  //   //   ) {
  //   //     teamCoach.setProperty("maxClients", 30);
  //   //   } else if (
  //   //     doc.data().price.id === ProductPrices.HASTA50 ||
  //   //     doc.data().price.id === "price_1Os2oKIu8R9ZwWzD3uh68Rsx"
  //   //   ) {
  //   //     teamCoach.setProperty("maxClients", 50);
  //   //   } else if (
  //   //     doc.data().price.id === ProductPrices.MAS50 ||
  //   //     doc.data().price.id === "price_1Os2oZIu8R9ZwWzD8meoNcr7"
  //   //   ) {
  //   //     teamCoach.setProperty("maxClients", 150);
  //   //   }
  //   // } else {
  //   //   const trialPeriodEndDate = new Date(teamCoach.trialPeriodStartDate);
  //   //   trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);
  //   //   if (new Date() <= trialPeriodEndDate) {
  //   //     teamCoach.setProperty("subscriptionStatus", "trialing");
  //   //     teamCoach.setProperty("maxClients", 20);
  //   //   } else {
  //   //     teamCoach.setProperty("subscriptionStatus", "canceled");
  //   //     teamCoach.setProperty("maxClients", 0);
  //   //   }
  //   // }
  //   // if (teamCoach.clients.length > teamCoach.maxClients) {
  //   //   if (
  //   //     teamCoach.id !== "DwvUwBGXJGZcqQRzD6U7iVSljzG2" &&
  //   //     teamCoach.id !== "jDJXmClW3MOEKDexIBPILdbDt6m2"
  //   //   ) {
  //   //     teamCoach.setProperty("showingSubscriptionEnded", true);
  //   //   }
  //   // }

  //   // if (
  //   //   teamCoach.subscriptionStatus === "past_due" ||
  //   //   teamCoach.subscriptionStatus === "incomplete"
  //   // ) {
  //   //   const invoicesRef = collection(
  //   //     db,
  //   //     "customers",
  //   //     teamCoach.id,
  //   //     "subscriptions",
  //   //     qSnapshot.docs[0].id,
  //   //     "invoices"
  //   //   );

  //   //   const invoicesQ = query(invoicesRef, where("status", "in", ["open"]));

  //   //   const invoicesQSnapshot = await getDocs(invoicesQ);

  //   //   if (!invoicesQSnapshot.empty) {
  //   //     const invoiceDoc = invoicesQSnapshot.docs[0];

  //   //     teamCoach.setProperty(
  //   //       "invoicePaymentLink",
  //   //       invoiceDoc.data().hosted_invoice_url
  //   //     );
  //   //   }

  //   //   teamCoach.setProperty("showingPaymentFailed", true);
  //   // }
  //   teamCoach.setProperty("initialLoading", false);
  //   teamCoach.setProperty("isFetching", false);

  //   this.teamCoach = teamCoach;
  // }
  // #endregion

  // #region /* UPLOADS */
  startOrResetBatchTimer() {
    if (!this.isOffline) {
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
      }
      this.syncStatusManager.setOperationStatus(
        "performTransaction",
        SyncStatus.WAITING
      );
      this.batchTimer = setTimeout(() => {
        this.syncStatusManager.safeOperation("performTransaction", () =>
          this.performTransaction()
        );
      }, 2000);
    } else {
      this.syncStatusManager.hasChangesOffline = true;
    }
  }

  async performTransaction() {
    const results = await Promise.allSettled([
      this.uploadPredefineds(),
      this.uploadWorkoutLibrary(),
      this.uploadCompetitions(),
      this.uploadFormLibrary(),
      this.uploadBlockLibrary(),
      this.uploadFoodLibrary(),
      this.uploadNutritionPlans(),
    ]);

    // collect any rejections
    const errors = results
      .filter((r) => r.status === "rejected")
      .map((r) => r.reason);

    if (errors.length) {
      errors.forEach((err, i) =>
        console.error(`performTransaction error #${i}:`, err)
      );
      throw new Error(
        `performTransaction: ${errors.length} operation(s) failed`
      );
    }

    console.log("✅ All upload…() operations succeeded");
  }
  async uploadPredefineds() {
    // Early return if no changes to process
    if (
      this.coachTransactionAttributes.length === 0 &&
      this.coachDeletionAttributes.length === 0 &&
      this.coachTransactionExercises.length === 0 &&
      this.coachDeletionExercises.length === 0
    ) {
      return;
    }

    const coachDocRef = doc(db, "coaches", this.id);
    const time = this.batchTimer;

    //Delete the attributes in custom_predefined_attributes that have the ids in coach.coachDeletionAttributes
    //Delete the exercises in custom_predefined_exercises that have the ids in coach.coachDeletionExercises
    //Add the attributes in this.customPredefinedAttributes that have the ids in coach.coachTransactionAttributes to the firestore field custom_predefined_attributes
    //Add the exercises in this.customPredefinedExercises that have the ids in coach.coachTransactionExercises to the firestore field custom_predefined_exercises
    //Do all of this in a transaction, if error, throw error, if no error, clear the coach arrays
    try {
      await runTransaction(db, async (transaction) => {
        // Get the current document
        const coachDoc = await transaction.get(coachDocRef);
        if (!coachDoc.exists()) {
          throw "Document does not exist!";
        }

        const coachData = coachDoc.data();

        // Handle attributes
        let currentAttributes = coachData.custom_predefined_attributes || [];

        // Delete attributes that have the ids in coachDeletionAttributes
        this.coachDeletionAttributes.forEach((deletedAttributeId) => {
          const index = currentAttributes.findIndex(
            (attr) => attr.id.toLowerCase() === deletedAttributeId.toLowerCase()
          );
          if (index !== -1) {
            currentAttributes.splice(index, 1);
          }
        });

        // Add/update attributes that have the ids in coachTransactionAttributes
        this.coachTransactionAttributes.forEach((attributeId) => {
          const internalAttribute = this.customPredefinedAttributes.find(
            (attr) => attr.id.toLowerCase() === attributeId.toLowerCase()
          );
          const foundAttribute = currentAttributes.find(
            (attr) => attr.id.toLowerCase() === attributeId.toLowerCase()
          );

          if (internalAttribute) {
            if (foundAttribute) {
              Object.assign(foundAttribute, internalAttribute.uploadToCloud());
            } else {
              currentAttributes.push(internalAttribute.uploadToCloud());
            }
          }
        });

        // Handle exercises
        let currentExercises = coachData.custom_predefined_exercises || [];

        // Delete exercises that have the ids in coachDeletionExercises
        this.coachDeletionExercises.forEach((deletedExerciseId) => {
          const index = currentExercises.findIndex(
            (ex) => ex.id.toLowerCase() === deletedExerciseId.toLowerCase()
          );
          if (index !== -1) {
            currentExercises.splice(index, 1);
          }
        });

        // Add/update exercises that have the ids in coachTransactionExercises
        this.coachTransactionExercises.forEach((exerciseId) => {
          const internalExercise = this.customPredefinedExercises.find(
            (ex) => ex.id.toLowerCase() === exerciseId.toLowerCase()
          );
          const foundExercise = currentExercises.find(
            (ex) => ex.id.toLowerCase() === exerciseId.toLowerCase()
          );

          if (internalExercise) {
            if (foundExercise) {
              Object.assign(foundExercise, internalExercise.uploadToCloud());
            } else {
              currentExercises.push(internalExercise.uploadToCloud());
            }
          }
        });

        // Update the document within the transaction
        transaction.update(coachDocRef, {
          custom_predefined_attributes: currentAttributes,
          custom_predefined_exercises: currentExercises,
        });
      });

      console.log("Predefineds successfully updated");

      // Clear the coach arrays if the operation was successful and timer hasn't changed
      if (time === this.batchTimer) {
        this.coachDeletionAttributes = [];
        this.coachTransactionAttributes = [];
        this.coachDeletionExercises = [];
        this.coachTransactionExercises = [];
      }
    } catch (error) {
      console.error("Transaction failed: ", error);
      throw error;
    }
  }

  async uploadWorkoutLibrary() {
    if (
      this.coachTransactionWorkouts.length === 0 &&
      this.coachDeletionWorkouts.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    // Reference to the library document in Firestore
    const libraryDocRef = doc(db, "coaches", this.id, "library", "library");

    // Wrap the transaction in a promise so it can be awaited
    const transactionPromise = runTransaction(db, async (transaction) => {
      const libraryDoc = await transaction.get(libraryDocRef);

      const libraryDocData = libraryDoc.data();
      if (libraryDocData) {
        const firestoreWorkoutLibrary = libraryDocData.workout_library ?? [];

        // console.log("firestoreWorkoutLibrary1", firestoreWorkoutLibrary);

        this.coachDeletionWorkouts.forEach((deletedWorkoutId) => {
          const index = firestoreWorkoutLibrary.findIndex(
            (w) => w.id.toLowerCase() === deletedWorkoutId.toLowerCase()
          );
          if (index !== -1) {
            firestoreWorkoutLibrary.splice(index, 1);
          }
        });

        this.coachTransactionWorkouts.forEach((workoutId) => {
          let internalWorkout = this.workoutLibrary.find(
            (w) => w.id.toLowerCase() === workoutId.toLowerCase()
          );
          let foundWorkout = firestoreWorkoutLibrary.find(
            (w) => w.id.toLowerCase() === workoutId.toLowerCase()
          );
          // console.log("foundWorkout", foundWorkout);
          if (foundWorkout) {
            Object.assign(foundWorkout, internalWorkout.uploadToCloud());
          } else {
            firestoreWorkoutLibrary.push(internalWorkout.uploadToCloud());
          }
        });
        // console.log("firestoreWorkoutLibrary2", firestoreWorkoutLibrary);
        transaction.set(
          libraryDocRef,
          { workout_library: firestoreWorkoutLibrary }
          // { merge: true }
        );
      } else {
        //Map over the workoutLibrary and use uploadToCloud() for each workout
        const workoutsForCloud = this.workoutLibrary.map((workout) =>
          workout.uploadToCloud()
        );
        transaction.set(
          libraryDocRef,
          { workout_library: workoutsForCloud }
          // { merge: true }
        );
      }
    });
    promises.push(transactionPromise);

    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in transaction ${index}:`, result.reason);
      }
    });

    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadWorkoutLibrary: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionWorkouts = [];
        this.coachTransactionWorkouts = [];
      }
    }
  }

  async uploadBlockLibrary() {
    if (
      this.coachTransactionBlocks.length === 0 &&
      this.coachDeletionBlocks.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    // Reference to the block library collection in Firestore
    const blockLibraryColRef = collection(
      db,
      "coaches",
      this.id,
      "library",
      "library",
      "block_library"
    );

    // Handle deletions
    this.coachDeletionBlocks.forEach((deletedBlockId) => {
      const deletePromise = deleteDoc(
        doc(blockLibraryColRef, deletedBlockId.toLowerCase())
      )
        .then(() => console.log(`Deleted block with ID: ${deletedBlockId}`))
        .catch((error) =>
          console.error(`Error deleting block: ${deletedBlockId}`, error)
        );

      promises.push(deletePromise);
    });

    // Handle additions/updates
    this.coachTransactionBlocks.forEach((blockId) => {
      const block = this.blockLibrary.find(
        (b) => b.id.toLowerCase() === blockId.toLowerCase()
      );

      if (block) {
        const blockData = block.uploadToCloudLibrary();
        const blockDocRef = doc(blockLibraryColRef, blockId.toLowerCase());
        const setPromise = setDoc(blockDocRef, blockData, { merge: true })
          .then(() => console.log(`Uploaded/updated block with ID: ${blockId}`))
          .catch((error) =>
            console.error(`Error uploading/updating block: ${blockId}`, error)
          );

        promises.push(setPromise);
      }
    });

    // Wait for all promises to settle
    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in operation ${index}:`, result.reason);
      }
    });

    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadBlockLibrary: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionBlocks = [];
        this.coachTransactionBlocks = [];
      }
    }
  }

  async uploadCompetitions() {
    if (
      this.coachTransactionCompetitions.length === 0 &&
      this.coachDeletionCompetitions.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    // Reference to the library document in Firestore
    const competitionsDocRef = doc(
      db,
      "coaches",
      this.id,
      "competitions",
      "competitions"
    );

    const transactionPromise = runTransaction(db, async (transaction) => {
      const libraryDoc = await transaction.get(competitionsDocRef);

      const libraryDocData = libraryDoc.data();
      if (libraryDocData) {
        const firestoreCompetitions = libraryDocData.competitions ?? [];

        this.coachDeletionCompetitions.forEach((deletedCompetitionId) => {
          const index = firestoreCompetitions.findIndex(
            (c) => c.id.toLowerCase() === deletedCompetitionId.toLowerCase()
          );
          if (index !== -1) {
            firestoreCompetitions.splice(index, 1);
          }
        });

        this.coachTransactionCompetitions.forEach((competitionId) => {
          let internalCompetition = this.competitions.find(
            (c) => c.id.toLowerCase() === competitionId.toLowerCase()
          );
          let foundCompetition = firestoreCompetitions.find(
            (c) => c.id.toLowerCase() === competitionId.toLowerCase()
          );
          if (foundCompetition) {
            Object.assign(
              foundCompetition,
              internalCompetition.uploadToCloud()
            );
          } else {
            firestoreCompetitions.push(internalCompetition.uploadToCloud());
          }
        });
        // console.log("firestoreWorkoutLibrary2", firestoreWorkoutLibrary);
        transaction.set(
          competitionsDocRef,
          { competitions: firestoreCompetitions }
          // { merge: true }
        );
      } else {
        //Map over the workoutLibrary and use uploadToCloud() for each workout
        const competitionsForCloud = this.competitions.map((competition) =>
          competition.uploadToCloud()
        );
        transaction.set(
          competitionsDocRef,
          { competitions: competitionsForCloud }
          // { merge: true }
        );
      }
    });

    promises.push(transactionPromise);

    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in transaction ${index}:`, result.reason);
      }
    });
    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadCompetitions: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionCompetitions = [];
        this.coachTransactionCompetitions = [];
      }
    }
  }

  async uploadFormLibrary() {
    if (
      this.coachTransactionForms.length === 0 &&
      this.coachDeletionForms.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    // Wrap the transaction in a promise so it can be awaited
    const transactionPromise = runTransaction(db, async (transaction) => {
      // Reference to the coach document
      const coachDocRef = doc(db, "coaches", this.id);

      // Delete Forms from Firestore
      this.coachDeletionForms.forEach((deletedFormId) => {
        const formDocRef = doc(
          db,
          "coaches",
          this.id,
          "library",
          "library",
          "form_library",
          deletedFormId
        );
        transaction.delete(formDocRef);
      });

      // Upload or Update Forms in Firestore
      this.coachTransactionForms.forEach((formId) => {
        const formDocRef = doc(
          db,
          "coaches",
          this.id,
          "library",
          "library",
          "form_library",
          formId
        );
        let internalForm = this.forms.find(
          (form) => form.id.toLowerCase() === formId.toLowerCase()
        );

        if (internalForm) {
          transaction.set(formDocRef, internalForm.uploadToCloud());
        }
      });

      // Prepare the updated data for the coach document
      const coachDataUpdates = {};

      coachDataUpdates.weekly_form_id = this.weeklyFormId; // Update with the actual weekly form ID
      coachDataUpdates.block_form_id = this.blockFormId; // Update with the actual block form ID

      coachDataUpdates.number_of_forms = this.numberOfForms;

      // Update the coach document with the new form IDs
      transaction.update(coachDocRef, coachDataUpdates);
    });

    promises.push(transactionPromise);

    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in transaction ${index}:`, result.reason);
      }
    });
    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadFormLibrary: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionForms = [];
        this.coachTransactionForms = [];
      }
    }
  }

  async uploadFoodLibrary() {
    if (
      this.coachTransactionFoods.length === 0 &&
      this.coachDeletionFoods.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    // Reference to the library document in Firestore
    const nutritionDocRef = doc(
      db,
      "coaches",
      this.id,
      "nutrition",
      "nutrition"
    );

    // Wrap the transaction in a promise so it can be awaited
    const transactionPromise = runTransaction(db, async (transaction) => {
      const nutritionDoc = await transaction.get(nutritionDocRef);

      const nutritionDocData = nutritionDoc.data();
      if (nutritionDocData) {
        const firestoreFoodLibrary = nutritionDocData.foods ?? [];

        this.coachDeletionFoods.forEach((deletedFoodId) => {
          const index = firestoreFoodLibrary.findIndex(
            (f) => f.id.toLowerCase() === deletedFoodId.toLowerCase()
          );
          if (index !== -1) {
            firestoreFoodLibrary.splice(index, 1);
          }
        });

        this.coachTransactionFoods.forEach((foodId) => {
          let internalFood = this.foods.find(
            (f) => f.id.toLowerCase() === foodId.toLowerCase()
          );
          let foundFood = firestoreFoodLibrary.find(
            (f) => f.id.toLowerCase() === foodId.toLowerCase()
          );
          if (foundFood) {
            Object.assign(foundFood, internalFood.uploadToCloud(true));
          } else {
            firestoreFoodLibrary.push(internalFood.uploadToCloud(true));
          }
        });

        transaction.set(nutritionDocRef, {
          foods: firestoreFoodLibrary,
        });
      } else {
        //Map over the workoutLibrary and use uploadToCloud() for each workout
        const foodsForCloud = this.foods.map((food) =>
          food.uploadToCloud(true)
        );
        transaction.set(nutritionDocRef, { foods: foodsForCloud });
      }
    });
    promises.push(transactionPromise);

    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in transaction ${index}:`, result.reason);
      }
    });

    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadFoodLibrary: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionFoods = [];
        this.coachTransactionFoods = [];
      }
    }
  }

  async uploadNutritionPlans() {
    if (
      this.coachTransactionNutritionPlans.length === 0 &&
      this.coachDeletionNutritionPlans.length === 0
    ) {
      return;
    }

    let promises = [];
    const time = this.batchTimer;

    const nutritionPlansColRef = collection(
      db,
      "coaches",
      this.id,
      "nutrition",
      "nutrition",
      "nutrition_plans"
    );

    this.coachDeletionNutritionPlans.forEach((deletedPlanId) => {
      const deletePromise = deleteDoc(
        doc(nutritionPlansColRef, deletedPlanId.toLowerCase())
      )
        .then(() =>
          console.log(`Deleted nutrition plan with ID: ${deletedPlanId}`)
        )
        .catch((error) =>
          console.error(
            `Error deleting nutrition plan: ${deletedPlanId}`,
            error
          )
        );

      promises.push(deletePromise);
    });

    this.coachTransactionNutritionPlans.forEach((planId) => {
      const nutritionPlan = this.nutritionPlans.find(
        (np) => np.id.toLowerCase() === planId.toLowerCase()
      );

      if (nutritionPlan) {
        const planData = nutritionPlan.uploadToCloud(true);
        const planDocRef = doc(nutritionPlansColRef, planId.toLowerCase());
        const setPromise = setDoc(planDocRef, planData, { merge: true })
          .then(() =>
            console.log(`Uploaded/updated nutrition plan with ID: ${planId}`)
          )
          .catch((error) =>
            console.error(
              `Error uploading/updating nutrition plan: ${planId}`,
              error
            )
          );

        promises.push(setPromise);
      }
    });

    // Wait for all promises to settle
    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in operation ${index}:`, result.reason);
      }
    });

    if (hasError) {
      console.log("Error performing transaction for coach");
      throw new Error("uploadNutritionPlans: one or more operations failed");
    } else {
      if (time === this.batchTimer) {
        this.coachDeletionNutritionPlans = [];
        this.coachTransactionNutritionPlans = [];
      }
    }
  }

  // async createCoachDocument() {
  //   let attributesArray = this.customPredefinedAttributes.map(
  //     (attribute, index) => ({
  //       id: attribute.id.toString().toLowerCase(),
  //       is_predefined: attribute.isPredefined,
  //       name: attribute.name,
  //       title: attribute.title,
  //       order: index,
  //       subtitle: attribute.subtitle,
  //     })
  //   );

  //   let exercisesArray = this.customPredefinedExercises.map(
  //     (exercise, index) => ({
  //       id: exercise.id.toString().toLowerCase(),
  //       name: exercise.name,
  //       notes: exercise.notes,
  //       order: index,
  //       original_name: exercise.originalName,
  //       type: exercise.type,
  //       link: exercise.link,
  //       is_predefined: true,
  //     })
  //   );

  //   const data = {
  //     id: this.id,
  //     surname: this.surname,
  //     name: this.name,
  //     email: this.email,
  //     bodyweight_unit: false,
  //     exercise_unit: false,
  //     rir_or_rpe: false,
  //     coach_language: this.coachLanguage,
  //     max_clients: this.maxClients,
  //     trial_period_start_date: this.trialPeriodStartDate,
  //     custom_predefined_attributes: attributesArray,
  //     custom_predefined_exercises: exercisesArray,
  //     // email_authentication: coach.emailAuthentication,
  //   };

  //   const userCoachRef = doc(db, "coaches", this.id);

  //   setDoc(userCoachRef, data)
  //     .then(async () => {
  //       await this.createDemoClient();
  //       await this.createCoachDemoClient();
  //     })
  //     .catch((error) => {
  //       console.error("Error creating demo docs:", error);
  //     });
  // }

  saveCoachProfile(coachData) {
    const coachDocRef = doc(db, "coaches", this.id);
    // this.updateBatch(userDocRef, data);
    updateDoc(coachDocRef, coachData)
      .then(() => {
        console.log("Document successfully updated or created");
      })
      .catch((error) => {
        console.error("Error updating or creating document:", error);
      });
  }

  uploadData(data) {
    const userDocRef = doc(db, "coaches", this.id);
    if (!this.isFetching) {
      updateDoc(userDocRef, data);
    }
  }

  updateCoachProfile(coachData) {
    const coachDocRef = doc(db, "coaches", this.id);

    setDoc(coachDocRef, coachData, { merge: true })
      .then(() => {
        console.log("Document successfully updated or created");
      })
      .catch((error) => {
        console.error("Error updating or creating document:", error);
      });
  }
  async updateCoachPublicProfile(coachData) {
    const coachDocRef = doc(db, "coaches_public", this.id);

    try {
      await setDoc(coachDocRef, coachData, { merge: true });
      console.log("Document successfully updated or created");
    } catch (error) {
      console.error("Error updating or creating document:", error);
    }
  }

  async uploadFeedbackFirestore(text, source = null) {
    const feedbackDocRef = doc(db, "feedback", uuidv4());

    const data = {
      text: text,
      coach_id: this.id,
      timestamp: new Date(),
      number_of_clients: this.coach?.activeClients?.length || 0,
      source: source,
    };

    try {
      await setDoc(feedbackDocRef, data);
    } catch (error) {
      console.error("Error sending feedback", error);
    }
  }

  uploadToCloud(attributesArray, exercisesArray) {
    const appVersion = getAppVersion() || "1.0.0";

    const data = {
      id: this.id,
      name: this.name,
      surname: this.surname,
      email: this.email,
      trial_period_start_date: this.trialPeriodStartDate,
      custom_predefined_attributes: attributesArray,
      custom_predefined_exercises: exercisesArray,
      sidebar_config: this.sidebarConfig,
      last_seen_version: appVersion,

      //settings to be removed only if we ensure no coaches without coaches_public doc
      exercise_unit: this.exerciseUnit,
      rir_or_rpe: this.rirOrRpe,
      coach_language: this.coachLanguage,
    };

    return data;
  }

  async uploadCoachPredefinedExercise(predefined) {
    const coachDocRef = doc(db, "coaches", this.id);
    try {
      await runTransaction(db, async (transaction) => {
        // Get the current document
        const coachDoc = await transaction.get(coachDocRef);
        if (!coachDoc.exists()) {
          throw "Document does not exist!";
        }

        // Get the current exercise array and add the new exercise
        const currentExercises =
          coachDoc.data().custom_predefined_exercises || [];
        currentExercises.push(predefined.uploadToCloud());

        // Update the document within the transaction
        transaction.update(coachDocRef, {
          custom_predefined_exercises: currentExercises,
        });
      });

      console.log("Document successfully updated");
    } catch (error) {
      console.error("Transaction failed: ", error);
    }
  }

  async deleteCoachPredefinedExercise(exerciseIdToRemove) {
    const coachDocRef = doc(db, "coaches", this.id);

    try {
      await runTransaction(db, async (transaction) => {
        // Get the current document
        const coachDoc = await transaction.get(coachDocRef);
        if (!coachDoc.exists()) {
          throw "Document does not exist!";
        }

        // Get the current exercise array and filter it
        const currentExercises =
          coachDoc.data().custom_predefined_exercises || [];
        const updatedExercises = currentExercises.filter(
          (exercise) => exercise.id !== exerciseIdToRemove
        );

        // Update the document within the transaction
        transaction.update(coachDocRef, {
          custom_predefined_exercises: updatedExercises,
        });
      });

      console.log("Exercise successfully deleted");

      // Update your local array to reflect the change
      this.customPredefinedExercises = this.customPredefinedExercises.filter(
        (exercise) => exercise.id !== exerciseIdToRemove
      );
    } catch (error) {
      console.error("Transaction failed: ", error);
    }
  }

  async uploadCoachPredefinedAttribute(predefined) {
    const coachDocRef = doc(db, "coaches", this.id);

    try {
      await runTransaction(db, async (transaction) => {
        // Get the current document
        const coachDoc = await transaction.get(coachDocRef);
        if (!coachDoc.exists()) {
          throw "Document does not exist!";
        }

        // Get the current attribute array and add the new attribute
        const currentAttributes =
          coachDoc.data().custom_predefined_attributes || [];
        currentAttributes.push(predefined.uploadToCloud());

        // Update the document within the transaction
        transaction.update(coachDocRef, {
          custom_predefined_attributes: currentAttributes,
        });
      });

      console.log("Attribute successfully updated");
    } catch (error) {
      console.error("Transaction failed: ", error);
    }
  }

  async deleteCoachPredefinedAttribute(attributeIdToRemove) {
    const coachDocRef = doc(db, "coaches", this.id);

    try {
      await runTransaction(db, async (transaction) => {
        // Get the current document
        const coachDoc = await transaction.get(coachDocRef);
        if (!coachDoc.exists()) {
          throw "Document does not exist!";
        }

        // Get the current attribute array and filter it
        const currentAttributes =
          coachDoc.data().custom_predefined_attributes || [];
        const updatedAttributes = currentAttributes.filter(
          (attribute) => attribute.id !== attributeIdToRemove
        );

        // Update the document within the transaction
        transaction.update(coachDocRef, {
          custom_predefined_attributes: updatedAttributes,
        });
      });

      console.log("Attribute successfully deleted");

      // Update your local array to reflect the change
      this.customPredefinedAttributes = this.customPredefinedAttributes.filter(
        (attribute) => attribute.id !== attributeIdToRemove
      );

      // // Optionally, update any other local arrays that include this attribute
      // this.allAttributes = this.allAttributes.filter(
      //   (att) => att.id !== attributeIdToRemove
      // );
      // console.log("Attribute successfully deleted from all attributes locally");
    } catch (error) {
      console.error("Transaction failed: ", error);
    }
  }
  // #endregion

  // #region /* COACH */
  get isOffline() {
    return this.networkManager.isOffline;
  }

  get changesMadeOffline() {
    return (
      this.syncStatusManager.hasChangesOffline ||
      this.clients.some((client) => client.syncStatusManager.hasChangesOffline)
    );
  }

  setSettings(property, newValue) {
    this.coachSettings[property] = newValue;
  }

  toggleTracToShow(metric) {
    this.tracToShow[metric] = !this.tracToShow[metric];
  }

  resetCoach() {
    this.id = null;
    this.name = "";
    this.surname = "";
    this.email = "";
    this.maxClients = 0;
    this.clients = [];
    this.isLoading = false;
    this.selectedClientId = "";
    this.exerciseUnit = false;
    this.rirOrRpe = false;
    this.customPredefinedExercises = [];
    this.customPredefinedAttributes = [];
    this.coachLanguage = "es";
    this.showingSubscriptionEnded = false;
    this.subscriptionStatus = "active";
    this.showingPaymentFailed = false;
    this.showingCoachView = "CoachDashboard";
    this.workoutLibrary = [];
    this.blockLibrary = [];
    this.competitions = [];
    this.forms = [];
    this.logoUrl = null;
    this.logoAspectRatioWH = null;
    this.logoAspectRatioHW = null;
    this.isLogoUploading = false;
    this.applicationForm = null;
    this.applicationFormSections = [];
    this.applications = [];
    this.coachDirectory = "";
    this.coachDirectoryAux = "";
    this.UISections = {};
    this.showingAthleteAppOnboarding = false;
    this.showSetPriority = true;
    this.showAthleteExerciseStats = false;
    this.showOnlyProtocolSlots = false;
    this.onboardingShowAthleteApp = false;
    this.onboardingShowBlock = false;
    this.onboardingShowInviteClient = false;
    this.sidebarConfig = null;
    this.defaultBlockView = "workouts-string";
    this.firestoreListener.removeClientsListener();
    this.firestoreListener.removeInvitationsListener();
    this.firestoreListener.removeChatsListener();
    this.syncStatusManager = new CoachSyncStatusManager(this);
    this.firestoreListener = new CoachFirestoreListener(this);
    this.hideBlockDashboard = false;
    this.highlightedComponent = null;
    /* CHAT */
    this.showingChatList = false;
    this.chatIsExpanded = false;
    this.selectedChatId = "";
    const mediaManager = MediaManager.getInstance();
    if (mediaManager) mediaManager.setProperty("showingMediaPlayer", false);
  }

  async deleteClientDataLocally(client) {
    client.tracs = [];
    client.bodyweights = [];
    client.blocks = [];
    client.notesArray = [];
    client.forms = [];
    client.competitions = [];
    client.setProperty("isFetched", false);
  }

  get getName() {
    return this.name;
  }

  get activeClients() {
    return this.clients.filter((client) => client.status === "active-string");
  }

  /* SEARCH FOR PREDEFINEDS */
  searchTermPred = "";

  get getSearchTermPred() {
    return this.searchTermPred;
  }
  setSearchTermPred(w) {
    this.searchTermPred = w;
  }
  getFilteredPredefinedArray(type) {
    const searchTermLower = this.searchTermPred
      ? this.searchTermPred.toLowerCase()
      : "";

    const predefinedArray = this.customPredefinedExercises;
    const { t } = useTranslation();
    if (this.searchTermPred && predefinedArray.length > 0) {
      return predefinedArray.filter((pred) => {
        return (
          t(pred.name).toLowerCase().includes(searchTermLower) &&
          pred.type === type
        );
      });
    } else {
      return predefinedArray.filter((pred) => pred.type === type);
    }
  }

  hasMatchingExercises() {
    const searchTermLower = this.searchTermPred
      ? this.searchTermPred.toLowerCase()
      : "";

    const predefinedArray = this.customPredefinedExercises;
    const { t } = useTranslation();

    if (this.searchTermPred && predefinedArray.length > 0) {
      return predefinedArray.some((pred) => {
        return t(pred.name).toLowerCase().includes(searchTermLower);
      });
    } else {
      return predefinedArray.length > 0;
    }
  }

  /* SEARCH FOR ATTRIBUTES */
  searchTermAttr = "";

  get getSearchTermAttr() {
    return this.searchTermAttr;
  }
  setSearchTermAttr(w) {
    this.searchTermAttr = w;
  }
  getFilteredAttributesArray(type) {
    const searchTermLower = this.searchTermAttr
      ? this.searchTermAttr.toLowerCase()
      : "";

    const predefinedArray = this.customPredefinedAttributes;
    const { t } = useTranslation();
    if (this.searchTermAttr && predefinedArray.length > 0) {
      return predefinedArray.filter((pred) => {
        return (
          t(pred.name).toLowerCase().includes(searchTermLower) &&
          pred.title === type
        );
      });
    } else {
      return predefinedArray.filter((pred) => pred.title === type);
    }
  }

  hasMatchingAttributes() {
    const searchTermLower = this.searchTermAttr
      ? this.searchTermAttr.toLowerCase()
      : "";

    const predefinedArray = this.customPredefinedAttributes;
    const { t } = useTranslation();

    if (this.searchTermAttr && predefinedArray.length > 0) {
      return predefinedArray.some((pred) => {
        return t(pred.name).toLowerCase().includes(searchTermLower);
      });
    } else {
      return predefinedArray.length > 0;
    }
  }

  /* SEARCH COACH DASHBOARD */
  searchTerm = "";
  sortTerm = "client";
  billingYear = 2024;

  get getSearchTerm() {
    return this.searchTerm;
  }
  setSearchTerm(w) {
    this.searchTerm = w;
  }

  /* SEARCH BAR SIDE MENU */
  searchTermSideMenu = "";

  get getSearchTermSideMenu() {
    return this.searchTermSideMenu;
  }
  setSearchTermSideMenu(text) {
    this.searchTermSideMenu = text;
  }
  filterConditionSideMenu = "";
  filterConditionSideMenuDay = null;
  get getFilterConditionSideMenu() {
    return this.filterConditionSideMenu;
  }
  setFilterConditionSideMenu(condition) {
    this.filterConditionSideMenu = condition;
    // console.log("filterConditionSideMenu ", this.filterConditionSideMenu);
  }
  getFilteredClientsSideMenu() {
    const searchTermLower = this.searchTermSideMenu.toLowerCase();

    let filteredClients = this.teamCoach
      ? this.teamCoach.activeClients
      : this.activeClients;

    // Filter by search term
    if (this.searchTermSideMenu) {
      filteredClients = filteredClients.filter((client) => {
        const combinedString = (
          client.nameForCoach +
          " " +
          client.surnameForCoach
        ).toLowerCase();
        return combinedString.includes(searchTermLower);
      });
    }

    switch (this.filterConditionSideMenu) {
      case "Competidores":
        filteredClients = filteredClients.filter(
          (client) => client.isCompetitor === true
        );
        // console.log("Competidores", filteredClients);
        break;
      case "NoCompetidores":
        filteredClients = filteredClients.filter(
          (client) => client.isCompetitor === false
        );
        // console.log("NoCompetidores", filteredClients);
        break;
      case "Semana":
        filteredClients = filteredClients.filter(
          (client) =>
            client.getBlockCurrentWeek !== client.getBlockWeeks &&
            !client.isChecked
        );
        // console.log("Semana", filteredClients);
        break;
      case "Bloque":
        filteredClients = filteredClients.filter(
          (client) =>
            client.getBlockCurrentWeek === client.getBlockWeeks &&
            !client.isChecked
        );
        // console.log("Bloque", filteredClients);
        break;
      case "Complete":
        filteredClients = filteredClients.filter(
          (client) => client.isChecked === true
        );
        // console.log("Complete", filteredClients);
        break;
      case "Pending":
        filteredClients = filteredClients.filter(
          (client) => client.isChecked === false
        );
        // console.log("Pending", filteredClients);
        break;
      case "EmpiezaSemana":
        if (this.filterConditionSideMenuDay !== null) {
          filteredClients = filteredClients.filter((client) => {
            // Find the first day the client trains in the week
            const firstTrainingDayIndex = client.weekWorkouts.findIndex(
              (day) => day === true
            );
            // Check if the first training day matches the filterConditionSideMenuDay
            return firstTrainingDayIndex === this.filterConditionSideMenuDay;
          });
        }
        break;
      case "TerminaSemana":
        if (this.filterConditionSideMenuDay !== null) {
          filteredClients = filteredClients.filter((client) => {
            // Find the last day the client trains in the week
            const lastTrainingDayIndex = client.weekWorkouts.lastIndexOf(true);
            // Check if the last training day matches the filterConditionSideMenuDay
            return lastTrainingDayIndex === this.filterConditionSideMenuDay;
          });
        }
        break;
      default:
        // No additional filtering
        break;
    }

    filteredClients.sort((a, b) =>
      a.nameForCoach.localeCompare(b.nameForCoach)
    );

    return filteredClients;
  }

  getFilteredClients() {
    const searchTermLower = this.searchTerm.toLowerCase();
    const dayIndex =
      this.sortTerm === "mon"
        ? 0
        : this.sortTerm === "tue"
        ? 1
        : this.sortTerm === "wed"
        ? 2
        : this.sortTerm === "thu"
        ? 3
        : this.sortTerm === "fri"
        ? 4
        : this.sortTerm === "sat"
        ? 5
        : this.sortTerm === "sun"
        ? 6
        : -1; // Assuming this is already the correct index (0 for Monday, 6 for Sunday)

    const filteredClients =
      this.searchTerm && this.clients.length > 0
        ? this.clients.filter((client) => {
            const combinedString = (
              client.nameForCoach +
              " " +
              client.surnameForCoach
            ).toLowerCase();
            return combinedString.includes(searchTermLower);
          })
        : this.clients;

    if (this.sortTerm !== "") {
      if (dayIndex !== -1) {
        const sortedClients = filteredClients.slice().sort((a, b) => {
          // Priority order: active-string > pending-string > error-string
          const statusOrder = {
            "active-string": 0,
            "pending-string": 1,
            "error-string": 2,
          };

          const statusA = statusOrder[a.status] ?? 3;
          const statusB = statusOrder[b.status] ?? 3;

          if (statusA !== statusB) {
            return statusA - statusB;
          }

          // Criteria 1: Starting the week on this day
          const aStartsOnDay =
            a.weekWorkouts.slice(0, dayIndex).every((val) => !val) &&
            a.weekWorkouts[dayIndex];
          const bStartsOnDay =
            b.weekWorkouts.slice(0, dayIndex).every((val) => !val) &&
            b.weekWorkouts[dayIndex];

          if (aStartsOnDay && !bStartsOnDay) return -1;
          if (!aStartsOnDay && bStartsOnDay) return 1;

          // Criteria 2: Ending the week on this day
          const aEndsOnDay =
            a.weekWorkouts.slice(dayIndex + 1).every((val) => !val) &&
            a.weekWorkouts[dayIndex];
          const bEndsOnDay =
            b.weekWorkouts.slice(dayIndex + 1).every((val) => !val) &&
            b.weekWorkouts[dayIndex];

          if (aEndsOnDay && !bEndsOnDay) return -1;
          if (!aEndsOnDay && bEndsOnDay) return 1;

          // Criteria 3: Training on this day
          const aTrainsOnDay = a.weekWorkouts[dayIndex];
          const bTrainsOnDay = b.weekWorkouts[dayIndex];

          if (aTrainsOnDay && !bTrainsOnDay) return -1;
          if (!aTrainsOnDay && bTrainsOnDay) return 1;

          // Default case, keep original order
          return 0;
        });

        return sortedClients;
      } else {
        if (this.sortTerm === "client") {
          return filteredClients.slice().sort((a, b) => {
            // Priority order: active-string > pending-string > error-string
            const statusOrder = {
              "active-string": 0,
              "pending-string": 1,
              "error-string": 2,
            };

            const statusA = statusOrder[a.status] ?? 3;
            const statusB = statusOrder[b.status] ?? 3;

            if (statusA !== statusB) {
              return statusA - statusB;
            }

            // Convert names to lowercase to ensure case-insensitive comparison
            const nameA = a.nameForCoach.toLowerCase();
            const nameB = b.nameForCoach.toLowerCase();

            // Compare the two names
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;

            // names must be equal
            return 0;
          });
        } else if (this.sortTerm === "block") {
          return filteredClients.slice().sort((a, b) => {
            // Priority order: active-string > pending-string > error-string
            const statusOrder = {
              "active-string": 0,
              "pending-string": 1,
              "error-string": 2,
            };

            const statusA = statusOrder[a.status] ?? 3;
            const statusB = statusOrder[b.status] ?? 3;

            if (statusA !== statusB) {
              return statusA - statusB;
            }

            const blockA = a.getBlockCurrentName;
            const blockB = b.getBlockCurrentName;

            // Check if clients have active blocks
            const hasActiveBlockA = blockA && blockA !== "-";
            const hasActiveBlockB = blockB && blockB !== "-";

            // Clients with active blocks come first
            if (hasActiveBlockA && !hasActiveBlockB) return -1;
            if (!hasActiveBlockA && hasActiveBlockB) return 1;

            // If both have active blocks, sort alphabetically
            if (hasActiveBlockA && hasActiveBlockB) {
              return blockA.localeCompare(blockB);
            }

            return 0;
          });
        } else if (this.sortTerm === "week") {
          return filteredClients.slice().sort((a, b) => {
            // Priority order: active-string > pending-string > error-string
            const statusOrder = {
              "active-string": 0,
              "pending-string": 1,
              "error-string": 2,
            };

            const statusA = statusOrder[a.status] ?? 3;
            const statusB = statusOrder[b.status] ?? 3;

            if (statusA !== statusB) {
              return statusA - statusB;
            }

            // Check if clients have active blocks (non-zero week values)
            const hasActiveBlockA =
              a.getBlockCurrentWeek !== 0 && a.getBlockWeeks !== 0;
            const hasActiveBlockB =
              b.getBlockCurrentWeek !== 0 && b.getBlockWeeks !== 0;

            // Clients with active blocks come first
            if (hasActiveBlockA && !hasActiveBlockB) return -1;
            if (!hasActiveBlockA && hasActiveBlockB) return 1;

            // If both have active blocks
            if (hasActiveBlockA && hasActiveBlockB) {
              const weeksCompletedA = a.getBlockCurrentWeek >= a.getBlockWeeks;
              const weeksCompletedB = b.getBlockCurrentWeek >= b.getBlockWeeks;

              // If both have completed their blocks, sort by total weeks (more weeks first)
              if (weeksCompletedA && weeksCompletedB) {
                return b.getBlockWeeks - a.getBlockWeeks;
              }

              // Completed blocks come before active blocks
              if (weeksCompletedA && !weeksCompletedB) return -1;
              if (!weeksCompletedA && weeksCompletedB) return 1;

              // Both have active blocks, sort by weeks remaining (fewer weeks left comes first)
              const weeksRemainingA = a.getBlockWeeks - a.getBlockCurrentWeek;
              const weeksRemainingB = b.getBlockWeeks - b.getBlockCurrentWeek;

              // If weeks remaining are equal, sort by total weeks (more weeks first)
              if (weeksRemainingA === weeksRemainingB) {
                return b.getBlockWeeks - a.getBlockWeeks;
              }

              return weeksRemainingA - weeksRemainingB;
            }

            return 0;
          });
        } else if (this.sortTerm === "status") {
          return filteredClients.slice().sort((a, b) => {
            // Priority order: active-string > pending-string > error-string
            const statusOrder = {
              "active-string": 0,
              "pending-string": 1,
              "error-string": 2,
            };

            const statusA = statusOrder[a.status] ?? 3;
            const statusB = statusOrder[b.status] ?? 3;

            if (statusA !== statusB) {
              return statusA - statusB;
            }

            // If same status, sort alphabetically by name
            const nameA = a.nameForCoach.toLowerCase();
            const nameB = b.nameForCoach.toLowerCase();

            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;

            return 0;
          });
        } else {
          return filteredClients;
        }
      }
    } else {
      return filteredClients;
    }
  }

  translate(newLanguage, t) {
    this.isLoading = true;
    this.coachLanguage = newLanguage;
    i18n.changeLanguage(newLanguage);
    //En cas que tingui coach application form, canviar idioma
    this.isLoading = false;
  }

  get isActive() {
    if (this.subscriptionStatus === "canceled") {
      return false;
    } else {
      return true;
    }
  }

  async newClientInvitation(emails, showToast) {
    const results = {
      sent: [],
      alreadyInvited: [],
      alreadyHasCoach: [],
      errors: [],
    };

    const invitationsColRef = collection(db, "invitations");
    const usersRef = collection(db, "users");

    // Fetch initial form once
    const initialForm = this.forms.filter(
      (f) => f.type === "Inicial" && f.numberOfQuestionsAsked > 0
    )[0];
    const initialFormData = initialForm
      ? initialForm.uploadInitialToCloud()
      : null;

    await Promise.all(
      emails.map(async (email) => {
        try {
          // Check if invitation exists
          const qInv = query(
            invitationsColRef,
            where("new_client_email", "==", email)
          );
          const invSnap = await getDocs(qInv);
          if (!invSnap.empty) {
            results.alreadyInvited.push(email);
            return;
          }

          // Check if user exists
          const qUser = query(usersRef, where("email", "==", email));
          const userSnap = await getDocs(qUser);

          if (!userSnap.empty) {
            const userData = userSnap.docs[0].data();
            if (userData.coach_id && userData.coach_id !== "") {
              results.alreadyHasCoach.push(email);
              return;
            }
          }

          // Create invitation
          const newInvitationDocRef = doc(invitationsColRef);
          const newInvitationData = {
            coach_id: this.id,
            coach_email: this.email,
            coach_name: this.name,
            coach_surname: this.surname,
            coach_language: this.coachLanguage,
            coach_rir_or_rpe: this.rirOrRpe,
            coach_exercise_unit: this.exerciseUnit,
            new_client_email: email,
            initial_form: initialFormData,
            timestamp: serverTimestamp(),
            coach_weekly_form_id: this.weeklyFormId,
            coach_block_form_id: this.blockFormId,
          };

          await setDoc(newInvitationDocRef, newInvitationData);
          results.sent.push(email);
        } catch (error) {
          console.error(`Error inviting ${email}:`, error);
          results.errors.push({ email, error });
        }
      })
    );

    // Handle onboarding logic if needed (only if at least one sent)
    if (results.sent.length > 0 && this.onboardingShowInviteClient) {
      this.setProperty("onboardingShowInviteClient", false);
      this.updateCoachPublicProfile({
        onboarding_show_invite_client: false,
      });

      setTimeout(() => {
        showToast("step3Completed-string", {
          type: "info",
          description: "step3CompletedDescription-string",
        });
      }, 3000);
    }

    return results;
  }

  resetAttributes() {
    this.selectedEquipment = "";
    this.selectedTempo = "";
    this.selectedROM = "";
    this.selectedStanceGrip = "";
    this.selectedLoadAccomodation = "";
    this.selectedAccessories = "";
    this.selectedOther = "";
    this.customPredefinedAttributes.forEach((attribute) => {
      attribute.setIsSelected(false);
    });
  }
  selectAttribute(exercise, attribute, t, useEffect) {
    attribute.setIsSelected(true);
    if (this.coachLanguage === "es") {
      if (attribute.title === Attribute.AttributeTitles.equipment) {
        this.selectedEquipment = " con " + t(attribute.name);
      } else if (attribute.title === Attribute.AttributeTitles.rom) {
        this.selectedROM = " " + t(attribute.name);
      } else if (attribute.title === Attribute.AttributeTitles.tempo) {
        this.selectedTempo = " " + t(attribute.name);
      } else if (attribute.title === Attribute.AttributeTitles.stanceGrip) {
        this.selectedStanceGrip = " " + t(attribute.name);
      } else if (
        attribute.title === Attribute.AttributeTitles.loadAccomodation
      ) {
        this.selectedLoadAccomodation =
          this.selectedLoadAccomodation + " " + t(attribute.name);
      } else if (attribute.title === Attribute.AttributeTitles.accessories) {
        this.selectedAccessories =
          this.selectedAccessories + " " + t(attribute.name);
      } else {
        this.selectedOther = this.selectedOther + " " + t(attribute.name);
      }

      if (!useEffect) {
        if (
          attribute.title === Attribute.AttributeTitles.equipment ||
          attribute.title === Attribute.AttributeTitles.tempo ||
          attribute.title === Attribute.AttributeTitles.rom ||
          attribute.title === Attribute.AttributeTitles.stanceGrip
        ) {
          const toDeselect = this.customPredefinedAttributes.filter(
            (att) => att.title === attribute.title && att.isSelected
          );
          toDeselect.forEach((att) => {
            if (att.name !== attribute.name) att.setIsSelected(false);
          });
          exercise.deleteAttribute(attribute, false);
        }

        exercise.attributes.push(attribute.duplicate(exercise));

        const newName =
          t(exercise.originalName) +
          this.selectedTempo +
          this.selectedROM +
          this.selectedStanceGrip +
          this.selectedLoadAccomodation +
          this.selectedAccessories +
          this.selectedOther +
          this.selectedEquipment;

        exercise.setProperty("name", newName);
      }
    } else {
      //Angles
      if (attribute.title === Attribute.AttributeTitles.equipment) {
        this.selectedEquipment = t(attribute.name) + " ";
      } else if (attribute.title === Attribute.AttributeTitles.rom) {
        this.selectedROM = t(attribute.name) + " ";
      } else if (attribute.title === Attribute.AttributeTitles.tempo) {
        this.selectedTempo = t(attribute.name) + " ";
      } else if (attribute.title === Attribute.AttributeTitles.stanceGrip) {
        this.selectedStanceGrip = t(attribute.name) + " ";
      } else if (
        attribute.title === Attribute.AttributeTitles.loadAccomodation
      ) {
        this.selectedLoadAccomodation =
          this.selectedLoadAccomodation + t(attribute.name) + " ";
      } else if (attribute.title === Attribute.AttributeTitles.accessories) {
        this.selectedAccessories =
          this.selectedAccessories + t(attribute.name) + " ";
      } else {
        this.selectedOther = this.selectedOther + t(attribute.name) + " ";
      }

      if (!useEffect) {
        if (
          attribute.title === Attribute.AttributeTitles.equipment ||
          attribute.title === Attribute.AttributeTitles.tempo ||
          attribute.title === Attribute.AttributeTitles.rom ||
          attribute.title === Attribute.AttributeTitles.stanceGrip
        ) {
          const toDeselect = this.customPredefinedAttributes.filter(
            (att) => att.title === attribute.title && att.isSelected
          );
          toDeselect.forEach((att) => {
            if (att.name !== attribute.name) att.setIsSelected(false);
          });
          exercise.deleteAttribute(attribute, false);
        }

        exercise.attributes.push(attribute.duplicate(exercise));

        const newName =
          this.selectedEquipment +
          this.selectedTempo +
          this.selectedROM +
          this.selectedStanceGrip +
          this.selectedLoadAccomodation +
          this.selectedAccessories +
          this.selectedOther +
          t(exercise.originalName);

        exercise.setProperty("name", newName);
      }
    }

    // if (exercise.block) {
    //   exercise.block.updateExercisesNameWithProtocol(exercise);
    // }
  }

  deselectAttribute(exercise, attribute, t) {
    attribute.setIsSelected(false);

    if (this.coachLanguage === "es") {
      if (attribute.title === Attribute.AttributeTitles.equipment) {
        this.selectedEquipment = this.selectedEquipment.replace(
          " " + "con " + t(attribute.name),
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.rom) {
        this.selectedROM = this.selectedROM.replace(
          " " + t(attribute.name),
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.tempo) {
        this.selectedTempo = this.selectedTempo.replace(
          " " + t(attribute.name),
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.stanceGrip) {
        this.selectedStanceGrip = this.selectedStanceGrip.replace(
          " " + t(attribute.name),
          ""
        );
      } else if (
        attribute.title === Attribute.AttributeTitles.loadAccomodation
      ) {
        this.selectedLoadAccomodation = this.selectedLoadAccomodation.replace(
          " " + t(attribute.name),
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.accessories) {
        this.selectedAccessories = this.selectedAccessories.replace(
          " " + t(attribute.name),
          ""
        );
      } else {
        this.selectedOther = this.selectedOther.replace(
          " " + t(attribute.name),
          ""
        );
      }

      exercise.deleteAttribute(attribute, true);

      const newName =
        t(exercise.originalName) +
        this.selectedTempo +
        this.selectedROM +
        this.selectedStanceGrip +
        this.selectedLoadAccomodation +
        this.selectedAccessories +
        this.selectedOther +
        this.selectedEquipment;

      exercise.setProperty("name", newName);
    } else {
      if (attribute.title === Attribute.AttributeTitles.equipment) {
        this.selectedEquipment = this.selectedEquipment.replace(
          t(attribute.name) + " ",
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.rom) {
        this.selectedROM = this.selectedROM.replace(
          t(attribute.name) + " ",
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.tempo) {
        this.selectedTempo = this.selectedTempo.replace(
          t(attribute.name) + " ",
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.stanceGrip) {
        this.selectedStanceGrip = this.selectedStanceGrip.replace(
          t(attribute.name) + " ",
          ""
        );
      } else if (
        attribute.title === Attribute.AttributeTitles.loadAccomodation
      ) {
        this.selectedLoadAccomodation = this.selectedLoadAccomodation.replace(
          t(attribute.name) + " ",
          ""
        );
      } else if (attribute.title === Attribute.AttributeTitles.accessories) {
        this.selectedAccessories = this.selectedAccessories.replace(
          t(attribute.name) + " ",
          ""
        );
      } else {
        this.selectedOther = this.selectedOther.replace(
          t(attribute.name) + " ",
          ""
        );
      }

      exercise.deleteAttribute(attribute, true);

      const newName =
        this.selectedEquipment +
        this.selectedTempo +
        this.selectedROM +
        this.selectedStanceGrip +
        this.selectedLoadAccomodation +
        this.selectedAccessories +
        this.selectedOther +
        t(exercise.originalName);

      exercise.setProperty("name", newName);
    }

    // if (exercise.block) {
    //   exercise.block.updateExercisesNameWithProtocol(exercise);
    // }
  }

  getAllAttributes() {
    return [
      ...this.customPredefinedAttributes,
      ...this.customPredefinedAttributes,
    ];
  }

  selectPredefinedForSlot(exercise, t) {
    if (this.coachLanguage === "es") {
      const newName =
        t(exercise.originalName) +
        this.selectedTempo +
        this.selectedROM +
        this.selectedStanceGrip +
        this.selectedLoadAccomodation +
        this.selectedAccessories +
        this.selectedOther +
        this.selectedEquipment;

      exercise.setProperty("name", newName);
    } else {
      //Angles
      const newName =
        this.selectedEquipment +
        this.selectedTempo +
        this.selectedROM +
        this.selectedStanceGrip +
        this.selectedLoadAccomodation +
        this.selectedAccessories +
        this.selectedOther +
        t(exercise.originalName);

      exercise.setProperty("name", newName);
    }
  }

  convertUnit(number, bool) {
    if (bool) {
      //To lbs
      const converted = number * 0.45359237;
      return converted;
    } else {
      const converted = number * 2.2046;
      return converted;
    }
  }

  selectedClient() {
    if (this.teamCoach) {
      if (this.teamCoach.selectedClientId) {
        return this.teamCoach.activeClients.find(
          (client) => client.id === this.teamCoach.selectedClientId
        );
      } else {
        return null;
      }
    } else {
      if (this.selectedClientId) {
        return this.activeClients.find(
          (client) => client.id === this.selectedClientId
        );
      } else {
        return null;
      }
    }
  }

  selectClient(clientId, t, i18n, hideChat = true) {
    if (!this.isLoading && this.isActive) {
      this.setProperty("showingCoachView", "ClientDashboard");
      if (this.teamCoach) {
        this.teamCoach.setProperty("selectedClientId", clientId);
        this.teamCoach.fetchNewClient(clientId, t, i18n.language);
      } else {
        this.setProperty("selectedClientId", clientId);
        this.fetchNewClient(clientId, t, i18n.language);
      }

      if (this.chatIsExpanded && hideChat) {
        this.setProperty("chatIsExpanded", false);
        this.setProperty("showingChatList", false);
        const mediaManager = MediaManager.getInstance();
        mediaManager?.closeMediaPlayer();
      }
      this.setProperty("sideMenuVisible", false);
    }
  }

  unselectClient() {
    this.selectedClientId = null;
  }
  // #endregion

  // #region /* APPLICATIONS */
  async fetchApplicationFormFromCloud() {
    const formDocRef = doc(db, "coaching_application_forms", this.id);

    try {
      const docSnapshot = await getDoc(formDocRef);

      if (docSnapshot.exists()) {
        const formData = docSnapshot.data();
        this.fetchApplicationForm(formData);
      } else {
        const applicationForm = new CoachingApplication(
          uuidv4(),
          this.coachDirectory,
          "",
          this
        );
        const section = new ApplicationSection(
          uuidv4(),
          "",
          0,
          [],
          applicationForm
        );
        const question = new ApplicationQuestion(
          uuidv4(),
          "",
          false,
          "",
          0,
          section
        );
        section.questions.push(question);
        applicationForm.sections.push(section);
        this.applicationForm = applicationForm;
        console.log("No application form found. Initialized a new one.");
      }
      // this.initEditApplicationFormView();
    } catch (error) {
      throw error;
      // console.error("Error checking application form:", error);
    }
  }

  fetchApplicationForm(formData) {
    const applicationForm = new CoachingApplication(
      formData.id,
      this.coachDirectory,
      formData.description,
      this
    );
    formData.sections.forEach((sectionData) => {
      const newSection = new ApplicationSection(
        sectionData.id,
        sectionData.name,
        sectionData.order,
        [], // Questions will be populated next
        applicationForm // Link the section to the form
      );

      sectionData.questions.forEach((questionData) => {
        const newQuestion = new ApplicationQuestion(
          questionData.id,
          questionData.question,
          questionData.is_required,
          questionData.answer,
          questionData.order,
          newSection // Link the question to the section
        );

        newSection.questions.push(newQuestion); // Add the question to the section
      });

      applicationForm.sections.push(newSection); // Add the section to the form
    });

    this.applicationForm = applicationForm;
  }

  initEditApplicationFormView() {
    if (this.applicationForm?.sections?.length > 0) {
      this.applicationFormSections = [];
      this.applicationForm.sections.forEach((s) => {
        const auxS = new ApplicationSection(s.id, s.name, s.order, [], null);
        s.questions.forEach((q) => {
          const auxQ = new ApplicationQuestion(
            q.id,
            q.question,
            q.isRequired,
            q.answer,
            q.order,
            auxS
          );
          auxS.questions.push(auxQ);
        });
        this.applicationFormSections.push(auxS);
      });
    } else {
      this.applicationFormSections = [];
      this.addApplicationSection();
    }
  }

  addApplicationSection() {
    const newOrder = this.applicationFormSections[
      this.applicationFormSections.length - 1
    ]
      ? this.applicationFormSections[this.applicationFormSections.length - 1]
          .order + 1
      : 0;

    const newSection = new ApplicationSection(uuidv4(), "", newOrder, []);

    const emptyQuestion = new ApplicationQuestion(
      uuidv4(),
      "",
      false,
      "",
      0,
      newSection
    );

    newSection.questions.push(emptyQuestion);
    this.applicationFormSections.push(newSection);
  }

  deleteApplicationSection(section) {
    const index = this.applicationFormSections.findIndex((s) => s === section);

    // Check if the question was found
    if (index > -1 && this.applicationFormSections.length > 1) {
      this.applicationFormSections.splice(index, 1);
    }
  }

  resetAuxApplicationForm() {
    this.applicationFormSections = [];
  }

  get canSaveApplicationForm() {
    const questionsAsked = this.applicationFormSections.some((section) =>
      section.questions.some((question) => question.question !== "")
    );

    return questionsAsked;
  }

  async EditApplicationForm() {
    if (this.applicationForm) {
      this.applicationForm.sections = [];
      this.applicationFormSections.forEach((s) => {
        const auxS = new ApplicationSection(s.id, s.name, s.order, [], null);
        s.questions.forEach((q) => {
          const auxQ = new ApplicationQuestion(
            q.id,
            q.question,
            q.isRequired,
            q.answer,
            q.order,
            auxS
          );
          auxS.questions.push(auxQ);
        });
        this.applicationForm.sections.push(auxS);
      });

      try {
        const formData = this.applicationForm.uploadToCloud();

        const docRef = doc(db, "coaching_application_forms", this.id);

        await setDoc(docRef, formData, { merge: true });
        console.log("Application form uploaded successfully.");
      } catch (error) {
        console.error("Error uploading application form:", error);
      }
    }

    //Upload to firestore here

    this.applicationFormSections = [];
  }

  get selectedApplication() {
    const selectedApplication = this.applications.find(
      (application) => application.isSelected === true
    );
    return selectedApplication;
  }

  setSelectedApplication(selectedApplication) {
    this.applications.forEach((application) => {
      if (selectedApplication && application.id === selectedApplication.id) {
        application.isSelected = true;
      } else {
        application.isSelected = false;
      }
    });
  }

  get sortedApplications() {
    return this.applications.slice().sort((a, b) => b.date - a.date);
  }

  get applicationsHasUnread() {
    return this.applications.some(
      (application) => application.seenByCoach === false
    );
  }

  fetchApplication(formData) {
    const applicationForm = new CoachingApplication(
      formData.id,
      this.coachDirectory,
      formData.description,
      this,
      null,
      formData.date?.toDate ? formData.date.toDate() : new Date(),
      formData.client_name,
      formData.client_surname,
      formData.client_email,
      formData.has_competed,
      [],
      formData.coach_id,
      formData.seen_by_coach ?? true
    );
    formData.sections.forEach((sectionData) => {
      const newSection = new ApplicationSection(
        sectionData.id,
        sectionData.name,
        sectionData.order,
        [],
        applicationForm
      );

      sectionData.questions.forEach((questionData) => {
        const newQuestion = new ApplicationQuestion(
          questionData.id,
          questionData.question,
          questionData.is_required,
          questionData.answer,
          questionData.order,
          newSection
        );

        newSection.questions.push(newQuestion);
      });

      applicationForm.sections.push(newSection); // Add the section to the form
    });

    this.applications.push(applicationForm);
  }

  async fetchApplicationsFromCloud() {
    try {
      const applicationsRef = collection(db, "answered_coaching_applications");

      const q = query(applicationsRef, where("coach_id", "==", this.id));

      const querySnapshot = await getDocs(q);

      const existingApplicationIds = new Set(
        this.applications.map((app) => app.id)
      );

      querySnapshot.forEach((doc) => {
        const formData = doc.data();
        if (!existingApplicationIds.has(formData.id)) {
          this.fetchApplication(formData);
          existingApplicationIds.add(formData.id);
        }
      });

      console.log("Applications fetched successfully.");
    } catch (error) {
      // console.error("Error fetching applications: ", error);
      throw error;
    }
  }

  async fetchUnreadApplicationsFromCloud() {
    try {
      const applicationsRef = collection(db, "answered_coaching_applications");
      const q = query(
        applicationsRef,
        where("coach_id", "==", this.id),
        where("seen_by_coach", "==", false),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return;
      }

      const existingApplicationIds = new Set(
        this.applications.map((app) => app.id)
      );

      querySnapshot.forEach((docSnapshot) => {
        const formData = docSnapshot.data();
        if (!existingApplicationIds.has(formData.id)) {
          this.fetchApplication(formData);
          existingApplicationIds.add(formData.id);
        }
      });
    } catch (error) {
      console.warn("Error fetching unread applications: ", error);
    }
  }

  async openApplication(application) {
    if (!application) return;
    this.setSelectedApplication(application);
  }

  async markApplicationSeen(application) {
    if (!application || application.seenByCoach === true) return;

    application.setProperty("seenByCoach", true);

    try {
      const applicationDocRef = doc(
        db,
        "answered_coaching_applications",
        application.id.toLowerCase()
      );
      await updateDoc(applicationDocRef, { seen_by_coach: true });
    } catch (error) {
      console.warn("Error marking application as seen: ", error);
    }
  }

  async deleteApplication(application) {
    try {
      // Remove application from this.applications array
      this.applications = this.applications.filter(
        (app) => app.id !== application.id
      );

      // Reference to Firestore document
      const applicationDocRef = doc(
        db,
        "answered_coaching_applications",
        application.id.toLowerCase()
      );

      // Delete the application document from Firestore
      await deleteDoc(applicationDocRef);

      console.log(
        `Application with ID ${application.id} deleted successfully.`
      );
    } catch (error) {
      console.error(
        `Error deleting application with ID ${application.id}:`,
        error
      );
    }
  }

  async acceptApplication(application, showToast) {
    console.log("acceptApplication FUNC");
    const invitationsColRef = collection(db, "invitations");
    const trimmedEmail = application.clientEmail.trim();

    if (
      !application.clientEmail ||
      !application.clientEmail.includes("@") ||
      !application.clientEmail.includes(".") ||
      trimmedEmail.includes(" ")
    ) {
      application.emailIsWrong = true;
      console.log("acceptApplication FUNC 1");
      return;
    }
    const queryInvitations = query(
      invitationsColRef,
      where("new_client_email", "==", trimmedEmail.toLowerCase())
    );

    const querySnapshot = await getDocs(queryInvitations);

    if (querySnapshot.empty) {
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("email", "==", trimmedEmail.toLowerCase())
      );

      const qSnapshot = await getDocs(q);
      if (!qSnapshot.empty) {
        const userDoc = qSnapshot.docs[0];
        const userData = userDoc.data();

        if ("coach_id" in userData && userData.coach_id !== "") {
          application.userAlreadyHasCoach = true;
          // setIsLoading(false);
        } else {
          const newInvitationDocRef = doc(invitationsColRef);
          const initialForm = this.forms.filter(
            (f) => f.type === "Inicial" && f.numberOfQuestionsAsked > 0
          )[0];
          const newInvitationData = {
            coach_id: this.id,
            coach_email: this.email,
            coach_name: this.name,
            coach_surname: this.surname,
            coach_language: this.coachLanguage,
            coach_rir_or_rpe: this.rirOrRpe,
            coach_exercise_unit: this.exerciseUnit,
            new_client_email: trimmedEmail.toLowerCase(),
            initial_form: initialForm
              ? initialForm.uploadInitialToCloud()
              : null,
            timestamp: serverTimestamp(),
            coach_weekly_form_id: this.weeklyFormId,
            coach_block_form_id: this.blockFormId,
          };

          try {
            await setDoc(newInvitationDocRef, newInvitationData);
            await this.deleteApplication(application);

            if (showToast) {
              showToast("invitationSent-string", {
                type: "info",
                description: "invitationSentDescription-string",
              });
            }

            // setEmailSent(true);
            // setIsLoading(false);
          } catch (error) {
            // Rethrow the error to be caught by the caller of sendTemplateEmail
            throw error;
          }
        }
      } else {
        const newInvitationDocRef = doc(invitationsColRef);
        const initialForm = this.forms.filter(
          (f) => f.type === "Inicial" && f.numberOfQuestionsAsked > 0
        )[0];
        const newInvitationData = {
          coach_id: this.id,
          coach_email: this.email,
          coach_name: this.name,
          coach_surname: this.surname,
          coach_language: this.coachLanguage,
          coach_rir_or_rpe: this.rirOrRpe,
          coach_exercise_unit: this.exerciseUnit,
          new_client_email: trimmedEmail.toLowerCase(),
          initial_form: initialForm ? initialForm.uploadInitialToCloud() : null,
          timestamp: serverTimestamp(),
          coach_weekly_form_id: this.weeklyFormId,
          coach_block_form_id: this.blockFormId,
        };

        try {
          await setDoc(newInvitationDocRef, newInvitationData);

          await this.deleteApplication(application);

          if (showToast) {
            showToast("invitationSent-string", {
              type: "info",
              description: "invitationSentDescription-string",
            });
          }
        } catch (error) {
          // Rethrow the error to be caught by the caller of sendTemplateEmail
          throw error;
        }
      }
    } else {
      // setIsLoading(false);
      application.emailAlreadyInUse = true;
    }
  }
  // #endregion

  // #region /* FORMS LIBRARY */
  searchTermFormLibrary = "";

  getFilteredFormLibrary(type) {
    const searchTermLower = this.searchTermFormLibrary.toLowerCase();

    // First, filter the forms based on the search term if provided and the type
    const filteredForms = this.forms.filter(
      (form) =>
        (this.searchTermFormLibrary
          ? form.name.toLowerCase().includes(searchTermLower)
          : true) && form.type === type
    );

    // Then, sort the filtered forms based on the type and their respective IDs
    const sortedForms = filteredForms.sort((a, b) => {
      if (type === "Semanal") {
        // Sort by weeklyFormId if type is "Semanal"
        if (a.id === this.weeklyFormId && b.id !== this.weeklyFormId) {
          return -1; // a comes before b
        } else if (a.id !== this.weeklyFormId && b.id === this.weeklyFormId) {
          return 1; // b comes before a
        }
      } else if (type === "Fin de bloque") {
        // Sort by blockFormId if type is "Fin de bloque"
        if (a.id === this.blockFormId && b.id !== this.blockFormId) {
          return -1; // a comes before b
        } else if (a.id !== this.blockFormId && b.id === this.blockFormId) {
          return 1; // b comes before a
        }
      }

      // If IDs are not specifically targeted or are the same, sort by name
      return a.name.localeCompare(b.name);
    });

    return sortedForms;
  }

  get selectedForm() {
    const selectedForm = this.forms.find((form) => form.isSelected === true);
    return selectedForm;
  }

  setSelectedForm(selectedForm) {
    this.forms.forEach((form) => {
      if (selectedForm && form.id === selectedForm.id) {
        form.setIsSelected(true);
      } else {
        form.setIsSelected(false);
      }
    });
  }

  get numberOfForms() {
    const notInitialForms = this.forms.filter(
      (f) => f.type !== "Inicial"
    ).length;

    return notInitialForms;
  }

  formTypeLocalized(t) {
    if (this.formType === "Inicial") {
      return t("initialFormType-string");
    } else if (this.formType === "Semanal") {
      return t("weeklyFormType-string");
    } else if (this.formType === "Fin de bloque") {
      return t("blockFormType-string");
    } else {
      return this.formType;
    }
  }

  initAddFormView(form) {
    if (form) {
      this.editingForm = form;
      this.formName = form.name;
      this.formType = form.type;
      this.formDay = form.weekDay;
      this.formSections = [];
      form.sections.forEach((s) => {
        const auxS = new FormSection(s.id, s.name, s.order, [], null);
        s.questions.forEach((q) => {
          const auxQ = new Question(
            q.id,
            q.question,
            q.type,
            q.answer,
            q.order,
            auxS
          );
          auxS.questions.push(auxQ);
        });
        this.formSections.push(auxS);
      });

      this.formSelectedClients = [];
      this.formDesSelectedClients = [];
      if (form.type === "Semanal") {
        this.clients
          .filter((c) => c.weeklyFormId === form.id.toString().toLowerCase())
          .forEach((client) => {
            this.formSelectedClients.push(client);
          });
      } else if (form.type === "Fin de bloque") {
        this.clients
          .filter((c) => c.blockFormId === form.id.toString().toLowerCase())
          .forEach((client) => {
            this.formSelectedClients.push(client);
          });
      }
    } else {
      this.editingForm = null;
      this.formName = "";
      this.formType = "- - -";
      this.formDay = 0;
      this.formSections = [];
      this.formSelectedClients = [];
      this.formDesSelectedClients = [];
      this.addFormSection();
    }
  }

  resetAuxForm() {
    this.editingForm = null;
    this.formName = "";
    this.formType = "- - -";
    this.formDay = 0;
    this.formSections = [];
  }

  get canSaveForm() {
    // For "Inicial" forms being edited, allow saving even without questions
    if (this.editingForm && this.editingForm.type === "Inicial") {
      return this.formName !== "" && this.formType !== "- - -";
    }

    const questionsAsked = this.formSections.some((section) =>
      section.questions.some((question) => question.question !== "")
    );
    if (this.formName !== "" && this.formType !== "- - -" && questionsAsked) {
      return true;
    } else {
      return false;
    }
  }

  async addOrEditForm(form) {
    if (!this.canSaveForm) {
      return;
    }

    // Helper function to create a new form section and its questions
    const createSectionsAndQuestions = (form) => {
      form.sections = [];
      this.formSections.forEach((s) => {
        const auxS = new FormSection(s.id, s.name, s.order, [], form);
        s.questions.forEach((q) => {
          const auxQ = new Question(
            q.id,
            q.question,
            q.type,
            q.answer,
            q.order,
            auxS
          );
          auxS.questions.push(auxQ);
        });
        form.sections.push(auxS);
      });
    };

    let clientsToUpdate = new Map(); // To hold the client and its form updates

    if (form) {
      // Editing an existing form
      form.name = this.formName;
      form.type = this.formType;
      form.weekDay = this.formDay;

      createSectionsAndQuestions(form);

      // Prepare clients to update for removal or addition of the form
      this.formDesSelectedClients.forEach((client) => {
        if (form.type === "Semanal") {
          client.weeklyFormId = "";
          clientsToUpdate.set(client, { weekly_form_id: "" });
        } else if (form.type === "Fin de bloque") {
          client.blockFormId = "";
          clientsToUpdate.set(client, { block_form_id: "" });
        }
      });

      this.formSelectedClients.forEach((client) => {
        if (form.type === "Semanal") {
          client.weeklyFormId = form.id;
          clientsToUpdate.set(client, { weekly_form_id: form.id });
        } else if (form.type === "Fin de bloque") {
          client.blockFormId = form.id;
          clientsToUpdate.set(client, { block_form_id: form.id });
        }
      });

      form.isSelected = false;

      this.updateCoachTransactionForms(form.id);
    } else {
      // Adding a new form
      const newForm = new Form(
        uuidv4(),
        this.formName,
        this.formType,
        this.formDay,
        null,
        [],
        this
      );
      createSectionsAndQuestions(newForm);
      this.forms.push(newForm);

      if (newForm.type === "Semanal") {
        if (this.weeklyFormId === "") {
          this.weeklyFormId = newForm.id.toString().toLowerCase();
        }
      } else if (newForm.type === "Fin de bloque") {
        if (this.blockFormId === "") {
          this.blockFormId = newForm.id.toString().toLowerCase();
        }
      }

      // Prepare clients to update for the new form
      this.formDesSelectedClients.forEach((client) => {
        if (newForm.type === "Semanal") {
          client.weeklyFormId = "";
          clientsToUpdate.set(client, { weekly_form_id: "" });
        } else if (newForm.type === "Fin de bloque") {
          client.blockFormId = "";
          clientsToUpdate.set(client, { block_form_id: "" });
        }
      });

      this.formSelectedClients.forEach((client) => {
        if (newForm.type === "Semanal") {
          client.weeklyFormId = newForm.id;
          clientsToUpdate.set(client, { weekly_form_id: newForm.id });
        } else if (newForm.type === "Fin de bloque") {
          client.blockFormId = newForm.id;
          clientsToUpdate.set(client, { block_form_id: newForm.id });
        }
      });

      // if (newForm.type === "Semanal") {
      //   if (
      //     this.forms.filter((f) => f.type === "Semanal" && f.id !== newForm.id)
      //       .length === 0
      //   ) {
      //     this.weeklyFormId = newForm.id.toString().toLowerCase();
      //     this.clients.forEach((client) => {
      //       client.weeklyFormId = newForm.id.toString().toLowerCase();
      //       clientsToUpdate.set(client, {
      //         weekly_form_id: newForm.id.toString().toLowerCase(),
      //       });
      //     });
      //   }
      // } else if (newForm.type === "Fin de bloque") {
      //   if (
      //     this.forms.filter(
      //       (f) => f.type === "Fin de bloque" && f.id !== newForm.id
      //     ).length === 0
      //   ) {
      //     this.blockFormId = newForm.id.toString().toLowerCase();
      //     this.clients.forEach((client) => {
      //       client.blockFormId = newForm.id.toString().toLowerCase();
      //       clientsToUpdate.set(client, {
      //         block_form_id: newForm.id.toString().toLowerCase(),
      //       });
      //     });
      //   }
      // }

      this.updateCoachTransactionForms(newForm.id);
    }

    // Perform a batch update for all clients
    await this.updateClientsInBatch(clientsToUpdate);

    // Reset form fields and selections
    this.formName = "";
    this.formType = "- - -";
    this.formDay = 0;
    this.formSections = [];
    this.formSelectedClients = [];
    // this.formDesSelectedClients = []; això ho hauria de descomentar?
    this.startOrResetBatchTimer();
  }

  async updateClientsInBatch(clientsToUpdate) {
    const batch = writeBatch(db);

    clientsToUpdate.forEach((updates, client) => {
      console.log("client.id");
      const userDocRef = doc(db, "users", client.id);
      batch.update(userDocRef, updates);
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error("Error performing batch update for clients", error);
    }
  }

  addFormSection() {
    const newOrder = this.formSections[this.formSections.length - 1]
      ? this.formSections[this.formSections.length - 1].order + 1
      : 0;

    const newSection = new FormSection(uuidv4(), "", newOrder, []);

    const emptyQuestion = new Question(
      uuidv4(),
      "",
      "Texto",
      "",
      0,
      newSection
    );

    newSection.questions.push(emptyQuestion);
    this.formSections.push(newSection);
  }

  deleteForm(form) {
    //FALTA FICAR TOTA LA LLOGICA DE QUE EL BORRI DE TOTS ELS CLIENTS EN ELS QUE ESTA
    /* ACTUALITZAR WEEKLY_FORM_ID O BLOCK_FORM_ID A FIRESTORE */
    let batch = writeBatch(db);
    let formIdAttribute;
    let property;
    let clientsWithFormToDelete;

    if (form.type === "Semanal") {
      clientsWithFormToDelete = this.clients.filter(
        (client) => client.weeklyFormId === form.id
      );
      formIdAttribute = "weekly_form_id";
      property = "weeklyFormId";

      if (this.weeklyFormId === form.id.toString().toLowerCase()) {
        this.weeklyFormId = "";
      }
    } else if (form.type === "Fin de bloque") {
      clientsWithFormToDelete = this.clients.filter(
        (client) => client.blockFormId === form.id
      );
      formIdAttribute = "block_form_id";
      property = "blockFormId";

      if (this.blockFormId === form.id.toString().toLowerCase()) {
        this.blockFormId = "";
      }
    }

    if (
      formIdAttribute != null &&
      clientsWithFormToDelete &&
      clientsWithFormToDelete.length > 0
    ) {
      clientsWithFormToDelete.forEach((client) => {
        const userRef = doc(db, "users", client.id);
        client.setProperty(property, "");
        batch.update(userRef, {
          [formIdAttribute]: deleteField(), // Using computed property name
        });
      });

      batch
        .commit()
        .then(() => {
          console.log("Batch update committed successfully");
        })
        .catch((error) => {
          console.error("Error committing batch update: ", error);
        });
    }

    this.updateCoachDeletionForms(form.id);
    this.startOrResetBatchTimer();

    const index = this.forms.findIndex((f) => f === form);
    if (index > -1) {
      this.forms.splice(index, 1);
    }
  }

  deleteFormSection(section) {
    const index = this.formSections.findIndex((s) => s === section);

    // Check if the question was found
    if (index > -1 && this.formSections.length > 1) {
      // Remove the question from the array
      this.formSections.splice(index, 1);
    }
  }

  clientsInForm(form) {
    const clients = this.clients.filter(
      (client) =>
        client.status === "active-string" && // Check if the client is active
        (form.type === "Semanal" ? client.weeklyFormId : client.blockFormId) ===
          form.id
    );
    return clients;
  }

  clientsWithForm(form) {
    // Checks if competition is defined and filters clients accordingly
    const clients = this.clients.filter(
      (client) =>
        client.status === "active-string" && // Check if the client is active
        client.forms.some(
          (f) => f.originalFormId === form.id // Check if the competition IDs match
        )
    );
    return clients;
  }

  selectClientForForm(client) {
    // Check if the client is already selected
    const isSelected = this.formSelectedClients.includes(client);

    if (isSelected) {
      // Remove the client's userId from the array
      this.formDesSelectedClients.push(client);
      this.formSelectedClients = this.formSelectedClients.filter(
        (c) => c.id !== client.id
      );
    } else {
      // Add the client's userId to the array
      this.formSelectedClients.push(client);
    }
  }

  searchTermClientsInForm = "";
  getFilteredClientsInForm() {
    const searchTermLower = this.searchTermClientsInForm.toLowerCase();

    let filteredClients = this.activeClients;

    if (this.searchTermClientsInForm) {
      filteredClients = filteredClients.filter((client) => {
        const combinedString = (
          client.nameForCoach +
          " " +
          client.surnameForCoach
        ).toLowerCase();
        return combinedString.includes(searchTermLower);
      });
    }

    filteredClients.sort((a, b) =>
      a.nameForCoach.localeCompare(b.nameForCoach)
    );

    return filteredClients;
  }

  createInitialForm() {
    const initialForm = new Form(
      uuidv4(),
      this.coachLanguage === "en"
        ? "Athlete Intake Form"
        : "Cuestionario inicial",
      "Inicial",
      0,
      null,
      [],
      this,
      null,
      "",
      null
    );
    const section = new FormSection(uuidv4(), "", 0, [], initialForm);
    const question = new Question(uuidv4(), "", "Texto", "", 0, section);
    section.questions.push(question);
    initialForm.sections.push(section);
    this.forms.push(initialForm);
  }
  // #endregion

  // #region /* WORKOUT LIBRARY */
  searchTermWorkoutLibrary = "";

  getFilteredWorkoutLibrary() {
    const searchTermLower = this.searchTermWorkoutLibrary.toLowerCase();

    // First, filter the workouts based on the search term if provided
    const filteredWorkouts = this.workoutLibrary.filter((workout) =>
      // Assuming the workout has a 'name' property to filter by
      // If there's no searchTermWorkoutLibrary, all workouts are included
      this.searchTermWorkoutLibrary
        ? workout.name.toLowerCase().includes(searchTermLower)
        : true
    );

    // Then, sort the filtered workouts by their 'order'
    const sortedWorkouts = filteredWorkouts.sort((a, b) => a.order - b.order);

    return sortedWorkouts;
  }

  get selectedWorkout() {
    const selectedWorkout = this.workoutLibrary.find(
      (workout) => workout.isSelected === true
    );
    return selectedWorkout;
  }

  setSelectedWorkout(selectedWorkout) {
    this.workoutLibrary.forEach((workout) => {
      if (selectedWorkout && workout.id === selectedWorkout.id) {
        workout.setIsSelected(true);
      } else {
        workout.setIsSelected(false);
      }
    });
  }
  // #endregion

  // #region /* BLOCK LIBRARY */
  searchTermBlockLibrary = "";

  getFilteredBlockLibrary() {
    const searchTermLower = this.searchTermBlockLibrary.toLowerCase();

    const filteredBlocks = this.blockLibrary.filter((block) =>
      this.searchTermBlockLibrary
        ? block.name.toLowerCase().includes(searchTermLower)
        : true
    );

    // const sortedWorkouts = filteredBlocks.sort((a, b) => a.order - b.order);

    return filteredBlocks;
  }

  get selectedBlock() {
    const selectedBlock = this.blockLibrary.find(
      (block) => block.isSelected === true
    );
    return selectedBlock;
  }

  setSelectedBlock(selectedBlock) {
    this.blockLibrary.forEach((block) => {
      if (selectedBlock && block.id === selectedBlock?.id) {
        block.setProperty("isSelected", true);
      } else {
        block.setProperty("isSelected", false);
      }
    });
  }
  // #endregion

  // #region /* COMPETITION */
  searchTermCompetitions = "";

  getFilteredCompetitions() {
    const searchTermLower = this.searchTermCompetitions.toLowerCase();

    // First, filter the workouts based on the search term if provided
    const filteredCompetitions = this.competitions.filter(
      (competition) =>
        // Assuming the workout has a 'name' property to filter by
        // If there's no searchTermWorkoutLibrary, all workouts are included
        (this.searchTermCompetitions
          ? competition.name.toLowerCase().includes(searchTermLower)
          : true) && competition.notNew
    );

    // Then, sort the filtered workouts by their 'order'
    const sortedCompetitions = filteredCompetitions.sort(
      (a, b) => b.date - a.date
    );

    return sortedCompetitions;
  }

  get selectedCompetition() {
    const selectedCompetition = this.competitions.find(
      (competition) => competition.isSelected === true
    );
    return selectedCompetition;
  }

  setSelectedCompetition(selectedCompetition) {
    this.competitions.forEach((competition) => {
      if (selectedCompetition && competition.id === selectedCompetition.id) {
        competition.setIsSelected(true);
      } else {
        competition.setIsSelected(false);
      }
    });
  }

  get getFutureCompetitions() {
    // First, filter the competitions based on whether their date is in the future
    const filteredCompetitions = this.competitions.filter(
      (competition) => new Date(competition.date) > new Date()
    );

    // Then, sort the filtered competitions by their date
    const sortedCompetitions = filteredCompetitions.sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    return sortedCompetitions;
  }

  initAddCompetitionView(competition) {
    if (competition) {
      this.competitionName = competition.name;
      this.competitionCategory = competition.competitionClass;
      this.competitionLevel = competition.competitionLevel;
      this.competitionDate = competition.date;
      this.competitionSelectedClients = [];
      this.clients.forEach((client) => {
        client.initAddCompetitionView(competition);
      });
    } else {
      this.competitionName = "";
      this.competitionCategory = "";
      this.competitionLevel = "";
      this.competitionDate = new Date();
      this.competitionSelectedClients = [];
      this.clients.forEach((client) => {
        client.initAddCompetitionView();
      });
    }
    this.showingCustomCategory = false;
    this.showingCustomLevel = false;
    this.selectedWeightClass = "";
  }

  resetAuxCompetition() {
    this.competitionName = "";
    this.competitionCategory = "";
    this.competitionLevel = "";
    this.competitionDate = new Date();
    this.competitionSelectedClients.forEach((client) => {
      client.resetAuxCompetition();
    });
    this.competitionSelectedClients = [];
    this.selectedWeightClass = "";
    this.showingCustomCategory = false;
    this.showingCustomLevel = false;
  }

  async addOrEditCompetition(competition) {
    if (competition) {
      competition.name = this.competitionName;
      competition.competitionClass = this.competitionCategory;
      competition.competitionLevel = this.competitionLevel;
      competition.date = this.competitionDate;

      // this.competitionSelectedClients.forEach((client) => {
      //   //Si el client ja te la compe sedita la info, si no la te, se li crea. Possiblement crear una func de addOrEditCompetition a client es lo millor
      // });

      this.clients.forEach((client) => {
        if (this.competitionSelectedClients.includes(client)) {
          client.addOrEditCompetition(competition);
        } else {
          client.deleteCompetition(competition);
        }
      });

      competition.isSelected = false;

      this.updateCoachTransactionCompetitions(competition.id);
    } else {
      // Adding a new form
      const id = uuidv4();
      const newComp = new Competition(
        id.toLowerCase(),
        this.competitionName,
        this.competitionDate,
        this,
        null,
        "",
        this.competitionCategory,
        this.competitionLevel,
        "",
        "",
        "",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );
      this.competitions.push(newComp);

      // this.competitionSelectedClients.forEach((client) => {
      //   //Se li crea. Possiblement crear una func de addOrEditCompetition a client es lo millor
      // });

      this.clients.forEach((client) => {
        if (this.competitionSelectedClients.includes(client)) {
          client.addOrEditCompetition(newComp);
        }
      });

      this.updateCoachTransactionCompetitions(newComp.id);
    }

    // Reset form fields and selections
    this.formName = "";
    this.formType = "- - -";
    this.formDay = 0;
    this.formSections = [];
    this.formSelectedClients = [];
    this.showingCustomCategory = false;
    this.showingCustomLevel = false;

    this.startOrResetBatchTimer();
  }

  getFilteredCompetitionClients(gender) {
    const searchTermLower = this.searchTerm.toLowerCase();

    const filteredClients = this.clients
      .filter((client) => {
        // Filter based on search term if it exists
        const isSearchTermMatch = this.searchTerm
          ? (client.nameForCoach + " " + client.surnameForCoach)
              .toLowerCase()
              .includes(searchTermLower)
          : true;

        // Ensure client does not have the specified competition ID in their competitions array
        // const hasCompetition = client.competitions.some(
        //   (comp) => comp.id === competition.id
        // );
        const isNotSelected = !this.competitionSelectedClients.includes(client);

        // Combine conditions
        return (
          isSearchTermMatch &&
          isNotSelected &&
          client.gender === gender &&
          client.status === "active-string" &&
          client.isCompetitor
        );
      })
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.nameForCoach.localeCompare(b.nameForCoach);
      });

    return filteredClients;
  }

  clientsInComp(competition) {
    const clients = this.clients.filter(
      (client) =>
        client.competitions.some((comp) => comp.id === competition.id) &&
        client.status === "active-string" &&
        client.isCompetitor
    );
    return clients;
  }

  clientsInWeightClass(competition, weightClass) {
    // console.log("PROVA COMPETIS WEIGHT: ", weightClass);
    if (competition) {
      // console.log("PROVA COMPETIS WEIGHT IF");
      const clients = this.clients.filter(
        (client) =>
          client.isCompetitor &&
          client.status === "active-string" && // Check if the client is active
          client.competitions.some(
            (comp) =>
              comp.id === competition.id && // Check if the competition IDs match
              comp.weightClass === weightClass // Check if the weight class matches
          )
      );
      return clients;
    } else {
      // console.log(
      //   "PROVA COMPETIS WEIGHT ELSE: ",
      //   this.competitionSelectedClients
      // );
      const clients = this.competitionSelectedClients.filter(
        (client) =>
          client.isCompetitor &&
          client.status === "active-string" && // Check if the client is active
          client.selectedWeightClass === weightClass
      );
      // console.log("PROVA COMPETIS WEIGHT ELSE RETURN: ", clients);
      return clients;
    }
  }

  clientsInGender(competition, gender) {
    // Checks if competition is defined and filters clients accordingly
    const clients = this.clients.filter(
      (client) =>
        client.gender === gender &&
        client.isCompetitor &&
        client.status === "active-string" && // Check if the client is active
        client.competitions.some(
          (comp) => comp.id === competition.id // Check if the competition IDs match
        )
    );
    return clients;
  }

  get selectedClientComp() {
    const selectedClientComp = this.clients.find(
      (client) => client.isSelectedComp === true
    );
    return selectedClientComp;
  }

  setSelectedClientComp(client) {
    this.clients.forEach((cl) => {
      if (client && cl.id === client.id) {
        cl.setIsSelectedComp(true);
      } else {
        cl.setIsSelectedComp(false);
      }
    });
  }

  deleteCompetition(competition) {
    this.updateCoachDeletionCompetitions(competition.id);
    this.startOrResetBatchTimer();

    this.competitions = this.competitions.filter(
      (comp) => comp.id !== competition.id
    );

    const compClients = this.clients.filter((client) =>
      client.competitions.some((comp) => comp.id === competition.id)
    );

    compClients.forEach((client) => {
      client.updateClientDeletionCompetitions(competition.id);
      client.startOrResetBatchTimer();
      client.competitions = client.competitions.filter(
        (comp) => comp.id !== competition.id
      );
    });
  }
  // #endregion

  // #region /* BILLING */
  get clientToRenew() {
    return this.activeClients.find((client) => client.selectedToRenew === true);
  }

  getFilteredBillingClients() {
    const searchTermLower = this.searchTerm.toLowerCase();
    const monthIndex =
      this.sortTerm === "ene"
        ? 0
        : this.sortTerm === "feb"
        ? 1
        : this.sortTerm === "mar"
        ? 2
        : this.sortTerm === "abr"
        ? 3
        : this.sortTerm === "may"
        ? 4
        : this.sortTerm === "jun"
        ? 5
        : this.sortTerm === "jul"
        ? 6
        : this.sortTerm === "ago"
        ? 7
        : this.sortTerm === "set"
        ? 8
        : this.sortTerm === "oct"
        ? 9
        : this.sortTerm === "nov"
        ? 10
        : this.sortTerm === "dic"
        ? 11
        : -1; // This assumes your sortTerm will be a three-letter abbreviation of the month.

    let filteredClients = this.clients.filter((client) => {
      // First, filter by search term if any
      const includesSearchTerm =
        this.searchTerm.length === 0 ||
        (client.nameForCoach + " " + client.surnameForCoach)
          .toLowerCase()
          .includes(searchTermLower);

      // Then, if onlyActive is true, further filter by the "active-string" status
      const isActiveClient = client.status === "active-string";

      return includesSearchTerm && isActiveClient;
    });

    if (this.sortTerm === "") return filteredClients;
    if (monthIndex === -1) {
      if (this.sortTerm === "client") {
        return filteredClients.slice().sort((a, b) => {
          // Prioritize clients with an existing lastFee
          // const aHasLastFee = !!a.lastFee; // true if a.lastFee exists, false otherwise
          // const bHasLastFee = !!b.lastFee; // true if b.lastFee exists, false otherwise

          // // If one has a lastFee and the other doesn't, prioritize the one with a lastFee
          // if (aHasLastFee && !bHasLastFee) return -1;
          // if (!aHasLastFee && bHasLastFee) return 1;

          // For clients both with or without a lastFee, compare names alphabetically
          const nameA = a.nameForCoach.toLowerCase();
          const nameB = b.nameForCoach.toLowerCase();

          // Alphabetical comparison
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          // If names are equal (unlikely but possible with different clients having the same name),
          // retain their order or compare by another attribute
          return 0;
        });
      } else if (this.sortTerm === "startDate") {
        return filteredClients.slice().sort((a, b) => {
          // Prioritize clients with an existing lastFee's startDate
          const aHasValidLastFeeStartDate =
            a.lastFee &&
            a.lastFee.fee_start_date_year > 0 &&
            a.lastFee.fee_start_date_month > 0 &&
            a.lastFee.fee_start_date_day > 0;
          const bHasValidLastFeeStartDate =
            b.lastFee &&
            b.lastFee.fee_start_date_year > 0 &&
            b.lastFee.fee_start_date_month > 0 &&
            b.lastFee.fee_start_date_day > 0;

          // If one has a valid lastFee's startDate and the other doesn't, prioritize the one with a valid lastFee's startDate
          if (aHasValidLastFeeStartDate && !bHasValidLastFeeStartDate)
            return -1;
          if (!aHasValidLastFeeStartDate && bHasValidLastFeeStartDate) return 1;

          // For clients both with or without a valid lastFee's startDate, compare lastFee's startDate
          if (aHasValidLastFeeStartDate && bHasValidLastFeeStartDate) {
            const dateA = new Date(
              a.lastFee.fee_start_date_year,
              a.lastFee.fee_start_date_month - 1,
              a.lastFee.fee_start_date_day
            );
            const dateB = new Date(
              b.lastFee.fee_start_date_year,
              b.lastFee.fee_start_date_month - 1,
              b.lastFee.fee_start_date_day
            );
            return dateA - dateB; // Sort by oldest lastFee's startDate first
          }

          // If both lack a valid lastFee's startDate or lastFee, retain their current order or sort by another attribute
          return 0;
        });
      } else if (this.sortTerm === "fee") {
        return filteredClients.slice().sort((a, b) => {
          // Check for existence of lastFee
          const hasLastFeeA = a.lastFee !== null;
          const hasLastFeeB = b.lastFee !== null;

          // Move clients without a lastFee to the end
          if (!hasLastFeeA && hasLastFeeB) return 1;
          if (hasLastFeeA && !hasLastFeeB) return -1;

          // Among those with lastFees, sort by fee from highest to lowest
          if (hasLastFeeA && hasLastFeeB) {
            return b.lastFee.fee - a.lastFee.fee;
          }

          // If both don't have a lastFee or fees are equal, then compare by names as a secondary sort criteria
          const nameA = a.nameForCoach.toLowerCase();
          const nameB = b.nameForCoach.toLowerCase();

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          return 0; // names must be equal
        });
      } else if (this.sortTerm === "feeType") {
        return filteredClients.slice().sort((a, b) => {
          // Check for existence of lastFee and prioritize "Mensual"
          const hasLastFeeA = a.lastFee !== null;
          const hasLastFeeB = b.lastFee !== null;

          // Move clients without a lastFee to the end
          if (!hasLastFeeA && hasLastFeeB) return 1;
          if (hasLastFeeA && !hasLastFeeB) return -1;

          // Among those with lastFees, prioritize "Mensual"
          if (hasLastFeeA && hasLastFeeB) {
            if (
              a.lastFee.fee_type === "Mensual" &&
              b.lastFee.fee_type !== "Mensual"
            )
              return -1;
            if (
              a.lastFee.fee_type !== "Mensual" &&
              b.lastFee.fee_type === "Mensual"
            )
              return 1;
          }

          // If both have the same fee_type or both don't have a lastFee, then compare by names
          const nameA = a.nameForCoach.toLowerCase();
          const nameB = b.nameForCoach.toLowerCase();

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          return 0; // names must be equal
        });
      } else if (this.sortTerm === "leftToPay") {
        return filteredClients.slice().sort((a, b) => {
          // Handle clients without a lastFee to be sorted at the end
          const aHasLastFee = a.lastFee !== null;
          const bHasLastFee = b.lastFee !== null;

          if (!aHasLastFee && bHasLastFee) return 1; // a goes to the end
          if (aHasLastFee && !bHasLastFee) return -1; // b goes to the end
          if (!aHasLastFee && !bHasLastFee) return 0; // Both at the end, retain their order

          // Calculate total owed money based on owedQuotas and lastFee.fee
          const totalOwedA = a.owedQuotas * a.lastFee.fee;
          const totalOwedB = b.owedQuotas * b.lastFee.fee;

          // Since we want to sort from highest to lowest total owed, subtract a's total from b's total
          const difference = totalOwedB - totalOwedA;

          if (difference !== 0) return difference; // Prioritize by total owed if different

          // If total owed amounts are equal, use names as a secondary sort criteria to maintain order.
          const nameA = a.nameForCoach.toLowerCase();
          const nameB = b.nameForCoach.toLowerCase();

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          return 0; // names must be equal
        });
      } else if (this.sortTerm === "endDate") {
        return filteredClients.slice().sort((a, b) => {
          // Prioritize clients who owe a quota
          if (a.owedQuotas > 0 && b.owedQuotas <= 0) return -1;
          if (a.owedQuotas <= 0 && b.owedQuotas > 0) return 1;

          // Calculate endDate from lastFee for both clients
          const getEndDate = (client) => {
            if (!client.lastFee) return new Date(9999, 11, 31); // Future date for clients without fees
            const feeTypeIndex =
              client.lastFee.fee_type === "Trimestral" ? 3 : 1;
            const endMonthIndex =
              client.lastFee.fee_start_date_month + feeTypeIndex - 1; // Adjust for 0-indexed months
            const endYear =
              endMonthIndex >= 12
                ? client.lastFee.fee_start_date_year + 1
                : client.lastFee.fee_start_date_year;
            const endMonth =
              endMonthIndex >= 12 ? endMonthIndex - 12 : endMonthIndex;
            return new Date(
              endYear,
              endMonth,
              client.lastFee.fee_start_date_day
            );
          };

          const dateA = getEndDate(a);
          const dateB = getEndDate(b);

          // Earlier endDates come first
          return dateA - dateB;
        });
      } else {
        return filteredClients;
      }
    } else {
      // Sort clients based on their payment status for the specified month
      return filteredClients.slice().sort((a, b) => {
        // Assuming billingMonths is an object where keys are years and values are arrays of payment statuses for each month
        // Check if the month's payment status is available for the year
        const aPaidThisMonth =
          a.billingMonths[this.billingYear] &&
          a.billingMonths[this.billingYear][monthIndex];
        const bPaidThisMonth =
          b.billingMonths[this.billingYear] &&
          b.billingMonths[this.billingYear][monthIndex];

        // Prioritize clients who haven't paid this month
        if (!aPaidThisMonth && bPaidThisMonth) return -1;
        if (aPaidThisMonth && !bPaidThisMonth) return 1;

        // If both have the same payment status for this month, do not change their order
        return 0;
      });
    }
  }

  getTotalOwed(t, language) {
    const total = this.clients.reduce((total, client) => {
      // Only add to total if owedQuotas is greater than 0 and lastFee exists
      if (client.owedQuotas > 0 && client.lastFee) {
        const amount = parseFloat(client.owedQuotas * client.lastFee.fee);
        return total + amount;
      }
      return total; // Return accumulated total if no amount is owed
    }, 0);

    // Format the total with currency based on language
    return language === "es"
      ? `${total} ${t("currency-string")}` // "es" format: total with currency after
      : `${t("currency-string")}${total}`; // Other languages: currency before total
  }

  get getTotalRevenuePerMonth() {
    return this.clients.reduce((total, client) => {
      // Only add to total if owedAmount is a number
      const amount = parseFloat(client.clientFee);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  }
  // #endregion

  // #region /* ONBOARDING */
  get showCompetitionInfoView() {
    //return false when there is no this.competitions with name different than "IPF Worlds 2026"
    return !this.competitions.some((comp) => comp.name !== "IPF Worlds 2026");
  }

  get showFormsLibraryInfoView() {
    //return false when there is any form with type different than Inicial
    return !this.forms.some(
      (form) => form.type === "Semanal" || form.type === "Fin de bloque"
    );
  }

  get showWorkoutLibraryInfoView() {
    //return false when there is any this.workoutLibrary with name different than any of these ("Push Accessory Exercises", "Accesorios Push", "Pull Accessory Exercises", "Accesorios Pull",  "Push Accessory Exercises","Accesorios Push", "Arms", "Brazo",) or more than 3 entries (no matter the name)
    const validNames = [
      "Push Accessory Exercises",
      "Accesorios Push",
      "Pull Accessory Exercises",
      "Accesorios Pull",
      "Arms",
      "Brazo",
    ];

    if (this.workoutLibrary.length > 3) {
      return false;
    }

    // Return false if any workout has a name not in the valid names list
    return !this.workoutLibrary.some(
      (workout) => !validNames.includes(workout.name)
    );
  }

  get showBillingInfoView() {
    //return false if any of the this.activeClients has a feeHistory with an entry or more
    return !this.activeClients.some(
      (client) => client.feeHistory && client.feeHistory.length > 0
    );
  }

  shouldShowUISectionInfo(uiSection) {
    //return false if the coach has this.trialPeriodStartDate null or if the date of trialPeriodStartDate is before 22 October 2025
    if (!this.trialPeriodStartDate) {
      return false;
    }

    const cutoffDate = new Date("2025-10-22");
    const trialStartDate = new Date(this.trialPeriodStartDate);

    if (trialStartDate < cutoffDate) {
      return false;
    }

    // Returns true if the section should show info (not present or present and false)
    // Returns false if the section is present and true (already shown)
    return !this.UISections[uiSection];
  }

  isLegacyCoachForBlockView() {
    if (!this.trialPeriodStartDate) {
      return false;
    }

    const cutoffDate = new Date("2026-02-03");
    const trialStartDate = new Date(this.trialPeriodStartDate);

    return trialStartDate < cutoffDate;
  }

  shouldShowBlockViewUpdateModal(uiSection) {
    if (!this.isLegacyCoachForBlockView()) {
      return false;
    }

    return !this.UISections[uiSection];
  }

  async markUISectionAsShown(uiSection) {
    // Update locally
    this.UISections[uiSection] = true;

    // Update Firestore
    const coachDocRef = doc(db, "coaches", this.id);
    try {
      await updateDoc(coachDocRef, {
        [`ui_sections.${uiSection}`]: true,
      });
      console.log(`UI section ${uiSection} marked as shown`);
    } catch (error) {
      console.error(`Error marking UI section ${uiSection} as shown:`, error);
    }
  }

  async setTestBlockView(value) {
    this.testBlockView = value === true;

    if (!this.networkManager?.isOnline) {
      console.warn("Skipped test_block_view update while offline.");
      return;
    }

    const coachDocRef = doc(db, "coaches", this.id);
    try {
      await updateDoc(coachDocRef, {
        test_block_view: this.testBlockView,
      });
      console.log(`test_block_view set to ${this.testBlockView}`);
    } catch (error) {
      console.error("Error updating test_block_view:", error);
    }
  }

  async setUseLegacyPlanView(value) {
    this.useLegacyPlanView = value === true;

    // if (this.networkManager?.isOnline === false) {
    //   console.warn("Skipped use_legacy_plan_view update while offline.");
    //   return;
    // }

    const coachDocRef = doc(db, "coaches", this.id);
    try {
      await updateDoc(coachDocRef, {
        use_legacy_plan_view: this.useLegacyPlanView,
      });
      console.log(`use_legacy_plan_view set to ${this.useLegacyPlanView}`);
    } catch (error) {
      console.error("Error updating use_legacy_plan_view:", error);
    }
  }

  async setUseLegacyPlanViewSelection(value) {
    if (this.useLegacyPlanView !== true) {
      console.warn(
        "Skipped use_legacy_plan_view_selection update while legacy view is disabled."
      );
      return;
    }

    this.useLegacyPlanViewSelection = value === true;

    const coachDocRef = doc(db, "coaches", this.id);
    try {
      await updateDoc(coachDocRef, {
        use_legacy_plan_view_selection: this.useLegacyPlanViewSelection,
      });
      console.log(
        `use_legacy_plan_view_selection set to ${this.useLegacyPlanViewSelection}`
      );
    } catch (error) {
      console.error("Error updating use_legacy_plan_view_selection:", error);
    }
  }

  // #endregion

  // #region /* WEBAPP CLIPBOARD (IN-APP) */
  // NOTE: This is NOT the OS clipboard. It's an in-app clipboard used for copy/paste
  // within the web app. It stores plain snapshots so paste still works after deletion.
  copiedExercises = null; // Array<ExerciseSnapshot>
  copiedSets = null; // { protocolText: string, sets: Array<SetSnapshot> }

  serializeExerciseForClipboard(exercise) {
    if (!exercise) return null;
    const series = Array.isArray(exercise?.series) ? exercise.series : [];
    const attributes = Array.isArray(exercise?.attributes)
      ? exercise.attributes
      : [];

    return {
      name: typeof exercise?.name === "string" ? exercise.name : "",
      originalName:
        typeof exercise?.originalName === "string" ? exercise.originalName : "",
      notes: typeof exercise?.notes === "string" ? exercise.notes : "",
      link: typeof exercise?.link === "string" ? exercise.link : "",
      protocolSlot:
        typeof exercise?.protocolSlot === "string"
          ? exercise.protocolSlot
          : null,
      protocolText:
        typeof exercise?.protocolText === "string" ? exercise.protocolText : "",
      isSecondaryProtocol: !!exercise?.isSecondaryProtocol,
      series: series
        .slice()
        .filter(Boolean)
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        .map((s) => ({
          order: s?.order ?? 0,
          targetReps: s?.targetReps ?? "",
          targetRpe: s?.targetRpe ?? "",
          targetWeight: s?.targetWeight ?? "",
          relevance: s?.relevance,
          requireVideo: s?.requireVideo,
        })),
      attributes: attributes
        .slice()
        .filter(Boolean)
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        .map((a) => ({
          name: typeof a?.name === "string" ? a.name : "",
          title: typeof a?.title === "string" ? a.title : "",
          subtitle: typeof a?.subtitle === "string" ? a.subtitle : "",
          order: a?.order ?? 0,
        })),
    };
  }

  copyExercisesFromExercise(exercise) {
    const payload = this.serializeExerciseForClipboard(exercise);
    if (!payload) return;
    this.copiedExercises = [payload];
  }

  copyExercisesFromWorkout(workout) {
    const exercises = Array.isArray(workout?.exercises)
      ? workout.exercises
      : [];
    const payloads = exercises
      .slice()
      .filter(Boolean)
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((ex) => this.serializeExerciseForClipboard(ex))
      .filter(Boolean);
    if (payloads.length === 0) return;
    this.copiedExercises = payloads;
  }

  copySetsFromExercise(exercise) {
    if (!exercise) return;
    const sortedSeries = (
      Array.isArray(exercise?.series) ? exercise.series.slice() : []
    )
      .filter(Boolean)
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    this.copiedSets = {
      protocolText:
        typeof exercise?.protocolText === "string" ? exercise.protocolText : "",
      sets: sortedSeries.map((s) => ({
        targetWeight: s?.targetWeight ?? "",
        targetReps: s?.targetReps ?? "",
        targetRpe: s?.targetRpe ?? "",
        relevance: s?.relevance,
        requireVideo: s?.requireVideo,
      })),
    };
  }

  get hasCopiedExercises() {
    return (
      Array.isArray(this.copiedExercises) && this.copiedExercises.length > 0
    );
  }

  get hasCopiedSets() {
    return !!this.copiedSets;
  }
  // #endregion

  // #region /* HELPERS */
  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
      console.log(`Property ${propertyName} set to ${value}`);
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  updateCoachTransactionWorkouts(id) {
    if (!this.coachTransactionWorkouts.includes(id)) {
      this.coachTransactionWorkouts.push(id);
    }
  }

  updateCoachDeletionWorkouts(id) {
    if (!this.coachDeletionWorkouts.includes(id)) {
      this.coachDeletionWorkouts.push(id);
    }
  }

  updateCoachTransactionBlocks(id) {
    if (!this.coachTransactionBlocks.includes(id)) {
      this.coachTransactionBlocks.push(id);
    }
  }
  updateCoachDeletionBlocks(id) {
    if (!this.coachDeletionBlocks.includes(id)) {
      this.coachDeletionBlocks.push(id);
    }
  }

  updateCoachTransactionCompetitions(id) {
    if (!this.coachTransactionCompetitions.includes(id)) {
      this.coachTransactionCompetitions.push(id);
    }
  }
  updateCoachDeletionCompetitions(id) {
    if (!this.coachDeletionCompetitions.includes(id)) {
      this.coachDeletionCompetitions.push(id);
    }
  }

  updateCoachTransactionForms(id) {
    if (!this.coachTransactionForms.includes(id)) {
      this.coachTransactionForms.push(id);
    }
  }
  updateCoachDeletionForms(id) {
    if (!this.coachDeletionForms.includes(id)) {
      this.coachDeletionForms.push(id);
    }
  }
  updateCoachTransactionExercises(id) {
    if (!this.coachTransactionExercises.includes(id)) {
      this.coachTransactionExercises.push(id);
    }
  }
  updateCoachDeletionExercises(id) {
    if (!this.coachDeletionExercises.includes(id)) {
      this.coachDeletionExercises.push(id);
    }
  }

  updateCoachTransactionAttributes(id) {
    if (!this.coachTransactionAttributes.includes(id)) {
      this.coachTransactionAttributes.push(id);
    }
  }
  updateCoachDeletionAttributes(id) {
    if (!this.coachDeletionAttributes.includes(id)) {
      this.coachDeletionAttributes.push(id);
    }
  }
  // #endregion

  // #region /* FIRESTORE ANALYTICS */
  /* EVENT LOGGING */
  // async logEvent(eventName, additionalParams = {}) {
  //   try {
  //     const commonParams = {
  //       [ANALYTICS_PARAMS.USER_ID]: this.id,
  //       [ANALYTICS_PARAMS.SUBSCRIPTION_STATUS]: this.subscriptionStatus,
  //       [ANALYTICS_PARAMS.DEVICE_TYPE]:
  //         Platform.OS === "ios" || Platform.OS === "android"
  //           ? Platform.OS
  //           : "web",
  //     };
  //     const eventParams = { ...commonParams, ...additionalParams };
  //     firebaseLogEvent(analytics, eventName, eventParams);
  //     // console.log(`${eventName} event logged with params:`, eventParams);
  //   } catch (error) {
  //     console.error(`Failed to log event ${eventName}:`, error);
  //   }
  // }

  // logSessionStart() {
  //   this.logEvent(ANALYTICS_EVENTS.SESSION_START, {
  //     [ANALYTICS_PARAMS.DATE]: new Date().toISOString(),
  //   });
  //   // console.log("session_start event logged");
  // }
  // logNewUser() {
  //   if (!this.trialPeriodStartDate) {
  //     return;
  //   }
  //   const trialPeriodEndDate = new Date(this.trialPeriodStartDate);
  //   trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);
  //   this.logEvent(ANALYTICS_EVENTS.NEW_USER, {
  //     [ANALYTICS_PARAMS.TRIAL_START]: this.trialPeriodStartDate.toISOString(),
  //     [ANALYTICS_PARAMS.TRIAL_END]: trialPeriodEndDate.toISOString(),
  //   });
  //   // console.log("new_user event logged");
  // }
  // logPageView(pageTitle) {
  //   this.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
  //     [ANALYTICS_PARAMS.PAGE_TITLE]: pageTitle,
  //   });
  //   // console.log("page_view event logged");
  // }

  // logFirstFirestoreWrite() {
  //   this.logEvent(ANALYTICS_EVENTS.FIRST_FIRESTORE_WRITE, {
  //     [ANALYTICS_PARAMS.DATE]: new Date().toISOString(),
  //   });
  //   // console.log("first_firestore_write event logged");
  // }
  // logFeatureUsage(feature) {
  //   this.logEvent(ANALYTICS_EVENTS.FEATURE_USAGE, {
  //     [ANALYTICS_PARAMS.FEATURE]: feature,
  //   });
  //   // console.log("feature_usage event logged");
  // }
  // logStripeActions(action) {
  //   this.logEvent(ANALYTICS_EVENTS.STRIPE_ACTIONS, {
  //     [ANALYTICS_PARAMS.ACTION]: action,
  //   });
  //   // console.log("stripe_actions event logged");
  // }

  // /* ATTRIBUTES LOGGING */
  // async setUserProperty(name, value) {
  //   try {
  //     const userProperties = {
  //       [name]: value,
  //     };
  //     setUserProperties(analytics, userProperties);
  //     console.log(`Set user property ${name} to ${value}`);
  //   } catch (error) {
  //     console.error(`Failed to set user property ${name}:`, error);
  //   }
  // }
  // async setUserProperties(properties) {
  //   try {
  //     setUserProperties(analytics, properties);
  //     // console.log("Set user properties:", properties);
  //   } catch (error) {
  //     console.error("Failed to set user properties:", error);
  //   }
  // }

  // setUserId() {
  //   this.setUserProperty("user_id", this.id.toString());
  // }

  // setUserSubscriptionStatus() {
  //   this.setUserProperty("subscription_status", this.subscriptionStatus);
  // }

  // setUserTrialStart() {
  //   if (!this.trialPeriodStartDate) {
  //     return;
  //   }
  //   this.setUserProperty(
  //     "trial_start",
  //     this.trialPeriodStartDate.toISOString()
  //   );
  // }

  // setUserTrialEnd() {
  //   if (!this.trialPeriodStartDate) {
  //     return;
  //   }
  //   const trialPeriodEndDate = new Date(this.trialPeriodStartDate);
  //   trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);
  //   this.setUserProperty("trial_end", trialPeriodEndDate.toISOString());
  // }

  // setUserLastBackup() {
  //   this.setUserProperty("last_backup", this.lastBackup);
  // }

  // setUserFirstFirestoreWrite(firstFirestoreWrite) {
  //   this.setUserProperty("first_firestore_write", firstFirestoreWrite);
  // }

  // setAllUserProperties() {
  //   if (this.id) {
  //     const trialPeriodEndDate = new Date(this.trialPeriodStartDate);
  //     trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);

  //     const numClients = Array.isArray(this.clients)
  //       ? this.clients.filter(
  //           (c) => c.email && !c.email.includes("efortcoach.com")
  //         ).length
  //       : 0;

  //     const properties = {
  //       [ANALYTICS_PARAMS.USER_ID]: this.id.toString(),
  //       [ANALYTICS_PARAMS.SUBSCRIPTION_STATUS]: this.subscriptionStatus,
  //       [ANALYTICS_PARAMS.NUM_CLIENTS]: numClients,
  //       ...(this.trialPeriodStartDate && {
  //         [ANALYTICS_PARAMS.TRIAL_START]:
  //           this.trialPeriodStartDate.toISOString(),
  //       }),
  //       ...(trialPeriodEndDate && {
  //         [ANALYTICS_PARAMS.TRIAL_END]: trialPeriodEndDate.toISOString(),
  //       }),
  //       ...(this.lastBackup && {
  //         [ANALYTICS_PARAMS.LAST_BACKUP]: this.lastBackup.toISOString(),
  //       }),
  //     };

  //     this.setUserProperties(properties);
  //   }
  // }
  // #endregion

  // #region /* DEMO CLIENTS */

  async initializeCoach(
    id,
    name,
    email,
    logoUri,
    preferences,
    language,
    selectedFeatures,
    athletesRange,
    athleteTypes,
    completeOnboarding,
    screenSize,
    acquisitionRef,
    fbclid,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
    cookiesAccepted,
    completeRegistrationEventId
  ) {
    /* SET KEY PROPERTIES */
    runInAction(() => {
      this.id = id;
      this.name = name ?? "Coach";
      this.email = email;
      this.trialPeriodStartDate = new Date();
      this.initialLoading = true;
      this.hideBlockDashboard = true;
      this.showingProtocolsGuide = true;
    });

    /* UPLOAD THE ONBOARDING DATA AND CONFIGURATIONS TO COACHES_PUBLIC */
    const onboardingData = {
      id: id,
      name: name,
      email: email,
      trial_period_start_date: this.trialPeriodStartDate,
      onboarding_athletes_count: athletesRange,
      onboarding_athletes_types: athleteTypes,
      onboarding_use_cases: selectedFeatures,
      onboarding_logo_upload: !!logoUri,
      onboarding_show_athlete_app: true,
      onboarding_show_block: true,
      onboarding_show_invite_client: true,
      language: language || "en",
      default_block_view: "workouts-string",
      hide_block_dashboard: true,
      showing_protocols_guide: true,
      ...preferences,
    };

    // Add acquisition_ref if it exists
    if (acquisitionRef) {
      onboardingData.acquisition_ref = acquisitionRef;
      console.log("[Coach] Saving acquisition_ref:", acquisitionRef);
    }

    // Add fbclid if it exists
    if (fbclid) {
      onboardingData.fbclid = fbclid;
      console.log("[Coach] Saving fbclid:", fbclid);
    }

    // Add UTM parameters if they exist
    if (utmSource) {
      onboardingData.utm_source = utmSource;
      console.log("[Coach] Saving utm_source:", utmSource);
    }

    if (utmMedium) {
      onboardingData.utm_medium = utmMedium;
      console.log("[Coach] Saving utm_medium:", utmMedium);
    }

    if (utmCampaign) {
      onboardingData.utm_campaign = utmCampaign;
      console.log("[Coach] Saving utm_campaign:", utmCampaign);
    }

    if (utmContent) {
      onboardingData.utm_content = utmContent;
      console.log("[Coach] Saving utm_content:", utmContent);
    }

    if (utmAdset) {
      onboardingData.utm_adset = utmAdset;
      console.log("[Coach] Saving utm_adset:", utmAdset);
    }

    if (utmTerm) {
      onboardingData.utm_term = utmTerm;
      console.log("[Coach] Saving utm_term:", utmTerm);
    }

    // Add cookies_accepted if cookies were accepted
    if (cookiesAccepted) {
      onboardingData.cookies_accepted = true;
      console.log("[Coach] Saving cookies_accepted: true");
    }

    if (fbclid && completeRegistrationEventId) {
      onboardingData.meta_event_id_complete_registration =
        completeRegistrationEventId;
      console.log(
        "[Coach] Saving meta_event_id_complete_registration:",
        completeRegistrationEventId
      );
    }

    await this.updateCoachPublicProfile(onboardingData);

    /* UPLOAD LOGO TO STORAGE */
    if (logoUri) {
      try {
        console.log("[OnboardingView] Uploading logo to storage...");
        const storageRef = ref(storage, `coach_logos/${id}`);

        // Fetch the blob from the URI
        const response = await fetch(logoUri);
        const blob = await response.blob();

        // Upload to Firebase Storage
        await uploadBytes(storageRef, blob);

        // Get the download URL
        const downloadURL = await getDownloadURL(storageRef);
        console.log(
          "[OnboardingView] Logo uploaded successfully:",
          downloadURL
        );
        this.logoUrl = downloadURL;
      } catch (error) {
        console.error("[OnboardingView] Error uploading logo:", error);
        // Continue with onboarding even if logo upload fails
      }
    }

    /* SET COACH PROPERTIES AND UPLOAD */
    runInAction(() => {
      //preferences
      this.exerciseUnit = preferences.exercise_unit;
      this.rirOrRpe = preferences.rir_or_rpe;
      this.showAthleteExerciseStats = preferences.show_athlete_exercise_stats;
      this.showSetPriority = preferences.show_set_priority;
      this.coachLanguage = language;

      //onboarding
      this.onboardingShowTutorialApp = true;
      this.onboardingShowTutorialBlock = true;
      this.onboardingShowInviteClient = true;

      // Create sidebar configuration based on selectedFeatures. SelectedFeatures array can contain: "programs", "videos", "billing", "applications", "forms", "competitions"
      this.sidebarConfig = [
        { id: "CoachLibrary", visible: true, order: 0 },
        {
          id: "CoachBilling",
          visible: selectedFeatures?.includes("billing") || false,
          order: 1,
        },
        {
          id: "CoachCompetitions",
          visible: selectedFeatures?.includes("competitions") || false,
          order: 2,
        },
        {
          id: "CoachApplications",
          visible: selectedFeatures?.includes("applications") || false,
          order: 3,
        },
      ];

      this.subscriptionStatus = "trialing";
      this.maxClients = 100;
      if (screenSize != "small") {
        this.showingTutorialPanel = true;
      }
      // this.coachSettings = {
      //   name: this.name ? this.name : "",
      //   surname: this.surname ? this.surname : "",
      //   exercise_unit: this.exerciseUnit !== null ? this.exerciseUnit : false,
      //   rir_or_rpe: this.rirOrRpe !== null ? this.rirOrRpe : false,
      //   coach_language: this.coachLanguage ? this.coachLanguage : "en",

      //   //new settings default values
      //   show_athlete_exercise_stats: this.showAthleteExerciseStats
      //     ? this.showAthleteExerciseStats
      //     : false,
      //   show_set_priority: this.showSetPriority ? this.showSetPriority : true,
      // };
    });
    this.customPredefinedAttributes = this.getPredefinedAttributes();

    const predefinedAttributes = this.customPredefinedAttributes.map(
      (attribute, index) => ({
        ...attribute.uploadToCloud(),
        order: index,
      })
    );

    this.customPredefinedExercises = this.getPredefinedArray();

    const predefinedExercises = this.customPredefinedExercises.map(
      (predefinedExercise, index) => ({
        ...predefinedExercise.uploadToCloud(),
        order: index,
      })
    );

    const coachDocRef = doc(db, "coaches", this.id);
    await setDoc(
      coachDocRef,
      this.uploadToCloud(predefinedAttributes, predefinedExercises),
      { merge: true }
    );

    /* MARK ONBOARDING AS COMPLETED TO REDIRECT TO INDEX */
    completeOnboarding();

    /* DEMO CLIENTS CREATION */
    await this.createDemoClient();
    await this.createCoachDemoClient();
  }

  async createDemoClient() {
    console.log(
      "CREATING DEMO CLIENT, this.coachlanguage: ",
      this.coachLanguage
    );

    const exercisePlan = [
      {
        workouts: [
          [
            {
              name: "400 Tempo Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
                {
                  name: "400Tempo-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
              protocolText: [
                "Ramp 3x3 @6 + x3 @4",
                "Ramp 3x3 @6.5 + x3 @4.5",
                "Ramp 3x3 @7 + x3 @5",
                "Ramp 3x3 @7 + x3 @5.5",
                "Ramp 3x3 @7.5 + x3 @5.5",
                "Ramp 3x3 @7.5 + x3 @6",
                "Ramp 3x3 @8 + x3 @6",
              ],
              series: [
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5, 5.5, 5.5, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 5.5, 6, 6, 6.5, 6.5, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [180, 185, 190, 195, 200, 205, 210],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [6, 6.5, 7, 7, 7.5, 7.5, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [190, 195, 200, 205, 210, 215, 220],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
              ],
            },
            {
              name: "2ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
              protocolText: [
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @6 + x4 @6.5 + 3x4 @5",
                "x1 @6.5 + x4 @7 + 3x4 @6",
                "x1 @6.5 + x4 @7.5 + 3x4 @6",
                "x1 @7 + x4 @8 + 3x4 @6",
                "x1 @8 + x4 @8.5 + 3x4 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5.5, 5.5, 6, 6.5, 6.5, 7, 8],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [152.5, 155, 157.5, 160, 162.5, 162.5, 167.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6.5, 6.5, 6.5, 7, 7.5, 8, 8.5],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [145, 147.5, 150, 152.5, 152.5, 155, 157.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
              ],
            },
            {
              name: "Romanian Deadlift",
              originalName: "romanianDeadlift-string",
              series: [
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [4, 4, 4, 4, 4, 4, 4],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [185, 185, 185, 190, 190, 190, 195],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [5, 5, 5, 5, 5, 5, 5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [195, 195, 195, 200, 200, 200, 205],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [6, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [205, 205, 205, 210, 210, 210, 215],
                },
              ],
            },
            {
              name: "Machine Press",
              originalName: "machinePress-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
              ],
            },
          ],

          [
            {
              name: "1ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "1ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
              protocolText: [
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5, 5, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
              ],
            },
            {
              name: "Sumo Deadlift",
              originalName: "sumoDeadlift-string",
              protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
              protocolText: [
                "Ramp 3x5 @6",
                "Ramp 3x5 @6.5",
                "Ramp 3x5 @7",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @8",
                "Ramp 3x5 @8",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [210, 215, 220, 225, 230, 235, 240],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [220, 225, 230, 235, 240, 245, 250],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [230, 235, 240, 245, 250, 255, 260],
                },
              ],
            },
            {
              name: "Dumbbell Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "Dumbbell-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                ,
              ],
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
              ],
            },
            {
              name: "Leg Curls",
              originalName: "legCurls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [0, 0, 0, 0, 0, 0, 0],
                },
              ],
            },
            {
              name: "Pull Down",
              originalName: "pullDown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
              ],
            },
            {
              name: "Cable Row",
              originalName: "cableRow-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
              ],
            },
            {
              name: "Tricep Pushdown",
              originalName: "tricepPushdown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
              ],
            },
          ],

          [
            {
              name: "Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
              protocolText: [
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 192.5, 195, 197.5, 200, 202.5, 202.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6, 6, 6, 7, 7, 7],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [195, 197.5, 200, 200, 202.5, 205, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
              ],
            },
            {
              name: "3ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "3ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.QUATERNARY_BENCH_PRESS,
              protocolText: [
                "3x1 @5",
                "3x1 @5.5",
                "3x1 @6",
                "3x1 @6.5",
                "3x1 @6.5",
                "3x1 @7",
                "3x1 @7",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
              ],
            },
            ,
            {
              name: "Bulgarian Split Squat",
              originalName: "bulgarianSplitSquat-string",
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
              ],
            },
            {
              name: "Leg Extension",
              originalName: "legExtension-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
              ],
            },
          ],

          [
            {
              name: "2ct Pause Spoto Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
                {
                  name: "Spoto-string",
                  title: "rom-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
              protocolText: [
                "2x1 @6 + 3x3 @5",
                "2x1 @7 + 3x3 @6",
                "2x1 @7 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
              ],
            },
            {
              name: "Conventional Deadlift",
              originalName: "conventionalDeadlift-string",
              protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
              protocolText: [
                "Ramp 3x4 @6",
                "Ramp 3x4 @6.5",
                "Ramp 3x4 @7",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @8",
                "Ramp 3x4 @8",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 185, 187.5, 190, 195, 200, 200],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 195, 197.5, 200, 205, 210, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [200, 205, 207.5, 210, 215, 220, 220],
                },
              ],
            },
            {
              name: "Cable Single Arm Pull Down",
              originalName: "pullDown-string",
              attributes: [
                {
                  name: "Cable-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                {
                  name: "SingleArm-string",
                  title: "other-string",
                  subtitle: "",
                },
              ],
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
              ],
            },
            {
              name: "Row",
              originalName: "row-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
              ],
            },
            {
              name: "Tricep Extensions",
              originalName: "tricepExtensions-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
              ],
            },
          ],
        ],
      },
      // Repeat this structure for weeks 2 to 7
    ];

    const randNum = Math.floor(1000 + Math.random() * 9000);

    const newClientId = String(uuidv4());

    const newBlockId = String(uuidv4());

    const newClient = new Client(
      newClientId,
      this.coachLanguage === "es"
        ? `juan${randNum}@efortcoach.com`
        : `james${randNum}@efortcoach.com`,
      this.coachLanguage === "es" ? "Juan" : "James",
      this.coachLanguage === "es" ? "García" : "Anderson",
      this.coachLanguage === "es" ? "Juan" : "James",
      this.coachLanguage === "es" ? "García" : "Anderson",
      false,
      false,
      Date(),
      false,
      this.coachLanguage,
      undefined,
      false,
      undefined,
      undefined,
      this,
      [true, false, true, true, false, true, false],
      true,
      "",
      "",
      undefined,
      undefined,
      undefined,
      undefined,
      "Develop Block"
    );

    const newBlock = new Block(
      newBlockId,
      "Develop Block",
      "", // notes
      [], // workouts
      true,
      false,
      newClient,
      6, // totalWeeks
      6,
      "Develop",
      new Date()
    );

    // Helper function to get a random date between two dates
    const getRandomDate = (startDate, endDate) => {
      const date = new Date(
        startDate.getTime() +
          Math.random() * (endDate.getTime() - startDate.getTime())
      );
      return new Date(date.setHours(0, 0, 0, 0)); // Ensure the time is set to midnight
    };

    // Function to generate unique random dates
    const generateUniqueRandomDates = (startDate, endDate, numDates) => {
      const dates = new Set();
      while (dates.size < numDates) {
        dates.add(
          getRandomDate(startDate, endDate).toISOString().split("T")[0]
        );
      }
      return Array.from(dates).map((date) => new Date(date));
    };

    // Function to generate bodyweights for workout dates
    const generateRandomBodyweights = (
      workoutDates,
      startWeight,
      endWeight
    ) => {
      const bodyweights = [];
      const sortedDates = [...workoutDates].sort((a, b) => a - b);

      let currentWeight = startWeight;
      const totalWeightChange = endWeight - startWeight;
      const averageDailyChange = totalWeightChange / sortedDates.length;

      sortedDates.forEach((date) => {
        const randomFluctuation = Math.random() * 0.2 - 0.1; // Random change between -0.1 and +0.1
        const dailyChange = averageDailyChange + randomFluctuation;
        currentWeight += dailyChange;
        bodyweights.push(
          new Bodyweight(undefined, currentWeight, date, newClient)
        );
      });

      return bodyweights;
    };

    // Function to generate Trac entries for workout dates
    const generateRandomTracs = (workoutDates, client) => {
      const tracs = [];
      const sortedDates = [...workoutDates].sort((a, b) => a - b);

      sortedDates.forEach((date) => {
        const legSoreness = Math.floor(Math.random() * 3) + 3;
        const pushSoreness = Math.floor(Math.random() * 3) + 3;
        const pullSoreness = Math.floor(Math.random() * 2) + 2;
        const motivation = Math.floor(Math.random() * 5) + 1;
        const perceivedRecovery = Math.floor(Math.random() * 3) + 2;
        const sleepQualityNutrition = Math.floor(Math.random() * 2) + 4;
        const technicalComfort = Math.floor(Math.random() * 3) + 3;

        tracs.push(
          new Trac(
            undefined,
            date,
            legSoreness,
            pushSoreness,
            pullSoreness,
            motivation,
            perceivedRecovery,
            sleepQualityNutrition,
            technicalComfort,
            client
          )
        );
      });

      return tracs;
    };

    // Helper function to get the date of a specific weekday `weeksAgo` weeks ago
    const getDateOfSpecificWeekday = (weeksAgo, weekday) => {
      const today = new Date(); // Get today's date
      const currentDay = today.getDay(); // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
      const startOfWeek = new Date(today); // Create a new date object for the start of the week

      // Calculate the start of the week (Monday)
      startOfWeek.setDate(today.getDate() - currentDay + 1);
      if (currentDay === 0) {
        startOfWeek.setDate(startOfWeek.getDate() - 7); // Adjust if today is Sunday
      }

      // Calculate the date `weeksAgo` weeks ago
      startOfWeek.setDate(startOfWeek.getDate() - weeksAgo * 7);

      // Calculate the target date
      const targetDate = new Date(startOfWeek);
      targetDate.setDate(startOfWeek.getDate() + (weekday - 1));

      return targetDate;
    };

    // Weekdays for Monday, Wednesday, Thursday, and Saturday
    const weekdays = [1, 3, 4, 6]; // 1 = Monday, 3 = Wednesday, 4 = Thursday, 6 = Saturday

    // Generate the workout dates
    let firstWorkoutDate = null;
    let lastWorkoutDate = null;
    const workoutDates = [];

    // Create the workouts for the 7-week training block
    runInAction(() => {
      for (let week = 5; week >= 0; week--) {
        for (let day = 0; day < 4; day++) {
          const startDate = getDateOfSpecificWeekday(5 - week, weekdays[day]);

          workoutDates.push(startDate);

          if (!firstWorkoutDate || startDate < firstWorkoutDate) {
            firstWorkoutDate = startDate;
          }
          if (!lastWorkoutDate || startDate > lastWorkoutDate) {
            lastWorkoutDate = startDate;
          }

          const newWorkout = new Workout(
            undefined,
            startDate,
            this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
            day + 1, // order
            week + 1, // week
            false,
            newBlock,
            []
          );

          exercisePlan[0].workouts[day].forEach((exerciseInfo, index) => {
            const {
              originalName,
              name,
              attributes,
              series,
              protocolSlot,
              protocolText,
            } = exerciseInfo;
            const newExercise = new Exercise(
              undefined,
              name,
              originalName,
              "", // notes
              index, // order
              undefined,
              index === 0,
              newWorkout,
              [],
              []
            );
            if (protocolSlot) {
              newExercise.protocolSlot = protocolSlot;
            }
            if (protocolText?.length > 0) {
              newExercise.protocolText = protocolText[week];
            }

            if (series) {
              // Add sets/series
              for (let setIndex = 0; setIndex < series.length; setIndex++) {
                const serieInfo = series[setIndex];
                if (serieInfo) {
                  const newSerie = new Serie(
                    undefined,
                    setIndex + 1, // order
                    serieInfo.targetReps[week],
                    serieInfo.targetRPE[week], // Specific targetRPE for this week
                    serieInfo.weight[week], // Specific weight for this week
                    serieInfo.targetReps[week],
                    serieInfo.targetRPE[week], // target_rpe
                    0, // target_weight
                    newExercise,
                    serieInfo.relevance[week],
                    undefined,
                    serieInfo.relevance[week] === 1
                  );
                  newExercise.series.push(newSerie);
                }
              }
            }
            // Add attributes if present
            if (attributes) {
              attributes.forEach((attribute, attrIndex) => {
                const newAttribute = new Attribute(
                  String(uuidv4()),
                  attribute.name,
                  attribute.title,
                  attribute.subtitle,
                  attrIndex, // order
                  false, // isPredefined
                  false,
                  null, // date
                  newExercise
                );
                newExercise.attributes.push(newAttribute);
              });
            }

            newWorkout.exercises.push(newExercise);
          });

          newBlock.workouts.push(newWorkout);
        }
      }
    });

    // Generate bodyweights and tracs for each workout date
    const bodyweights = generateRandomBodyweights(workoutDates, 74.8, 74);
    const tracs = generateRandomTracs(workoutDates, newClient);
    newClient.bodyweights.push(...bodyweights);
    newClient.tracs.push(...tracs);

    newClient.blocks.push(newBlock);
    newClient.setProperty("isFetched", true);
    newClient.setProperty("metricsSelectedBlocks", [newBlock]);
    //canviar selected aqui
    newClient.setProperty(
      "exercisesMetricsSelected",
      this.coachLanguage === "es"
        ? ["Sentadilla Tempo 400 Barra Baja"]
        : ["400 Tempo Low Bar Squat"]
    );
    //canviar pinneds aqui
    newClient.setProperty(
      "pinnedExercises",
      this.coachLanguage === "es"
        ? [
            "Sentadilla Barra Baja",
            "Sentadilla Tempo 400 Barra Baja",
            "Press Banca Pausa 2ct",
            "Peso Muerto Sumo",
            "Peso Muerto Convencional",
          ]
        : [
            "Low Bar Squat",
            "400 Tempo Low Bar Squat",
            "2ct Pause Bench Press",
            "Sumo Deadlift",
            "Conventional Deadlift",
          ]
    );
    newClient.setProperty("startWeek", 1);
    newClient.setProperty("endWeek", 6);

    const competitionId = String(uuidv4());

    const coachCompetition = new Competition(
      competitionId,
      "IPF Worlds 2026", // name
      new Date("2026-06-13T12:00:00+02:00"), // date
      this,
      null,
      "",
      "Open", // competitionClass
      "IPF",
      "United Kingdom",
      "",
      "",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );
    coachCompetition.setProperty("isSelected", true);
    // newComp.setProperty("notNew", false);
    this.competitions.push(coachCompetition);

    const clientCompetition = new Competition(
      competitionId, // id
      "IPF Worlds 2026", // name
      new Date("2026-06-13T12:00:00+02:00"), // date
      null, // coach
      newClient, // client
      "-74 kg", // weightClass
      "Open", // competitionClass
      "IPF", // competitionLevel
      "", // localization
      "", // weightIn
      "", // hour
      ["Total 690kg", "9/9", ""], // goals
      "", // notes
      ["245", "170", "285"], // topEnd
      "", // minimumMark
      [
        { good: "225", medium: "220", bad: "215" },
        { good: "237.5", medium: "235", bad: "230" },
        { good: "245", medium: "242.5", bad: "237.5" },
      ], // squatAttempts
      ["70", "120", "160", "200", "210", "", "", ""], // squatWarmups
      [
        { good: "155", medium: "150", bad: "147.5" },
        { good: "160", medium: "155", bad: "152.5" },
        { good: "170", medium: "165", bad: "160" },
      ], // benchAttempts
      ["70", "90", "110", "130", "145", "", "", ""], // benchWarmups
      [
        { good: "260", medium: "255", bad: "250" },
        { good: "275", medium: "270", bad: "265" },
        { good: "285", medium: "280", bad: "275" },
      ], // deadliftAttempts
      ["70", "120", "170", "210", "240", "", "", ""], // deadliftWarmups
      [
        { result: "", isNull: false },
        { result: "", isNull: false },
        { result: "", isNull: false },
      ], // squatResults
      [
        { result: "", isNull: false },
        { result: "", isNull: false },
        { result: "", isNull: false },
      ], // benchPressResults
      [
        { result: "", isNull: false },
        { result: "", isNull: false },
        { result: "", isNull: false },
      ], // deadliftResults
      "" // recapNotes
    );
    newClient.competitions.push(clientCompetition);

    const workoutLibrarySample = [
      {
        workoutName:
          this.coachLanguage === "en"
            ? "Pull Accessory Exercises"
            : "Accesorios Pull",
        exercises: [
          {
            name: "Pull Down",
            originalName: "pullDown-string",
            attributes: [],
            series: [
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "Cable Row",
            originalName: "cableRow-string",
            attributes: [],
            series: [
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "Cable Single Arm Pull Down",
            originalName: "pullDown-string",
            attributes: [
              {
                name: "Cable-string",
                title: "equipment-string",
                subtitle: "",
              },
              {
                name: "SingleArm-string",
                title: "other-string",
                subtitle: "",
              },
            ],
            series: [
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
            ],
          },
        ],
      },
      {
        workoutName:
          this.coachLanguage === "en"
            ? "Push Accessory Exercises"
            : "Accesorios Push",
        exercises: [
          {
            name: "machinePress-string",
            originalName: "machinePress-string",
            attributes: [],
            series: [
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "flies-string",
            originalName: "flies-string",
            attributes: [],
            series: [
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 12,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "military-string",
            originalName: "military-string",
            attributes: [
              {
                name: "Dumbbell-string",
                title: "equipment-string",
                subtitle: "",
              },
            ],
            series: [
              {
                targetReps: 6,
                targetRPE: 8,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 8,
                targetRPE: 7,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 8,
                targetRPE: 7,
                relevance: 3,
                weight: 0,
              },
            ],
          },
        ],
      },
      {
        workoutName: this.coachLanguage === "en" ? "Arms" : "Brazo",
        exercises: [
          {
            name: "curls-string",
            originalName: "curls-string",
            attributes: [
              {
                name: "Dumbbell-string",
                title: "equipment-string",
                subtitle: "",
              },
            ],
            series: [
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "curls-string",
            originalName: "curls-string",
            attributes: [
              {
                name: "Cable-string",
                title: "equipment-string",
                subtitle: "",
              },
            ],
            series: [
              {
                targetReps: 10,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 10,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
            ],
          },
          {
            name: "tricepPushdown-string",
            originalName: "tricepPushdown-string",
            attributes: [
              {
                name: "Cable-string",
                title: "equipment-string",
                subtitle: "",
              },
              {
                name: "SingleArm-string",
                title: "other-string",
                subtitle: "",
              },
            ],
            series: [
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
              {
                targetReps: 12,
                targetRPE: 9,
                relevance: 3,
                weight: 0,
              },
            ],
          },
        ],
      },
    ];

    workoutLibrarySample.forEach((workoutInfo, workoutIndex) => {
      const newWorkout = new Workout(
        undefined,
        null,
        workoutInfo.workoutName,
        workoutIndex,
        0,
        false,
        null,
        [],
        this
      );

      workoutInfo.exercises.forEach((exerciseInfo, exerciseIndex) => {
        const { originalName, name, attributes, series } = exerciseInfo;
        const newExercise = new Exercise(
          undefined,
          name,
          originalName,
          "", // notes
          exerciseIndex, // order
          undefined,
          exerciseIndex === 0,
          newWorkout,
          [],
          []
        );

        if (series) {
          for (let setIndex = 0; setIndex < series.length; setIndex++) {
            const serieInfo = series[setIndex];
            if (serieInfo) {
              const newSerie = new Serie(
                undefined,
                setIndex + 1, // order
                0,
                0, // Specific targetRPE for this week
                serieInfo.weight, // Specific weight for this week
                serieInfo.targetReps,
                serieInfo.targetRPE, // target_rpe
                0, // target_weight
                newExercise,
                serieInfo.relevance
              );
              newExercise.series.push(newSerie);
            }
          }
        }

        // Add attributes if present
        if (attributes) {
          attributes.forEach((attribute, attrIndex) => {
            const newAttribute = new Attribute(
              undefined,
              attribute.name,
              attribute.title,
              attribute.subtitle,
              attrIndex, // order
              false, // isPredefined
              false,
              null, // date
              newExercise
            );
            newExercise.attributes.push(newAttribute);
          });
        }
        newWorkout.exercises.push(newExercise);
      });

      // Add the new workout to the appropriate collection or list
      this.workoutLibrary.push(newWorkout);
    });

    this.clients.push(newClient);

    const coachCompDocRef = doc(
      db,
      "coaches",
      this.id,
      "competitions",
      "competitions"
    );
    const coachLibDocRef = doc(db, "coaches", this.id, "library", "library");
    const clientCompDocRef = doc(
      db,
      "users",
      newClientId,
      "competitions",
      competitionId
    );
    const clientArraysDocRef = doc(
      db,
      "users",
      newClientId,
      "arrays",
      "arrays"
    );
    const clientBlocksDocRef = doc(
      db,
      "users",
      newClientId,
      "blocks",
      newBlockId
    );
    const clientDocRef = doc(db, "users", newClientId);

    await setDoc(clientDocRef, newClient.uploadToCloud());

    const formattedTracs = newClient.tracs.map((trac) => trac.uploadToCloud());
    const formattedBodyweights = newClient.bodyweights.map((bodyweight) =>
      bodyweight.uploadToCloud()
    );

    await setDoc(clientArraysDocRef, {
      attributes: [],
      predefined_exercises: [],
      tracs: formattedTracs,
      bodyweights: formattedBodyweights,
      notes: [],
    });
    await setDoc(clientBlocksDocRef, newBlock.uploadToCloud(new Date()));
    await setDoc(coachCompDocRef, {
      competitions: [coachCompetition.uploadToCloud()],
    });
    await setDoc(clientCompDocRef, clientCompetition.uploadToCloud());
    await setDoc(coachLibDocRef, {
      workout_library: this.workoutLibrary.map((w) => w.uploadToCloud()),
    });
  }

  async createCoachDemoClient() {
    console.log(
      "CREATING COACH DEMO CLIENT. this.coachlanguage: ",
      this.coachLanguage
    );

    const clientDocRef = doc(db, "users", this.id);

    try {
      const clientDoc = await getDoc(clientDocRef);

      if (clientDoc.exists()) {
        console.log("Document already exists. Aborting creation.");
        return; // Exit the function if the document already exists
      }

      console.log("Coach demo client created successfully.");
    } catch (error) {
      console.error("Error creating coach demo client:", error.message);
      return; // Exit the function if an error occurs
    }

    const exercisePlan = [
      {
        workouts: [
          [
            {
              name: "400 Tempo Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
                {
                  name: "400Tempo-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],

              protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
              protocolText: [
                "Ramp 3x3 @6",
                "Ramp 3x3 @6.5",
                "Ramp 3x3 @7",
                "Ramp 3x3 @7",
                "Ramp 3x3 @7.5",
                "Ramp 3x3 @7.5",
                "Ramp 3x3 @8",
              ],
              series: [
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4.5, 5, 5.5, 6, 6, 6.5, 6.5],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [180, 185, 190, 195, 200, 205, 210],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [190, 195, 200, 205, 210, 215, 220],
                },
              ],
            },
            {
              name: "Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
                ,
              ],

              protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
              protocolText: [
                "x3 @4",
                "x3 @4.5",
                "x3 @5",
                "x3 @5.5",
                "x3 @5.5",
                "x3 @6",
                "x3 @6",
              ],
              isSecondaryProtocol: true,
              series: [
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
              ],
            },
            {
              name: "2ct Pause Bench Press",
              originalName: "benchPress-string",

              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
              protocolText: [
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @6 + x4 @6.5 + 3x4 @5",
                "x1 @6.5 + x4 @7 + 3x4 @6",
                "x1 @6.5 + x4 @7.5 + 3x4 @6",
                "x1 @7 + x4 @8 + 3x4 @6",
                "x1 @8 + x4 @8.5 + 3x4 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5.5, 5.5, 6, 6.5, 6.5, 7, 8],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [152.5, 155, 157.5, 160, 162.5, 162.5, 167.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6.5, 6.5, 6.5, 7, 7.5, 8, 8.5],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [145, 147.5, 150, 152.5, 152.5, 155, 157.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
              ],
            },
            {
              name: "Romanian Deadlift",
              originalName: "romanianDeadlift-string",
              series: [
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [4, 4, 4, 4, 4, 4, 4],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [185, 185, 185, 190, 190, 190, 195],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [5, 5, 5, 5, 5, 5, 5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [195, 195, 195, 200, 200, 200, 205],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [6, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [205, 205, 205, 210, 210, 210, 215],
                },
              ],
            },
            {
              name: "Machine Press",
              originalName: "machinePress-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
              ],
            },
          ],

          [
            {
              name: "1ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "1ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
              protocolText: [
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5, 5, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
              ],
            },
            {
              name: "Sumo Deadlift",
              originalName: "sumoDeadlift-string",
              protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
              protocolText: [
                "Ramp 3x5 @6",
                "Ramp 3x5 @6.5",
                "Ramp 3x5 @7",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @8",
                "Ramp 3x5 @8",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [210, 215, 220, 225, 230, 235, 240],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [220, 225, 230, 235, 240, 245, 250],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [230, 235, 240, 245, 250, 255, 260],
                },
              ],
            },
            {
              name: "Dumbbell Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "Dumbbell-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                ,
              ],
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
              ],
            },
            {
              name: "Leg Curls",
              originalName: "legCurls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [0, 0, 0, 0, 0, 0, 0],
                },
              ],
            },
            {
              name: "Pull Down",
              originalName: "pullDown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
              ],
            },
            {
              name: "Cable Row",
              originalName: "cableRow-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
              ],
            },
            {
              name: "Tricep Pushdown",
              originalName: "tricepPushdown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
              ],
            },
          ],

          [
            {
              name: "Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
              protocolText: [
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 192.5, 195, 197.5, 200, 202.5, 202.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6, 6, 6, 7, 7, 7],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [195, 197.5, 200, 200, 202.5, 205, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
              ],
            },
            {
              name: "3ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "3ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.QUATERNARY_BENCH_PRESS,
              protocolText: [
                "3x1 @5",
                "3x1 @5.5",
                "3x1 @6",
                "3x1 @6.5",
                "3x1 @6.5",
                "3x1 @7",
                "3x1 @7",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
              ],
            },
            ,
            {
              name: "Bulgarian Split Squat",
              originalName: "bulgarianSplitSquat-string",
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
              ],
            },
            {
              name: "Leg Extension",
              originalName: "legExtension-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
              ],
            },
          ],

          [
            {
              name: "2ct Pause Spoto Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
                {
                  name: "Spoto-string",
                  title: "rom-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
              protocolText: [
                "2x1 @6 + 3x3 @5",
                "2x1 @7 + 3x3 @6",
                "2x1 @7 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
              ],
            },
            {
              name: "Conventional Deadlift",
              originalName: "conventionalDeadlift-string",
              protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
              protocolText: [
                "Ramp 3x4 @6",
                "Ramp 3x4 @6.5",
                "Ramp 3x4 @7",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @8",
                "Ramp 3x4 @8",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 185, 187.5, 190, 195, 200, 200],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 195, 197.5, 200, 205, 210, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [200, 205, 207.5, 210, 215, 220, 220],
                },
              ],
            },
            {
              name: "Cable Single Arm Pull Down",
              originalName: "pullDown-string",
              attributes: [
                {
                  name: "Cable-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                {
                  name: "SingleArm-string",
                  title: "other-string",
                  subtitle: "",
                },
              ],
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
              ],
            },
            {
              name: "Row",
              originalName: "row-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
              ],
            },
            {
              name: "Tricep Extensions",
              originalName: "tricepExtensions-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
              ],
            },
          ],
        ],
      },
      // Repeat this structure for weeks 2 to 7
    ];

    // const newClientId = "tnpUUq3q2odLFvtVb3rAt0OC0Y94";
    const newBlockId = String(uuidv4());

    const newClient = new Client(
      this.id,
      this.email,
      this.name,
      this.surname,
      this.name,
      this.surname,
      false,
      this.exerciseUnit,
      new Date(),
      this.rirOrRpe,
      this.coachLanguage,
      undefined,
      true,
      5,
      5,
      this,
      [false, true, true, false, true, false, true],
      true,
      "",
      "",
      undefined,
      undefined,
      undefined,
      undefined,
      "Build Block"
    );
    newClient.setProperty("avoidTransaction", true);

    const newBlock = new Block(
      newBlockId,
      "Build Block",
      "", // notes
      [], // workouts
      true,
      false,
      newClient,
      5, // totalWeeks
      5,
      "Base",
      new Date()
    );
    console.log("just after creating workouts for new block");

    const getDateOfSpecificWeekday = (weeksAgo, weekday) => {
      const today = new Date(); // Get today's date
      const currentDay = today.getDay(); // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
      const startOfWeek = new Date(today); // Create a new date object for the start of the week

      // Calculate the start of the week (Monday)
      startOfWeek.setDate(today.getDate() - currentDay + 1);
      if (currentDay === 0) {
        startOfWeek.setDate(startOfWeek.getDate() - 7); // Adjust if today is Sunday
      }

      // Calculate the date `weeksAgo` weeks ago
      startOfWeek.setDate(startOfWeek.getDate() - weeksAgo * 7);

      // Calculate the target date
      const targetDate = new Date(startOfWeek);
      targetDate.setDate(startOfWeek.getDate() + (weekday - 1));

      return targetDate;
    };

    // Weekdays for Monday, Wednesday, Thursday, and Saturday
    const weekdays = [2, 3, 5, 7];

    // Generate the workout dates
    let firstWorkoutDate = null;
    let lastWorkoutDate = null;

    // Create the workouts for the 7-week training block
    runInAction(() => {
      for (let week = 4; week >= 0; week--) {
        for (let day = 0; day < 4; day++) {
          const startDate = getDateOfSpecificWeekday(4 - week, weekdays[day]);

          if (!firstWorkoutDate || startDate < firstWorkoutDate) {
            firstWorkoutDate = startDate;
          }
          if (!lastWorkoutDate || startDate > lastWorkoutDate) {
            lastWorkoutDate = startDate;
          }

          const newWorkout = new Workout(
            undefined,
            startDate,
            this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
            day + 1, // order
            week + 1, // week
            false,
            newBlock,
            []
          );

          exercisePlan[0].workouts[day].forEach((exerciseInfo, index) => {
            const {
              originalName,
              name,
              attributes,
              series,
              protocolSlot,
              protocolText,
              isSecondaryProtocol,
            } = exerciseInfo;
            const newExercise = new Exercise(
              undefined,
              name,
              originalName,
              "", // notes
              index, // order
              undefined,
              index === 0,
              newWorkout,
              [],
              []
            );

            if (protocolSlot) {
              newExercise.protocolSlot = protocolSlot;
            }
            if (protocolText?.length > 0) {
              newExercise.protocolText = protocolText[week];
            }
            if (isSecondaryProtocol) {
              newExercise.isSecondaryProtocol = isSecondaryProtocol;
            }

            if (series) {
              // Add sets/series
              for (let setIndex = 0; setIndex < series.length; setIndex++) {
                const serieInfo = series[setIndex];
                if (serieInfo) {
                  const newSerie = new Serie(
                    undefined,
                    setIndex + 1, // order
                    week < 4 ? serieInfo.targetReps[week] : 0,
                    week < 4 ? serieInfo.targetRPE[week] : 0, // Specific targetRPE for this week
                    week < 4 ? serieInfo.weight[week] : 0, // Specific weight for this week
                    serieInfo.targetReps[week],
                    serieInfo.targetRPE[week], // target_rpe
                    0, // target_weight
                    newExercise,
                    serieInfo.relevance[week],
                    undefined,
                    serieInfo.relevance[week] === 1
                  );
                  newExercise.series.push(newSerie);
                }
              }
            }
            // Add attributes if present
            if (attributes) {
              attributes.forEach((attribute, attrIndex) => {
                const newAttribute = new Attribute(
                  String(uuidv4()),
                  attribute.name,
                  attribute.title,
                  attribute.subtitle,
                  attrIndex, // order
                  false, // isPredefined
                  false,
                  null, // date
                  newExercise
                );
                newExercise.attributes.push(newAttribute);
              });
            }

            newWorkout.exercises.push(newExercise);
          });

          newBlock.workouts.push(newWorkout);
        }
      }
    });

    newClient.setProperty("isFetched", true);
    newClient.setProperty("metricsSelectedBlocks", [newBlock]);
    //canviar selected aqui
    newClient.setProperty(
      "exercisesMetricsSelected",
      this.coachLanguage === "es"
        ? ["Sentadilla Tempo 400 Barra Baja"]
        : ["400 Tempo Low Bar Squat"]
    );
    //canviar pinneds aqui
    newClient.setProperty(
      "pinnedExercises",
      this.coachLanguage === "es"
        ? ["Sentadilla Tempo 400 Barra Baja"]
        : ["400 Tempo Low Bar Squat"]
    );
    newClient.setProperty("startWeek", 1);
    newClient.setProperty("endWeek", 5);

    newClient.blocks.push(newBlock);
    this.clients.push(newClient);
    setTimeout(() => {
      newClient.setProperty("avoidTransaction", false);
    }, 0);
    console.log("just before uploading demo client to firestore");

    const clientArraysDocRef = doc(db, "users", this.id, "arrays", "arrays");
    const clientBlocksDocRef = doc(db, "users", this.id, "blocks", newBlockId);

    await setDoc(clientDocRef, {
      ...newClient.uploadToCloud(),
      coach_language: this.coachLanguage,
      // show_ec_message: true,
    });
    await setDoc(clientArraysDocRef, {
      attributes: [],
      predefined_exercises: [],
      tracs: [],
      bodyweights: [],
      notes: [],
    });
    await setDoc(clientBlocksDocRef, newBlock.uploadToCloud(new Date()));
  }

  changeClientNamesDemo(language, i18n) {
    this.coachLanguage = language;
    i18n.changeLanguage(language);
    const namesList =
      language == "es"
        ? [
            "María García",
            "José Fernández",
            "Lucía López",
            "Antonio Martínez",
            "Carmen González",
            "Francisco Pérez",
            "Laura Sánchez",
            "Manuel Rodríguez",
            "Sofía Gómez",
            "David Hernández",
            "Isabel Ruiz",
            "Javier Jiménez",
            "Ana Díaz",
            "Alejandro Morales",
            "Paula Romero",
            "Juan Muñoz",
            "Elena Alonso",
            "Pedro Gutiérrez",
            "Clara Navarro",
            "Daniel Torres",
            "Marta Castro",
            "Pablo Gil",
            "Adriana Ortiz",
            "Carlos Domínguez",
            "Irene Moreno",
            "Raúl Vázquez",
            "Marina Ramos",
            "Hugo Blanco",
            "Victoria Medina",
            "Sergio Delgado",
            "Alba Molina",
            "Lucas Santiago",
            "Daniela Pastor",
            "Samuel Rubio",
            "Carla Rivas",
            "Diego Castillo",
            "Beatriz Peña",
            "Gabriel Suárez",
            "Valeria Iglesias",
            "Miguel Cabrera",
            "Inés Ortiz",
            "Luis Campos",
            "Alicia Pardo",
            "Enrique Serrano",
            "Noa Ortega",
            "Tomás Soler",
            "Natalia Márquez",
            "Jorge Cortés",
            "Andrea Ibáñez",
          ]
        : [
            "Emily Johnson",
            "Michael Smith",
            "Sarah Brown",
            "Christopher Davis",
            "Jessica Garcia",
            "Matthew Wilson",
            "Ashley Martinez",
            "Joshua Taylor",
            "Megan Robinson",
            "Daniel Lewis",
            "Lauren Walker",
            "Andrew Hall",
            "Amanda Allen",
            "Jacob Young",
            "Rachel King",
            "Benjamin Wright",
            "Emily Scott",
            "Tyler Green",
            "Hannah Adams",
            "Ryan Baker",
            "Olivia Gonzalez",
            "Nathan Nelson",
            "Madison Carter",
            "David Perez",
            "Emma Phillips",
            "William Mitchell",
            "Grace Hill",
            "Alexander Evans",
            "Victoria Turner",
            "Jonathan Torres",
            "Isabella Harris",
            "Ethan Reed",
            "Abigail Cook",
            "Samuel Rogers",
            "Chloe Morgan",
            "Dylan Cooper",
            "Samantha Richardson",
            "Caleb Bailey",
            "Lily Flores",
            "Elijah Ramirez",
            "Zoe Murphy",
            "Luke Howard",
            "Ella Brooks",
            "Jack Price",
            "Nora Bennett",
            "Logan Reed",
            "Sophia Barnes",
            "Mason Parker",
            "Ava Butler",
          ];

    let nameIndex = 0; // To keep track of the current name in the list

    this.clients.forEach((client) => {
      if (nameIndex < namesList.length) {
        if (client.email === "thewizlix@gmail.com") {
          client.setProperty(
            "nameForCoach",
            language === "es" ? "Marcos" : "James"
          );
          client.setProperty(
            "surnameForCoach",
            language === "es" ? "López" : "Anderson"
          );
          client.setProperty(
            "email",
            language === "es"
              ? "marcoslopez@gmail.com"
              : "jamesanderson@gmail.com"
          );
        } else {
          client.setProperty("nameForCoach", namesList[nameIndex]);
          client.setProperty("surnameForCoach", "");
          nameIndex++; // Move to the next name

          // const userDocRef = doc(db, "users", client.id);

          // clientData = {
          //   name_for_coach: client.nameForCoach,
          //   surname_for_coach: client.surnameForCoach,
          //   user_surname: "",
          // };

          // updateDoc(userDocRef, clientData)
          //   .then(() => {})
          //   .catch((error) => {
          //     console.error("Error updating or creating document:", error);
          //   });
        }
      }
    });
  }
  // #endregion

  // #region /* PREDEFINEDS */
  ExerciseTypes = {
    kneeDominant: "kneeDominant-string",
    hipDominant: "hipDominant-string",
    horizontalPush: "horizontalPush-string",
    verticalPush: "verticalPush-string",
    horizontalPull: "horizontalPull-string",
    verticalPull: "verticalPull-string",
    weightlifting: "weightlifting-string",
    other: "other-string",
  };
  getPredefinedArray() {
    //KNEE DOMINANT
    const squat = new PredefinedExercise(
      uuidv4(),
      "squat-string",
      "squat-string",
      1000,
      this.ExerciseTypes.kneeDominant,
      "1. Coloca los pies a la anchura de los hombros con los dedos ligeramente apuntados hacia afuera. \n\n2. Dobla las rodillas y la cadera, bajando hasta que los muslos estén justo por debajo de la paralela. \n\n3. Empuja el suelo para ponerte de pie, estirando las piernas y la cadera.",
      "",
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const hackSquat = new PredefinedExercise(
      uuidv4(),
      "hackSquat-string",
      "hackSquat-string",
      1001,
      this.ExerciseTypes.kneeDominant,
      "1. Colócate en la máquina de hack squat con la espalda contra la almohadilla y los hombros bajo las almohadillas de los hombros. \n\n2. Coloca los pies a la anchura de los hombros y desactiva las barras de seguridad. Baja tu cuerpo doblando las rodillas, yendo un poco por debajo de la paralela. \n\n3. Empuja para extender las piernas, regresando a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const bulgarianSquat = new PredefinedExercise(
      uuidv4(),
      "bulgarianSplitSquat-string",
      "bulgarianSplitSquat-string",
      1002,
      this.ExerciseTypes.kneeDominant,
      "1. Párate a unos pies de un banco, de espaldas a él, coloca un pie adelantado y el otro encima del banco. \n\n2. Baja tu cuerpo doblando la rodilla delantera, manteniéndola alineada con el pie, hasta que el muslo esté justo por debajo de la paralela. \n\n3. Empuja para volver a la posición inicial. Cambia de pierna y repite.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const staticLunge = new PredefinedExercise(
      uuidv4(),
      "splitSquat-string",
      "splitSquat-string",
      1003,
      this.ExerciseTypes.kneeDominant,
      "1. Comienza con una postura escalonada con un pie hacia adelante y el otro pie extendido detrás de ti. \n\n2. Dobla ambas rodillas para bajar tu cuerpo recto hacia abajo, manteniendo la rodilla delantera alineada con el pie, hasta que la rodilla trasera casi toque el suelo. \n\n3. Presiona el pie delantero para volver a la posición inicial. Cambia de pierna y repite.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const frontSquat = new PredefinedExercise(
      uuidv4(),
      "frontSquat-string",
      "frontSquat-string",
      1004,
      this.ExerciseTypes.kneeDominant,
      "1. Coloca la barra sobre el frente de tus hombros, cruzando tus brazos para agarrar la barra. \n\n2. Baja tu cuerpo doblando las rodillas y la cadera, manteniendo el pecho erguido, hasta que los muslos estén justo por debajo de la paralela. \n\n3. Empuja para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const platzSquat = new PredefinedExercise(
      uuidv4(),
      "platzSquat-string",
      "platzSquat-string",
      1005,
      this.ExerciseTypes.kneeDominant,
      "1. Coloca los pies más anchos que la anchura de los hombros y angula los dedos de los pies hacia afuera. \n\n2. Bájate doblando las rodillas y la cadera, manteniendo la espalda recta, hasta que los muslos estén justo por debajo de la paralela. \n\n3. Empuja para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const zercherSquat = new PredefinedExercise(
      uuidv4(),
      "zercherSquat-string",
      "zercherSquat-string",
      1011,
      this.ExerciseTypes.kneeDominant,
      "1. Sostén una barra en el pliegue de tus codos, con los pies a la anchura de los hombros. \n\n2. Dobla tus rodillas y caderas, bajando tu cuerpo hasta que los muslos estén justo por debajo de la paralela. \n\n3. Empuja para ponerte de pie, estirando las piernas y la cadera.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const beltSquat = new PredefinedExercise(
      uuidv4(),
      "beltSquat-string",
      "beltSquat-string",
      1006,
      this.ExerciseTypes.kneeDominant,
      "1. Colócate en la máquina de belt squat y ajusta el cinturón a la altura de la cadera. \n\n2. Inicia el movimiento bajando en sentadilla (rodillas y cadera), manteniendo el torso lo más erguido posible. \n\n3. Empuja el suelo para volver a la posición inicial, extendiendo rodillas y cadera.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const legPress = new PredefinedExercise(
      uuidv4(),
      "legPress-string",
      "legPress-string",
      1007,
      this.ExerciseTypes.kneeDominant,
      "1. Siéntate en una máquina de prensa de piernas con los pies a la anchura de los hombros en la plataforma. \n\n2. Baja el peso doblando tus rodillas, manteniéndolas alineadas con tus pies, hasta que los muslos estén justo por debajo de paralelos al suelo. \n\n3. Empuja para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const legExtension = new PredefinedExercise(
      uuidv4(),
      "legExtension-string",
      "legExtension-string",
      1008,
      this.ExerciseTypes.kneeDominant,
      "1. Siéntate en una máquina de extensión de piernas con la espalda contra la almohadilla y coloca tus espinillas debajo de la palanca acolchada. \n\n2. Extiende tus piernas flexionando los cuádriceps, levantando el peso. \n\n3. Regresa lentamente a la posición inicial, controlando el movimiento.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const legCurls = new PredefinedExercise(
      uuidv4(),
      "legCurls-string",
      "legCurls-string",
      1009,
      this.ExerciseTypes.kneeDominant,
      "1. Acuéstate boca abajo en una máquina de curl de piernas, posicionando la palanca acolchada debajo de tus pantorrillas. \n\n2. Flexionando los isquiotibiales, trayendo los talones hacia tus glúteos. \n\n3. Baja lentamente el los pies a la posición inicial, manteniendo el control durante todo el movimiento.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const zancadas = new PredefinedExercise(
      uuidv4(),
      "lunges-string",
      "lunges-string",
      1010,
      this.ExerciseTypes.kneeDominant,
      "1. Párate con los pies separados a la anchura de las caderas y da un paso adelante con una pierna. \n\n2. Baja tu cuerpo doblando ambas rodillas hasta que la rodilla trasera casi toque el suelo, manteniendo la rodilla delantera alineada con el pie. \n\n3. Empuja para volver a la posición inicial, luego cambia de pierna y repite.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //HIP DOMINANT
    const conventionalDeadlift = new PredefinedExercise(
      uuidv4(),
      "conventionalDeadlift-string",
      "conventionalDeadlift-string",
      1100,
      this.ExerciseTypes.hipDominant,
      "1. Párate con los pies separados a la anchura de la cadera y agarra la barra con las manos justo fuera de las rodillas. \n\n2. Manteniendo la espalda recta, empuja para levantar la barra, extendiendo completamente tu cadera y rodillas. \n\n3. Baja la barra al suelo.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const sumoDeadlift = new PredefinedExercise(
      uuidv4(),
      "sumoDeadlift-string",
      "sumoDeadlift-string",
      1101,
      this.ExerciseTypes.hipDominant,
      "1. Párate con los pies más anchos que la anchura de los hombros, los dedos de los pies apuntando hacia afuera, y agarra la barra dentro de tus rodillas. \n\n2. Manteniendo la espalda recta, empuja para levantar la barra, extendiendo completamente tu cadera y rodillas. \n\n3. Baja la barra al suelo, manteniendo el control durante todo el movimiento.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const romanianDeadlift = new PredefinedExercise(
      uuidv4(),
      "romanianDeadlift-string",
      "romanianDeadlift-string",
      1102,
      this.ExerciseTypes.hipDominant,
      "1. Párate con los pies separados a la anchura de las caderas, sosteniendo una barra con un agarre mixto. \n\n2. Manteniendo las piernas ligeramente dobladas, inclínate en las caderas para bajar la barra hacia el suelo, manteniéndola cerca de tus piernas. \n\n3. Invierte el movimiento, impulsando a través de las caderas para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const oneLegDeadlift = new PredefinedExercise(
      uuidv4(),
      "singleLegRDL-string",
      "singleLegRDL-string",
      1103,
      this.ExerciseTypes.hipDominant,
      "1. Párate sobre una pierna, sosteniendo un peso con la mano opuesta. \n\n2. Inclínate en la cadera de la pierna que está parada, bajando el peso hacia el suelo y extendiendo la otra pierna detrás de ti. \n\n3. Regresa a la posición inicial, manteniendo tu equilibrio, y cambia de lado.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const stiffLegDeadlift = new PredefinedExercise(
      uuidv4(),
      "stiffLegRDL-string",
      "stiffLegRDL-string",
      1104,
      this.ExerciseTypes.hipDominant,
      "1. Párate con los pies a la anchura de los hombros, sosteniendo una barra delante de ti. \n\n2. Manteniendo las piernas rectas, inclínate en las caderas para bajar la barra hacia el suelo. \n\n3. Regresa a la posición inicial activando los glúteos y los isquiotibiales.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const goodMorning = new PredefinedExercise(
      uuidv4(),
      "goodMorning-string",
      "goodMorning-string",
      1105,
      this.ExerciseTypes.hipDominant,
      "1. Coloca una barra sobre la parte superior de tu espalda, con los pies a la anchura de los hombros. \n\n2. Inclínate en las caderas, bajando tu torso hacia el suelo, manteniendo las piernas rectas y la espalda plana. \n\n3. Vuelve a la posición inicial impulsando a través de las caderas.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const hipThrust = new PredefinedExercise(
      uuidv4(),
      "hipThrust-string",
      "hipThrust-string",
      1106,
      this.ExerciseTypes.hipDominant,
      "1. Siéntate en el suelo con una barra sobre tu cadera y los hombros contra un banco. \n\n2. Empuja levantando tu cadera hacia el techo, apretando los glúteos en la parte superior. \n\n3. Baja las caderas de nuevo al suelo, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const elevacionGluteo = new PredefinedExercise(
      uuidv4(),
      "gluteHamRaise-string",
      "gluteHamRaise-string",
      1107,
      this.ExerciseTypes.hipDominant,
      "1. Posiciónate en una máquina de GHR con los pies asegurados y las rodillas justo detrás de la almohadilla. \n\n2. Baja tu cuerpo extendiendo las rodillas, manteniendo una línea recta desde la cabeza hasta las rodillas. \n\n3. Vuelve a la posición inicial flexionando los isquiotibiales y los glúteos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const abductorMachine = new PredefinedExercise(
      uuidv4(),
      "abductorMachine-string",
      "abductorMachine-string",
      1108,
      this.ExerciseTypes.hipDominant,
      "1. Siéntate en la máquina de abducción con las piernas dentro de las almohadillas. \n\n2. Presiona contra las almohadillas, separando las piernas. \n\n3. Regresa lentamente a la posición inicial, manteniendo el control durante todo el movimiento.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const hyperextensions = new PredefinedExercise(
      uuidv4(),
      "hyperextensions-string",
      "hyperextensions-string",
      1109,
      this.ExerciseTypes.hipDominant,
      "1. Colócate en un banco de hiperextensiones (roman chair) con las caderas apoyadas y los pies fijados. \n\n2. Flexiona la cadera para bajar el torso de forma controlada manteniendo la espalda neutra. \n\n3. Extiende la cadera para volver arriba, contrayendo glúteos e isquios sin hiperextender la zona lumbar.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //HORIZONTAL PUSH
    const benchPress = new PredefinedExercise(
      uuidv4(),
      "benchPress-string",
      "benchPress-string",
      1200,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate plano en un banco con los pies apoyados en el suelo, agarrando la barra sobre ti. \n\n2. Baja la barra hacia tu pecho, manteniendo los codos en un ángulo de 45 grados. \n\n3. Presiona la barra de vuelta a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const inclineBenchPress = new PredefinedExercise(
      uuidv4(),
      "inclineBenchPress-string",
      "inclineBenchPress-string",
      1201,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en un banco inclinado con los pies apoyados en el suelo, agarrando la barra sobre ti. \n\n2. Baja la barra hacia tu pecho superior, manteniendo los codos en un ángulo de 45 grados. \n\n3. Presiona la barra de vuelta a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const declineBenchPress = new PredefinedExercise(
      uuidv4(),
      "declineBenchPress-string",
      "declineBenchPress-string",
      1202,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en un banco declinado con los pies asegurados, agarrando la barra sobre ti. \n\n2. Baja la barra hacia tu pecho inferior, manteniendo los codos en un ángulo de 45 grados. \n\n3. Presiona la barra de vuelta a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const machinePress = new PredefinedExercise(
      uuidv4(),
      "machinePress-string",
      "machinePress-string",
      1203,
      this.ExerciseTypes.horizontalPush,
      "1. Siéntate en la máquina de press de pecho de forma que los codos estén a 45 grados respecto tu torso. \n\n2. Empuja la máquina para extender los codos por completo. \n\n3. Regresa a la posición inicial de forma controlada.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const floorPress = new PredefinedExercise(
      uuidv4(),
      "floorPress-string",
      "floorPress-string",
      1204,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en el suelo con las rodillas dobladas, agarrando una barra sobre tu pecho. \n\n2. Baja la barra hacia el suelo, hasta que tus codos toquen el suelo. \n\n3. Presiona la barra de vuelta a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const jmPress = new PredefinedExercise(
      uuidv4(),
      "jmPress-string",
      "jmPress-string",
      1205,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en un banco plano, sosteniendo una barra sobre tu pecho con los brazos extendidos. \n\n2. Baja la barra hacia tu pecho superior, pero detente a medio camino abriendo los codos hacia afuera. \n\n3. Extiende tus brazos para volver a la posición inicial, enfocándote en la activación del tríceps.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const inclinePushUps = new PredefinedExercise(
      uuidv4(),
      "inclinePushUp-string",
      "inclinePushUp-string",
      1206,
      this.ExerciseTypes.horizontalPush,
      "1. Coloca las manos en una superficie elevada, como un banco, con los pies en el suelo. \n\n2. Baja tu pecho hacia la superficie doblando los codos. \n\n3. Presiona a través de tus palmas para volver a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pushUps = new PredefinedExercise(
      uuidv4(),
      "pushUp-string",
      "pushUp-string",
      1207,
      this.ExerciseTypes.horizontalPush,
      "1. Comienza en una posición de plancha con las manos debajo de los hombros y el cuerpo recto. \n\n2. Baja tu pecho hacia el suelo doblando los codos. \n\n3. Presiona a través de tus palmas para volver a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const tricepExtensions = new PredefinedExercise(
      uuidv4(),
      "tricepExtensions-string",
      "tricepExtensions-string",
      1208,
      this.ExerciseTypes.horizontalPush,
      "1. Sostén una mancuerna con ambas manos sobre tu cabeza con los brazos extendidos. \n\n2. Dobla tus codos para bajar el peso detrás de tu cabeza, manteniendo los codos cerca uno del otro. \n\n3. Extiende tus brazos para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const tricepsPushdown = new PredefinedExercise(
      uuidv4(),
      "tricepPushdown-string",
      "tricepPushdown-string",
      1209,
      this.ExerciseTypes.horizontalPush,
      "1. Párate en una polea, agarrando la manija con las palmas hacia abajo. \n\n2. Empuja la manija hacia abajo extendiendo tus codos, manteniendo tus brazos superiores inmóviles. \n\n3. Regresa lentamente a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const skullCrushers = new PredefinedExercise(
      uuidv4(),
      "skullCrushers-string",
      "skullCrushers-string",
      1210,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en un banco, sosteniendo una barra sobre tu cabeza con los brazos extendidos. \n\n2. Dobla tus codos para bajar la barra hacia tu frente, manteniendo los brazos superiores inmóviles. \n\n3. Extiende tus brazos para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const flies = new PredefinedExercise(
      uuidv4(),
      "flies-string",
      "flies-string",
      1211,
      this.ExerciseTypes.horizontalPush,
      "1. Acuéstate en un banco con una mancuerna en cada mano, brazos extendidos sobre ti. \n\n2. Con un ligero doblez en los codos, baja los pesos hacia los lados. \n\n3. Junta los pesos nuevamente sobre tu pecho, apretando los pectorales.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //VERTICAL PUSH
    const militarPress = new PredefinedExercise(
      uuidv4(),
      "military-string",
      "military-string",
      1300,
      this.ExerciseTypes.verticalPush,
      "1. Párate con una barra a nivel del pecho, agarrándola con las manos a la anchura de los hombros. \n\n2. Presiona la barra por encima de la cabeza, extendiendo completamente tus brazos. \n\n3. Baja la barra a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const dips = new PredefinedExercise(
      uuidv4(),
      "dips-string",
      "dips-string",
      1301,
      this.ExerciseTypes.verticalPush,
      "1. Agarra las barras paralelas, sosteniéndote con los brazos completamente extendidos. \n\n2. Baja tu cuerpo doblando los codos, manteniéndolos cerca de tus costados. \n\n3. Empuja para volver a la posición inicial.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const flexionesDePino = new PredefinedExercise(
      uuidv4(),
      "handstandPushUp-string",
      "handstandPushUp-string",
      1302,
      this.ExerciseTypes.verticalPush,
      "1. Haz un pino contra una pared, con los brazos completamente extendidos. \n\n2. Baja tu cuerpo hacia el suelo doblando los codos. \n\n3. Presiona a través de tus palmas para volver a la posición inicial, extendiendo completamente tus brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const landminePress = new PredefinedExercise(
      uuidv4(),
      "landminePress-string",
      "landminePress-string",
      1303,
      this.ExerciseTypes.verticalPush,
      "1. Párate sosteniendo el extremo de una barra asegurada en un soporte de landmine, a la altura del hombro. \n\n2. Presiona la barra hacia arriba, extendiendo completamente tu brazo. \n\n3. Baja la barra a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //HORIZONTAL PULL
    const cableRow = new PredefinedExercise(
      uuidv4(),
      "cableRow-string",
      "cableRow-string",
      1400,
      this.ExerciseTypes.horizontalPull,
      "1. Siéntate en una máquina de remo con cable, agarrando las manijas con los pies en la plataforma. \n\n2. Tira de las manijas hacia tu torso, apretando los omóplatos juntos. \n\n3. Extiende tus brazos para volver a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const tRow = new PredefinedExercise(
      uuidv4(),
      "tBarRow-string",
      "tBarRow-string",
      1401,
      this.ExerciseTypes.horizontalPull,
      "1. Párate sobre una máquina de remo T-Bar, agarrando las manijas con una postura inclinada hacia adelante. \n\n2. Jala el peso hacia tus costillas inferiores, manteniendo los codos cerca de tu cuerpo. \n\n3. Baja el peso a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const inclineBenchRow = new PredefinedExercise(
      uuidv4(),
      "chestSupportedRow-string",
      "chestSupportedRow-string",
      1402,
      this.ExerciseTypes.horizontalPull,
      "1. Acuéstate boca abajo en un banco con una ligera inclinación, sosteniendo mancuernas debajo de ti. \n\n2. Tira de los pesos hacia tu cadera, apretando los omóplatos juntos. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pendlayRow = new PredefinedExercise(
      uuidv4(),
      "pendlayRow-string",
      "pendlayRow-string",
      1403,
      this.ExerciseTypes.horizontalPull,
      "1. Párate sobre una barra con una postura inclinada hacia adelante, agarrándola con las manos a la anchura de los hombros. \n\n2. Jala explosivamente la barra hacia tus costillas inferiores, manteniendo tu torso paralelo al suelo. \n\n3. Baja la barra a la posición inicial con control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const inclineRow = new PredefinedExercise(
      uuidv4(),
      "inclineRow-string",
      "inclineRow-string",
      1404,
      this.ExerciseTypes.horizontalPull,
      "1. Acuéstate boca abajo en un banco inclinado, sosteniendo mancuernas debajo de ti. \n\n2. Tira de los pesos hacia tu cadera, apretando los omóplatos juntos. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const oneArmRow = new PredefinedExercise(
      uuidv4(),
      "oneArmRow-string",
      "oneArmRow-string",
      1405,
      this.ExerciseTypes.horizontalPull,
      "1. Párate al lado de un banco, con una rodilla y una mano sobre él, sosteniendo una mancuerna con la otra mano. \n\n2. Tira de la mancuerna hacia tu cadera, manteniendo tu codo cerca de tu cuerpo. \n\n3. Baja la mancuerna a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const row = new PredefinedExercise(
      uuidv4(),
      "row-string",
      "row-string",
      1406,
      this.ExerciseTypes.horizontalPull,
      "1. Sostén una barra o mancuernas con una postura inclinada hacia adelante, las manos a la anchura de los hombros. \n\n2. Tira del peso hacia tus costillas inferiores, apretando los omóplatos juntos. \n\n3. Baja el peso a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const facePull = new PredefinedExercise(
      uuidv4(),
      "facePull-string",
      "facePull-string",
      1407,
      this.ExerciseTypes.horizontalPull,
      "1. Párate en una máquina de cables con un accesorio de cuerda, agarrando los extremos. \n\n2. Tira de la cuerda hacia tu cara, manteniendo los codos altos. \n\n3. Extiende tus brazos para volver a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const reverseRow = new PredefinedExercise(
      uuidv4(),
      "invertedBWRows-string",
      "invertedBWRows-string",
      1408,
      this.ExerciseTypes.horizontalPull,
      "1. Posiciónate debajo de una barra o anillos, agarrándolos con los brazos extendidos. \n\n2. Tira de tu pecho hacia la barra, apretando los omóplatos juntos. \n\n3. Bájate a la posición inicial con control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pajaros = new PredefinedExercise(
      uuidv4(),
      "reverseFlies-string",
      "reverseFlies-string",
      1409,
      this.ExerciseTypes.horizontalPull,
      "1. Posiciónate debajo de una barra o anillos, agarrándolos con los brazos extendidos. \n\n2. Tira de tu pecho hacia la barra, apretando los omóplatos juntos. \n\n3. Bájate a la posición inicial con control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const sealRow = new PredefinedExercise(
      uuidv4(),
      "sealRow-string",
      "sealRow-string",
      1410,
      this.ExerciseTypes.horizontalPull,
      "1. Túmbate boca abajo en un banco plano alto (que permita que los discos no toquen el suelo) con el pecho apoyado. \n\n2. Con barra o mancuernas colgando, rema llevando el peso hacia la parte baja del pecho/esternón, juntando las escápulas. \n\n3. Baja el peso de forma controlada hasta la extensión completa de brazos.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //VERTICAL PULL
    const pullUps = new PredefinedExercise(
      uuidv4(),
      "pullUp-string",
      "pullUp-string",
      1500,
      this.ExerciseTypes.verticalPull,
      "1. Agarra una barra de dominadas con las manos separadas a la anchura de los hombros, las palmas mirando hacia afuera. \n\n2. Elévate hasta que tu barbilla esté por encima de la barra. \n\n3. Bájate a la posición inicial con control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pullDown = new PredefinedExercise(
      uuidv4(),
      "pullDown-string",
      "pullDown-string",
      1501,
      this.ExerciseTypes.verticalPull,
      "1. Siéntate en una máquina de polea al pecho, agarrando la barra con las manos más separadas que los hombros. \n\n2. Tira de la barra hacia tu pecho superior, apretando los omóplatos. \n\n3. Extiende tus brazos para volver a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pullOver = new PredefinedExercise(
      uuidv4(),
      "pullOver-string",
      "pullOver-string",
      1502,
      this.ExerciseTypes.verticalPull,
      "1. Acuéstate en un banco, sosteniendo una mancuerna con ambas manos sobre tu pecho. \n\n2. Baja el peso detrás de tu cabeza, manteniendo tus brazos ligeramente doblados. \n\n3. Trae el peso de vuelta a la posición inicial, utilizando tus dorsales.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const verticalRow = new PredefinedExercise(
      uuidv4(),
      "uprightRow-string",
      "uprightRow-string",
      1503,
      this.ExerciseTypes.verticalPull,
      "1. Párate sosteniendo una barra o mancuernas a la altura del muslo, con las manos juntas. \n\n2. Tira del peso hacia tu pecho superior. \n\n3. Baja el peso a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //OTHER
    const curls = new PredefinedExercise(
      uuidv4(),
      "curls-string",
      "curls-string",
      1600,
      this.ExerciseTypes.other,
      "1. Párate sosteniendo mancuernas a tus costados, con las palmas mirando hacia adelante. \n\n2. Levanta los pesos hacia tus hombros, manteniendo los codos cerca de tu cuerpo. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const inclineCurls = new PredefinedExercise(
      uuidv4(),
      "inclineCurls-string",
      "inclineCurls-string",
      1601,
      this.ExerciseTypes.other,
      "1. Siéntate en un banco inclinado, sosteniendo mancuernas a tus costados. \n\n2. Levanta los pesos hacia tus hombros, manteniendo los codos cerca de tu cuerpo. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const hammerCurls = new PredefinedExercise(
      uuidv4(),
      "hammerCurls-string",
      "hammerCurls-string",
      1602,
      this.ExerciseTypes.other,
      "1. Párate sosteniendo mancuernas a tus costados, con las palmas mirando una hacia la otra. \n\n2. Levanta los pesos hacia tus hombros, manteniendo los codos cerca de tu cuerpo. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const lateralRaise = new PredefinedExercise(
      uuidv4(),
      "lateralRaise-string",
      "lateralRaise-string",
      1603,
      this.ExerciseTypes.other,
      "1. Párate sosteniendo mancuernas a tus costados, con las palmas mirando hacia adentro. \n\n2. Eleva los pesos hacia los lados, manteniendo los brazos ligeramente doblados. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const frontRaise = new PredefinedExercise(
      uuidv4(),
      "frontRaise-string",
      "frontRaise-string",
      1604,
      this.ExerciseTypes.other,
      "1. Párate sosteniendo mancuernas a tus costados, con las palmas mirando hacia adentro. \n\n2. Eleva los pesos frente a ti, manteniendo los brazos ligeramente doblados. \n\n3. Baja los pesos a la posición inicial, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const plank = new PredefinedExercise(
      uuidv4(),
      "plank-string",
      "plank-string",
      1605,
      this.ExerciseTypes.other,
      "1. Colócate en posición de plancha apoyando antebrazos y puntas de los pies. \n\n2. Mantén el cuerpo en línea recta (cabeza, hombros, cadera y talones), activando abdomen y glúteos. \n\n3. Sostén la posición durante el tiempo indicado sin dejar caer la cadera ni elevarla en exceso.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const copenhaguenPlank = new PredefinedExercise(
      uuidv4(),
      "copenhagenPlank-string",
      "copenhagenPlank-string",
      1606,
      this.ExerciseTypes.other,
      "1. Posiciónate en una plancha lateral, apoyándote en un antebrazo con la pierna superior en un banco. \n\n2. Mantén tu cuerpo en línea recta, activando tu core y el muslo interno. \n\n3. Mantén la posición durante el tiempo deseado.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const lateralPlank = new PredefinedExercise(
      uuidv4(),
      "sidePlank-string",
      "sidePlank-string",
      1607,
      this.ExerciseTypes.other,
      "1. Acuéstate de lado, apoyándote en un antebrazo con las piernas estiradas. \n\n2. Levanta tu cadera del suelo, formando una línea recta desde la cabeza hasta los talones. \n\n3. Mantén la posición, activando tu core, durante el tiempo deseado.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const floorBar = new PredefinedExercise(
      uuidv4(),
      "landmineRotation-string",
      "landmineRotation-string",
      1613,
      this.ExerciseTypes.other,
      "1. Párate sosteniendo el extremo de una barra asegurada en un soporte de landmine, a la altura del pecho. \n\n2. Rota tu torso, moviendo la barra de un lado a otro. \n\n3. Repite el movimiento, manteniendo el control y activando tu core.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const absWheel = new PredefinedExercise(
      uuidv4(),
      "abWheel-string",
      "abWheel-string",
      1608,
      this.ExerciseTypes.other,
      "1. Arrodíllate en el suelo, sosteniendo una rueda abdominal con ambas manos. \n\n2. Haz rodar la rueda hacia adelante, extendiendo tu cuerpo en una línea recta. \n\n3. Vuelve la rueda a la posición inicial, activando tu core.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const toeRaise = new PredefinedExercise(
      uuidv4(),
      "calfRaise-string",
      "calfRaise-string",
      1609,
      this.ExerciseTypes.other,
      "1. Párate derecho, con los pies separados a la anchura de las caderas. \n\n2. Elevate sobre tus dedos de los pies, activando los músculos de las pantorrillas. \n\n3. Baja los talones al suelo, manteniendo el control.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const legRaise = new PredefinedExercise(
      uuidv4(),
      "legLowers-string",
      "legLowers-string",
      1610,
      this.ExerciseTypes.other,
      "1. Acuéstate de espaldas, con las piernas extendidas hacia arriba. \n\n2. Baja tus piernas hacia el suelo, manteniéndolas rectas. \n\n3. Levántalas de nuevo a la posición inicial, activando tu core.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const pallofPress = new PredefinedExercise(
      uuidv4(),
      "pallofPress-string",
      "pallofPress-string",
      1611,
      this.ExerciseTypes.other,
      "1. Párate de lado a una máquina de cables, agarrando la manija a la altura del pecho. \n\n2. Presiona la manija directamente frente a ti, resistiendo la rotación. \n\n3. Vuelve a la posición inicial, manteniendo el control y la alineación.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );
    const crunchAbdominal = new PredefinedExercise(
      uuidv4(),
      "crunchAbdominal-string",
      "crunchAbdominal-string",
      1612,
      this.ExerciseTypes.other,
      "1. Túmbate boca arriba con las rodillas flexionadas y los pies apoyados. \n\n2. Eleva el torso contrayendo el abdomen (sin tirar del cuello), llevando las costillas hacia la pelvis. \n\n3. Baja de forma controlada hasta la posición inicial y repite.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    //HALTEROFILIA
    const cleanPull = new PredefinedExercise(
      uuidv4(),
      "cleanPull-string",
      "cleanPull-string",
      1700,
      this.ExerciseTypes.weightlifting,
      "1. Párate sobre una barra, agarrándola con las manos más separadas que el ancho de los hombros. \n\n2. Extiende de manera explosiva tu cadera y rodillas, tirando de la barra hacia arriba. \n\n3. Devuelve la barra al suelo con control, manteniendo una forma adecuada.",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      []
    );

    return [
      squat,
      hackSquat,
      bulgarianSquat,
      frontSquat,
      platzSquat,
      beltSquat,
      staticLunge,
      legPress,
      legExtension,
      legCurls,
      zancadas,
      conventionalDeadlift,
      sumoDeadlift,
      romanianDeadlift,
      oneLegDeadlift,
      stiffLegDeadlift,
      goodMorning,
      hipThrust,
      abductorMachine,
      hyperextensions,
      benchPress,
      inclineBenchPress,
      machinePress,
      jmPress,
      pushUps,
      tricepExtensions,
      tricepsPushdown,
      skullCrushers,
      flies,
      militarPress,
      dips,
      landminePress,
      cableRow,
      tRow,
      inclineBenchRow,
      pendlayRow,
      sealRow,
      oneArmRow,
      row,
      facePull,
      pajaros,
      pullUps,
      pullDown,
      pullOver,
      verticalRow,
      curls,
      inclineCurls,
      hammerCurls,
      lateralRaise,
      frontRaise,
      plank,
      copenhaguenPlank,
      lateralPlank,
      absWheel,
      toeRaise,
      legRaise,
      pallofPress,
      crunchAbdominal,
    ];
  }
  getPredefinedAttributes() {
    const equipments = [
      "Dumbbell-string",
      "Barbell-string",
      "Cable-string",
      "Machine-string",
      "SmithMachine-string",
      "ZBar-string",
      "SafetyBar-string",
      "TrapBar-string",
    ];

    const predefinedEquipments = equipments.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.equipment,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const tempo = [
      "1ctPause-string",
      "2ctPause-string",
      "3ctPause-string",
      "5ctPause-string",
      "320Tempo-string",
      "303Tempo-string",
      "300Tempo-string",
      "400Tempo-string",
      "500Tempo-string",
      "Low2ctPause-string",
      "High2ctPause-string",
      "TouchAndGo-string",

      // Commented out for now (kept in code)
      // "Tempo-string",
      // "530Tempo-string",
      // "600Tempo-string",
      // "Low3ctPause-string",
      // "High2ctPause-string",
      // "High3ctPause-string",
    ];

    const predefinedTempos = tempo.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.tempo,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const rom = [
      "Spoto-string",
      "LowPin-string",
      "MiddlePin-string",
      "HighPin-string",
      "LowBlock-string",
      "HighBlock-string",
      "Deficit-string",

      // Commented out for now (kept in code)
      // "5cmDeficit-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "10cmDeficit-string", //TO COMMENT ONCE ADDED TO ATHLETE APP
      // "LowBlock-string",
      // "HighBlock-string",
    ];

    const predefinedROM = rom.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.rom,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const romBoards = [
      "Board-string",

      // Commented out for now (kept in code)
      // "HalfBoard-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "1Board-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "2Boards-string",
      // "3Boards-string",
      // "4Boards-string",
      // "5Boards-string",
    ];

    const predefinedROMBoards = romBoards.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.rom,
          "boards-string",
          index,
          true,
          false,
          null,
          null
        )
    );

    const stanceGrip = [
      "HighBar-string",
      "LowBar-string",
      "Pronated-string",
      "Supinated-string",
      "SnatchGrip-string",

      // Commented out for now (kept in code)
      // "Standard-string",
      // "ReverseGrip-string",
    ];

    const predefinedStanceGrip = stanceGrip.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.stanceGrip,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const stanceGripGrip = [
      "CloseGrip-string",
      "WideGrip-string",
      "NeutralGrip-string",

      // Commented out for now (kept in code)
      // "VeryCloseGrip-string",
      // "VeryWideGrip-string",
    ];

    const predefinedStanceGripGrip = stanceGripGrip.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.stanceGrip,
          "grip-string",
          index,
          true,
          false,
          null,
          null
        )
    );

    const stanceGripStance = [
      "CloseStance-string",
      "WideStance-string",

      // Commented out for now (kept in code)
      // "VeryCloseStance-string",
      // "VeryWideStance-string",
      // "InSplit-string",
    ];

    const predefinedStanceGripStance = stanceGripStance.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.stanceGrip,
          "stance-string",
          index,
          true,
          false,
          null,
          null
        )
    );

    const loadAccomodation = [
      // Commented out for now (kept in code)
      // "Chains-string",
      // "WeightReleasers-string",
    ];

    const predefinedLoadAccomodation = loadAccomodation.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.loadAccomodation,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const loadAccomodationBands = [
      "Band-string",

      // Commented out for now (kept in code)
      // "StrongBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "AverageBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "LightBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
    ];

    const predefinedLoadAccomodationBands = loadAccomodationBands.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.loadAccomodation,
          "bands-string",
          index,
          true,
          false,
          null,
          null
        )
    );

    const loadAccomodationReverseBands = [
      "ReverseBand-string",

      // Commented out for now (kept in code)
      // "ReverseLightBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "ReverseAverageBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
      // "ReverseStrongBand-string", // TO COMMENT ONCE ADDED TO ATHLETE APP
    ];

    const predefinedLoadAccomodationReverseBands =
      loadAccomodationReverseBands.map(
        (text, index) =>
          new Attribute(
            uuidv4(),
            text,
            Attribute.AttributeTitles.loadAccomodation,
            "reverseBands-string",
            index,
            true,
            false,
            null,
            null
          )
      );

    const accessories = [
      "Belt-string",
      "Beltless-string",

      // Commented out for now (kept in code)
      // "KneeWraps-string",
      // "KneeSleeves-string",
      // "Straps-string",
    ];

    const predefinedAccessories = accessories.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.accessories,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    const other = [
      "Unilateral-string",
      "Seated-string",
      "Standing-string",
      "Lying-string",
      "FeetUp-string",
      "Larsen-string",
      "Clusters-string",
      "Kodama-string",

      //Replaced by unilateral
      // "SingleArm-string", // TO COMMENT ONCE UNILATERAL IS ADDED TO ATHLETE APP
      // "SingleLeg-string", // TO COMMENT ONCE UNILATERAL IS ADDED TO ATHLETE APP
    ];

    const predefinedOthers = other.map(
      (text, index) =>
        new Attribute(
          uuidv4(),
          text,
          Attribute.AttributeTitles.other,
          undefined,
          index,
          true,
          false,
          null,
          null
        )
    );

    return [
      ...predefinedEquipments,
      ...predefinedTempos,
      ...predefinedROM,
      ...predefinedROMBoards,
      ...predefinedStanceGrip,
      ...predefinedStanceGripGrip,
      ...predefinedStanceGripStance,
      ...predefinedLoadAccomodation,
      ...predefinedLoadAccomodationBands,
      ...predefinedLoadAccomodationReverseBands,
      ...predefinedAccessories,
      ...predefinedOthers,
      // ...this.customPredefinedAttributes,
    ];
  }
  // #endregion

  // #region /* NUTRITION */

  get selectedFood() {
    return this.foods.find((food) => food.isSelected);
  }

  setSelectedFood(selectedFood) {
    this.foods.forEach((food) => {
      if (food.id === selectedFood.id) {
        food.setProperty("isSelected", true);
      } else {
        food.setProperty("isSelected", false);
      }
    });
  }

  updateCoachTransactionFoods(id) {
    if (!this.coachTransactionFoods.includes(id)) {
      this.coachTransactionFoods.push(id);
    }
  }

  updateCoachDeletionFoods(id) {
    if (!this.coachDeletionFoods.includes(id)) {
      this.coachDeletionFoods.push(id);
    }
  }

  updateCoachTransactionNutritionPlans(id) {
    if (!this.coachTransactionNutritionPlans.includes(id)) {
      this.coachTransactionNutritionPlans.push(id);
    }
  }

  updateCoachDeletionNutritionPlans(id) {
    if (!this.coachDeletionNutritionPlans.includes(id)) {
      this.coachDeletionNutritionPlans.push(id);
    }
  }

  initAuxNutritionPlan(originalPlan) {
    const newAuxNutritionPlan = new NutritionPlan(
      uuidv4(),
      originalPlan ? originalPlan.name : undefined,
      originalPlan ? originalPlan.notes : undefined,
      originalPlan ? originalPlan.caloriesGoal : undefined,
      originalPlan ? originalPlan.carbsGoal : undefined,
      originalPlan ? originalPlan.proteinGoal : undefined,
      originalPlan ? originalPlan.fatsGoal : undefined,
      null,
      this
    );

    newAuxNutritionPlan.resetAuxGoals();

    if (originalPlan) {
      console.log("initAuxNutritionPlan if originalPlan");
      originalPlan.meals.forEach((meal) => {
        console.log("initAuxNutritionPlan meal: ", meal.order);
        const newMeal = new Meal(
          uuidv4(),
          meal.name,
          meal.notes,
          meal.order,
          meal.defaultFoodOption,
          newAuxNutritionPlan
        );

        meal.foodOptions.forEach((foodOption) => {
          console.log(
            "initAuxNutritionPlan meal: ",
            meal.order,
            "foodOption: ",
            foodOption.order
          );
          const newFoodOption = new FoodOption(
            uuidv4(),
            foodOption.order,
            newMeal
          );

          foodOption.foods.forEach((food) => {
            const newFoodToAdd = new Food(
              uuidv4(),
              food.name,
              food.order,
              food.quantity,
              food.calories,
              food.carbs,
              food.protein,
              food.fats,
              food.code,
              food.brand,
              food.store,
              newFoodOption
            );

            newFoodOption.foods.push(newFoodToAdd);
          });

          newMeal.foodOptions.push(newFoodOption);
        });

        newAuxNutritionPlan.meals.push(newMeal);
      });

      newAuxNutritionPlan.setProperty("originalPlanId", originalPlan.id);
    } else {
      const firstMeal = new Meal(
        uuidv4(),
        MealNames.BREAKFAST,
        undefined,
        undefined,
        undefined,
        newAuxNutritionPlan
      );

      const firstFoodOption = new FoodOption(uuidv4(), undefined, firstMeal);

      // firstFoodOption.setProperty("isSelected", true);

      firstMeal.foodOptions.push(firstFoodOption);

      newAuxNutritionPlan.meals.push(firstMeal);
    }

    this.auxNutritionPlan = newAuxNutritionPlan;
  }
  // #endregion

  async clientsDemoData(demoId = null, changeDemoClient = true) {
    const randInt = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    const demoClient = demoId
      ? this.clients.find((c) => c.id.toString() === demoId)
      : null;

    const clientsToChange = demoClient
      ? this.clients.filter((c) => c.id != demoClient.id)
      : this.clients;

    if (demoClient && changeDemoClient) {
      demoClient.setProperty("nameForCoach", "James");
      demoClient.setProperty("surnameForCoach", "Anderson");
      demoClient.setProperty("email", "jamesanderson@efortcoach.com");

      const getRandomDate = (startDate, endDate) => {
        const date = new Date(
          startDate.getTime() +
            Math.random() * (endDate.getTime() - startDate.getTime())
        );
        return new Date(date.setHours(0, 0, 0, 0)); // Ensure the time is set to midnight
      };

      // Function to generate unique random dates
      const generateUniqueRandomDates = (startDate, endDate, numDates) => {
        const dates = new Set();
        while (dates.size < numDates) {
          dates.add(
            getRandomDate(startDate, endDate).toISOString().split("T")[0]
          );
        }
        return Array.from(dates).map((date) => new Date(date));
      };

      // Function to generate random bodyweights with unique dates
      const generateRandomBodyweights = (
        numBodyweights,
        startWeight,
        endWeight,
        startDate,
        endDate
      ) => {
        const bodyweights = [];
        const dates = generateUniqueRandomDates(
          startDate,
          endDate,
          numBodyweights
        );

        let currentWeight = startWeight;
        const totalWeightChange = endWeight - startWeight;
        const averageDailyChange = totalWeightChange / numBodyweights;

        dates.forEach((date) => {
          const randomFluctuation = Math.random() * 0.2 - 0.1; // Random change between -0.1 and +0.1
          const dailyChange = averageDailyChange + randomFluctuation;
          currentWeight += dailyChange;
          bodyweights.push(
            new Bodyweight(undefined, currentWeight, date, demoClient)
          );
        });

        return bodyweights;
      };

      // Function to generate random Trac entries with unique dates
      const generateRandomTracs = (numTracs, startDate, endDate, client) => {
        const tracs = [];
        const dates = generateUniqueRandomDates(startDate, endDate, numTracs);

        dates.forEach((date) => {
          const legSoreness = Math.floor(Math.random() * 3) + 3;
          const pushSoreness = Math.floor(Math.random() * 3) + 3;
          const pullSoreness = Math.floor(Math.random() * 2) + 2;
          const motivation = Math.floor(Math.random() * 5) + 1;
          const perceivedRecovery = Math.floor(Math.random() * 3) + 2;
          const sleepQualityNutrition = Math.floor(Math.random() * 2) + 4;
          const technicalComfort = Math.floor(Math.random() * 3) + 3;

          tracs.push(
            new Trac(
              undefined,
              date,
              legSoreness,
              pushSoreness,
              pullSoreness,
              motivation,
              perceivedRecovery,
              sleepQualityNutrition,
              technicalComfort,
              client
            )
          );
        });

        return tracs;
      };

      const endDate = new Date();

      const startDate = new Date(endDate);
      startDate.setMonth(startDate.getMonth() - 10);

      const numBodyweights = 120;
      const numTracs = 120;

      const bodyweights = generateRandomBodyweights(
        numBodyweights,
        74.8,
        74,
        startDate,
        endDate
      );
      const tracs = generateRandomTracs(
        numTracs,
        startDate,
        endDate,
        demoClient
      );

      demoClient.setProperty("bodyweights", bodyweights);
      demoClient.setProperty("tracs", tracs);

      // Helper function to get the date of a specific weekday `weeksAgo` weeks ago
      const getDateOfSpecificWeekday = (weeksAgo, weekday) => {
        const today = new Date(); // Get today's date
        const currentDay = today.getDay(); // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
        const startOfWeek = new Date(today); // Create a new date object for the start of the week

        // Calculate the start of the week (Monday)
        startOfWeek.setDate(today.getDate() - currentDay + 1);
        if (currentDay === 0) {
          startOfWeek.setDate(startOfWeek.getDate() - 7); // Adjust if today is Sunday
        }

        // Calculate the date `weeksAgo` weeks ago
        startOfWeek.setDate(startOfWeek.getDate() - weeksAgo * 7);

        // Calculate the target date
        const targetDate = new Date(startOfWeek);
        targetDate.setDate(startOfWeek.getDate() + (weekday - 1));

        return targetDate;
      };

      const weekdays = [1, 3, 4, 6]; // 1 = Monday, 3 = Wednesday, 4 = Thursday, 6 = Saturday

      // Generate the workout dates
      let firstWorkoutDate = null;
      let lastWorkoutDate = null;

      demoClient.setProperty("blocks", []);

      const developBlockExercisePlan = [
        {
          workouts: [
            [
              {
                name: "400 Tempo Low Bar Squat",
                originalName: "squat-string",
                attributes: [
                  {
                    name: "LowBar-string",
                    title: "stanceGrip-string",
                    subtitle: "",
                  },
                  {
                    name: "400Tempo-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "Ramp 3x3 @6 + x3 @4",
                  "Ramp 3x3 @6.5 + x3 @4.5",
                  "Ramp 3x3 @7 + x3 @5",
                  "Ramp 3x3 @7 + x3 @5.5",
                  "Ramp 3x3 @7.5 + x3 @5.5",
                  "Ramp 3x3 @7.5 + x3 @6",
                  "Ramp 3x3 @8 + x3 @6",
                ],
                series: [
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [4, 4.5, 5, 5, 5.5, 5.5, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [170, 175, 180, 185, 190, 195, 200],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 5.5, 6, 6, 6.5, 6.5, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [180, 185, 190, 195, 200, 205, 210],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [6, 6.5, 7, 7, 7.5, 7.5, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [190, 195, 200, 205, 210, 215, 220],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [170, 175, 180, 185, 190, 195, 200],
                  },
                ],
              },
              {
                name: "2ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "x1 @5.5 + x4 @6.5 + 3x4 @5",
                  "x1 @5.5 + x4 @6.5 + 3x4 @5",
                  "x1 @6 + x4 @6.5 + 3x4 @5",
                  "x1 @6.5 + x4 @7 + 3x4 @6",
                  "x1 @6.5 + x4 @7.5 + 3x4 @6",
                  "x1 @7 + x4 @8 + 3x4 @6",
                  "x1 @8 + x4 @8.5 + 3x4 @6",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5.5, 5.5, 6, 6.5, 6.5, 7, 8],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [152.5, 155, 157.5, 160, 162.5, 162.5, 167.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6.5, 6.5, 6.5, 7, 7.5, 8, 8.5],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [145, 147.5, 150, 152.5, 152.5, 155, 157.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "1ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "x5 @5 + 2x5 @6",
                  "x5 @5 + 2x5 @6",
                  "x5 @5 + 2x5 @6",
                  "3x5 @6.5",
                  "3x5 @6.5",
                  "3x5 @6.5",
                  "3x5 @6.5",
                ],
                series: [
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [5, 5, 5, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x5 @6",
                  "Ramp 3x5 @6.5",
                  "Ramp 3x5 @7",
                  "Ramp 3x5 @7.5",
                  "Ramp 3x5 @7.5",
                  "Ramp 3x5 @8",
                  "Ramp 3x5 @8",
                ],
                series: [
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [210, 215, 220, 225, 230, 235, 240],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [220, 225, 230, 235, 240, 245, 250],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [230, 235, 240, 245, 250, 255, 260],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Low Bar Squat",
                originalName: "squat-string",
                attributes: [
                  {
                    name: "LowBar-string",
                    title: "stanceGrip-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: [
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @6 + x4 @7 + 2x4 @6",
                  "x4 @6 + x4 @7 + 2x4 @6",
                  "x4 @6 + x4 @7 + 2x4 @6",
                ],
                series: [
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [190, 192.5, 195, 197.5, 200, 202.5, 202.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6, 6, 6, 6, 7, 7, 7],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [195, 197.5, 200, 200, 202.5, 205, 210],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                  },
                ],
              },
              {
                name: "3ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "3ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.QUATERNARY_BENCH_PRESS,
                protocolText: [
                  "3x1 @5",
                  "3x1 @5.5",
                  "3x1 @6",
                  "3x1 @6.5",
                  "3x1 @6.5",
                  "3x1 @7",
                  "3x1 @7",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                ],
              },
              ,
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "2ct Pause Spoto Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: "rom-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "2x1 @6 + 3x3 @5",
                  "2x1 @7 + 3x3 @6",
                  "2x1 @7 + 3x3 @6",
                  "2x1 @7.5 + 3x3 @6",
                  "2x1 @7.5 + 3x3 @6",
                  "2x1 @8 + 3x3 @6",
                  "2x1 @8 + 3x3 @6",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                ],
              },
              {
                name: "Conventional Deadlift",
                originalName: "conventionalDeadlift-string",
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x4 @6",
                  "Ramp 3x4 @6.5",
                  "Ramp 3x4 @7",
                  "Ramp 3x4 @7.5",
                  "Ramp 3x4 @7.5",
                  "Ramp 3x4 @8",
                  "Ramp 3x4 @8",
                ],
                series: [
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 185, 187.5, 190, 195, 200, 200],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [190, 195, 197.5, 200, 205, 210, 210],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [200, 205, 207.5, 210, 215, 220, 220],
                  },
                ],
              },
              {
                name: "Cable Single Arm Pull Down",
                originalName: "pullDown-string",
                attributes: [
                  {
                    name: "Cable-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  {
                    name: "SingleArm-string",
                    title: "other-string",
                    subtitle: "",
                  },
                ],
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Row",
                originalName: "row-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                ],
              },
              {
                name: "Tricep Extensions",
                originalName: "tricepExtensions-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                ],
              },
            ],
          ],
        },
        // Repeat this structure for weeks 2 to 7
      ];

      const developBlock = new Block(
        uuidv4(),
        "Develop Block 1",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        6, // totalWeeks
        6,
        "Develop Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 5; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              36 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              developBlock,
              []
            );

            developBlockExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        0, // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        undefined,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            developBlock.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(developBlock);

      const buildBlockExercisePlan = [
        {
          workouts: [
            [
              {
                name: "400 Tempo Low Bar Squat",
                originalName: "squat-string",
                attributes: [
                  {
                    name: "LowBar-string",
                    title: "stanceGrip-string",
                    subtitle: "",
                  },
                  {
                    name: "400Tempo-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],

                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "Ramp 3x3 @6",
                  "Ramp 3x3 @6.5",
                  "Ramp 3x3 @7",
                  "Ramp 3x3 @7",
                  "Ramp 3x3 @7.5",
                  "Ramp 3x3 @7.5",
                  "Ramp 3x3 @8",
                ],
                series: [
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [170, 175, 180, 185, 190, 195, 200],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [4.5, 5, 5.5, 6, 6, 6.5, 6.5],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [180, 185, 190, 195, 200, 205, 210],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [190, 195, 200, 205, 210, 215, 220],
                  },
                ],
              },
              {
                name: "Low Bar Squat",
                originalName: "squat-string",
                attributes: [
                  {
                    name: "LowBar-string",
                    title: "stanceGrip-string",
                    subtitle: "",
                  },
                  ,
                ],

                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "x3 @4",
                  "x3 @4.5",
                  "x3 @5",
                  "x3 @5.5",
                  "x3 @5.5",
                  "x3 @6",
                  "x3 @6",
                ],
                isSecondaryProtocol: true,
                series: [
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [170, 175, 180, 185, 190, 195, 200],
                  },
                ],
              },
              {
                name: "2ct Pause Bench Press",
                originalName: "benchPress-string",

                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "x1 @5.5 + x4 @6.5 + 3x4 @5",
                  "x1 @5.5 + x4 @6.5 + 3x4 @5",
                  "x1 @6 + x4 @6.5 + 3x4 @5",
                  "x1 @6.5 + x4 @7 + 3x4 @6",
                  "x1 @6.5 + x4 @7.5 + 3x4 @6",
                  "x1 @7 + x4 @8 + 3x4 @6",
                  "x1 @8 + x4 @8.5 + 3x4 @6",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5.5, 5.5, 6, 6.5, 6.5, 7, 8],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [152.5, 155, 157.5, 160, 162.5, 162.5, 167.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6.5, 6.5, 6.5, 7, 7.5, 8, 8.5],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [145, 147.5, 150, 152.5, 152.5, 155, 157.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "1ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "x5 @5 + 2x5 @6",
                  "x5 @5 + 2x5 @6",
                  "x5 @5 + 2x5 @6",
                  "3x5 @6.5",
                  "3x5 @6.5",
                  "3x5 @6.5",
                  "3x5 @6.5",
                ],
                series: [
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [5, 5, 5, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x5 @6",
                  "Ramp 3x5 @6.5",
                  "Ramp 3x5 @7",
                  "Ramp 3x5 @7.5",
                  "Ramp 3x5 @7.5",
                  "Ramp 3x5 @8",
                  "Ramp 3x5 @8",
                ],
                series: [
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [210, 215, 220, 225, 230, 235, 240],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [220, 225, 230, 235, 240, 245, 250],
                  },
                  {
                    targetReps: [5, 5, 5, 5, 5, 5, 5],
                    targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [230, 235, 240, 245, 250, 255, 260],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Low Bar Squat",
                originalName: "squat-string",
                attributes: [
                  {
                    name: "LowBar-string",
                    title: "stanceGrip-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: [
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @5 + x4 @6 + 2x4 @5",
                  "x4 @6 + x4 @7 + 2x4 @6",
                  "x4 @6 + x4 @7 + 2x4 @6",
                  "x4 @6 + x4 @7 + 2x4 @6",
                ],
                series: [
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [190, 192.5, 195, 197.5, 200, 202.5, 202.5],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6, 6, 6, 6, 7, 7, 7],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [195, 197.5, 200, 200, 202.5, 205, 210],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                  },
                ],
              },
              {
                name: "3ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "3ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.QUATERNARY_BENCH_PRESS,
                protocolText: [
                  "3x1 @5",
                  "3x1 @5.5",
                  "3x1 @6",
                  "3x1 @6.5",
                  "3x1 @6.5",
                  "3x1 @7",
                  "3x1 @7",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                  },
                ],
              },
              ,
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "2ct Pause Spoto Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: "rom-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "2x1 @6 + 3x3 @5",
                  "2x1 @7 + 3x3 @6",
                  "2x1 @7 + 3x3 @6",
                  "2x1 @7.5 + 3x3 @6",
                  "2x1 @7.5 + 3x3 @6",
                  "2x1 @8 + 3x3 @6",
                  "2x1 @8 + 3x3 @6",
                ],
                series: [
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                  },
                  {
                    targetReps: [1, 1, 1, 1, 1, 1, 1],
                    targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                  {
                    targetReps: [3, 3, 3, 3, 3, 3, 3],
                    targetRPE: [5, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                  },
                ],
              },
              {
                name: "Conventional Deadlift",
                originalName: "conventionalDeadlift-string",
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x4 @6",
                  "Ramp 3x4 @6.5",
                  "Ramp 3x4 @7",
                  "Ramp 3x4 @7.5",
                  "Ramp 3x4 @7.5",
                  "Ramp 3x4 @8",
                  "Ramp 3x4 @8",
                ],
                series: [
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [180, 185, 187.5, 190, 195, 200, 200],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                    relevance: [2, 2, 2, 2, 2, 2, 2],
                    weight: [190, 195, 197.5, 200, 205, 210, 210],
                  },
                  {
                    targetReps: [4, 4, 4, 4, 4, 4, 4],
                    targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                    relevance: [1, 1, 1, 1, 1, 1, 1],
                    weight: [200, 205, 207.5, 210, 215, 220, 220],
                  },
                ],
              },
              {
                name: "Cable Single Arm Pull Down",
                originalName: "pullDown-string",
                attributes: [
                  {
                    name: "Cable-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  {
                    name: "SingleArm-string",
                    title: "other-string",
                    subtitle: "",
                  },
                ],
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 65, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Row",
                originalName: "row-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [70, 70, 70, 75, 75, 75, 75],
                  },
                ],
              },
              {
                name: "Tricep Extensions",
                originalName: "tricepExtensions-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [35, 35, 35, 35, 35, 35, 35],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 16, 18],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [14, 14, 14, 14, 14, 14, 16],
                  },
                ],
              },
            ],
          ],
        },
        // Repeat this structure for weeks 2 to 7
      ];

      const developBlock2 = new Block(
        uuidv4(),
        "Develop Block 2",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        5, // totalWeeks
        5,
        "Develop Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 4; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              30 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              developBlock2,
              []
            );

            buildBlockExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        0, // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        undefined,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            developBlock2.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(developBlock2);

      const buildBlockJuniorExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 3x4 @6 + 2x4x180",
                  "Ramp 2x4 @6.5 + 3x4x132.5",
                  "Ramp 2x4 @7 + 3x4x135",
                  "Ramp 2x4 @7.5 + 3x4x140",
                  "Ramp 2x4 @7.5 + 3x4x142.5",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [4, 5.5, 6, 6.5, 6.5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [120, 135, 135, 145, 147.5],
                    rpe: [4, 5.5, 6, 6.5, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [5, 6.5, 7, 7.5, 7.5],
                    relevance: [2, 1, 1, 1, 1, 1, 1],
                    weight: [130, 142.5, 147.5, 152.5, 157.5],
                    rpe: [5, 6.5, 7, 7.5, 7.5],
                  },
                  {
                    targetWeight: [0, 132.5, 135, 140, 142.5],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [6, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [137.5, 132.5, 135, 140, 142.5],
                    rpe: [6, 4, 4, 4, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140, 142.5],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140, 142.5],
                    rpe: [4, 4, 4, 4, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140, 142.5],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140, 142.5],
                    rpe: [4, 4, 4, 4, 5],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: [
                  "4x3 @5",
                  "4x3 @5",
                  "4x3 @5",
                  "4x3 @5",
                  "4x3 @5",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5, 5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [135, 137.5, 140, 142.5, 145],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5, 5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [135, 137.5, 140, 142.5, 145],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5, 5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [135, 137.5, 140, 142.5, 145],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5, 5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [135, 137.5, 140, 142.5, 145],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x4 @5 + single @5",
                  "Ramp 3x4 @5 + single @5",
                  "Ramp 3x4 @6 + single @5",
                  "Ramp 3x4 @6 + single @5",
                  "Ramp 3x4 @7 + single @6",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [3, 3, 4, 4, 5],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [195, 200, 205, 215, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [4, 4, 5, 5, 6],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [205, 210, 220, 225, 230],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 6, 6, 7],
                    relevance: [1, 1, 1, 1, 1],
                    weight: [215, 225, 235, 235, 245],
                  },
                  {
                    targetWeight: ["+10kg", "+10kg", "+10kg", "+10kg", "+10kg"],
                    targetReps: [1, 1, 1, 1, 1],
                    targetRPE: [5, 5, 5, 5, 6],
                    relevance: [1, 1, 1, 1, 1],
                    weight: [225, 235, 245, 245, 255],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x5x127.5 + 4x7x117.5",
                  "1x5x130 + 4x7x120",
                  "1x5x135 + 4x7x122.5",
                  "1x5x140 + 4x7x125",
                  "1x5x142.5 + 1x7x130 + 3x7x120",
                ],
                series: [
                  {
                    targetWeight: [127.5, 130, 135, 140, 142.5],
                    targetReps: [5, 5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [127.5, 130, 135, 140, 142.5],
                    rpe: [4, 5, 4.5, 5, 5],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125, 130],
                    targetReps: [7, 7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125, 130],
                    rpe: [4, 4, 4, 4.5, 5],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125, 120],
                    targetReps: [7, 7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125, 120],
                    rpe: [4, 4, 4, 4.5, 4],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125, 120],
                    targetReps: [7, 7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125, 120],
                    rpe: [4, 4, 4, 4.5, 4],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125, 120],
                    targetReps: [7, 7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125, 120],
                    rpe: [4, 4, 4, 4.5, 4],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "Ramp 2x4 @5 + x2 @4 + 2x4x180",
                  "Ramp 2x4 @5 + x2 @5 + 2x4x185",
                  "Ramp 2x4 @6 + x2 @5 + 2x4x190",
                  "Ramp 2x4 @7 + x2 @6.5 + 2x4x195",
                  "Ramp 2x4 @7.5 + 3x4x200",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [4, 4, 5, 6, 6.5],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [195, 200, 205, 210, 215],
                    rpe: [4, 4, 5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [5, 5, 6, 7, 7.5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [205, 210, 215, 220, 230],
                    rpe: [5, 5, 6, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 200],
                    targetReps: [2, 2, 2, 2, 4],
                    targetRPE: [4, 5, 5, 6.5, 0],
                    relevance: [1, 1, 1, 1, 2],
                    weight: [215, 220, 225, 230, 200],
                    rpe: [4, 5, 5, 6.5, 4],
                  },
                  {
                    targetWeight: [180, 185, 190, 195, 200],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [180, 185, 190, 195, 200],
                    rpe: [3, 3, 4, 5, 4],
                  },
                  {
                    targetWeight: [180, 185, 190, 195, 200],
                    targetReps: [4, 4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [180, 185, 190, 195, 200],
                    rpe: [3, 3, 4, 5, 4],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x2 @6 + x1 @4 + 4x3x130",
                  "Ramp 2x2 @6.5 + x1 @4 + 4x3x135",
                  "Ramp 2x2 @7 + x1 @5 + 4x3x137.5",
                  "Ramp 2x2 @7 + x1 @5 + 4x3x142.5",
                  "Ramp 2x2 @7.5 + x1 @6 + 4x3x145",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2, 2],
                    targetRPE: [5, 5.5, 6, 6, 6.5],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [135, 140, 147.5, 152.5, 155],
                    rpe: [5, 5.5, 6, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2, 2],
                    targetRPE: [6, 6.5, 7, 7, 7.5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [145, 150, 155, 160, 170],
                    rpe: [6, 6.5, 7, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1, 1],
                    targetRPE: [4, 4, 5, 5, 6],
                    relevance: [1, 1, 1, 1, 1],
                    weight: [150, 155, 160, 165, 170],
                    rpe: [4, 4, 5, 5, 6],
                  },
                  {
                    targetWeight: [130, 135, 137.5, 142.5, 145],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [130, 135, 137.5, 142.5, 145],
                    rpe: [4, 3, 4.5, 4, 4],
                  },
                  {
                    targetWeight: [130, 135, 137.5, 142.5, 145],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [130, 135, 137.5, 142.5, 145],
                    rpe: [4, 3, 4.5, 4, 4],
                  },
                  {
                    targetWeight: [130, 135, 137.5, 142.5, 145],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [130, 135, 137.5, 142.5, 145],
                    rpe: [4, 3, 4.5, 4, 4],
                  },
                  {
                    targetWeight: [130, 135, 137.5, 142.5, 145],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [130, 135, 137.5, 142.5, 145],
                    rpe: [4, 3, 4.5, 4, 4],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "Ramp 2x2 @5 + x1 @5 + 3x3x210",
                  "Ramp 2x2 @6 + x1 @5 + 3x3x230",
                  "Ramp 2x2 @6 + x1 @5 + 3x3x230",
                  "Ramp 2x2 @7 + x1 @6 + 3x3x235",
                  "Ramp 2x2 @7.5 + x1 @6 + 3x3x220",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2, 2],
                    targetRPE: [4, 5, 5, 6, 6.5],
                    relevance: [3, 3, 3, 3, 3],
                    weight: [220, 230, 235, 245, 260],
                    rpe: [4, 5, 5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2, 2],
                    targetRPE: [5, 6, 6, 7, 7.5],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [230, 240, 250, 260, 275],
                    rpe: [5, 6, 6, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1, 1],
                    targetRPE: [5, 5, 5, 6, 6],
                    relevance: [1, 1, 1, 1, 1],
                    weight: [245, 255, 265, 275, 280],
                    rpe: [5, 5, 5, 6, 6],
                  },
                  {
                    targetWeight: [210, 230, 230, 235, 220],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [210, 230, 230, 235, 220],
                    rpe: [3, 4.5, 4, 4, 4],
                  },
                  {
                    targetWeight: [210, 230, 230, 235, 220],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [210, 230, 230, 235, 220],
                    rpe: [3, 4.5, 4, 4, 4],
                  },
                  {
                    targetWeight: [210, 230, 230, 235, 220],
                    targetReps: [3, 3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2],
                    weight: [210, 230, 230, 235, 220],
                    rpe: [3, 4.5, 4, 4, 4],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const buildBlockJunior = new Block(
        uuidv4(),
        "Build Block 1",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        5, // totalWeeks
        5,
        "Build Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 4; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              25 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              buildBlockJunior,
              []
            );

            buildBlockJuniorExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            buildBlockJunior.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(buildBlockJunior);

      const buildBlock2JuniorExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x4 @6 + 3x4x132.5",
                  "Ramp 2x4 @6.5 + 3x4x135",
                  "Ramp 2x4 @7.5 + 3x4x140",
                  "x4 @8 + 4x4x145",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 6.5, 8],
                    relevance: [3, 3, 3, 3, 3, 1],
                    weight: [132.5, 140, 147.5, 160],
                    rpe: [5, 5.5, 6.5, 8],
                  },
                  {
                    targetWeight: [0, 0, 0, 145],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [6, 6.5, 7.5, 0],
                    relevance: [2, 2, 2, 2, 2, 2],
                    weight: [145, 150, 155, 145],
                    rpe: [6, 6.5, 7.5, 5],
                  },
                  {
                    targetWeight: [132.5, 135, 140, 145],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2, 2],
                    weight: [132.5, 135, 140, 145],
                    rpe: [4, 4, 5, 5],
                  },
                  {
                    targetWeight: [132.5, 135, 140, 145],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2, 2],
                    weight: [132.5, 135, 140, 145],
                    rpe: [4, 4, 5, 5],
                  },
                  {
                    targetWeight: [132.5, 135, 140, 145],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2, 2, 2],
                    weight: [132.5, 135, 140, 145],
                    rpe: [4, 4, 5, 5],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["4x3 @5", "4x3 @5", "4x3 @5", "4x3 @5.5"],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [205, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [205, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [205, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [205, 205, 210, 215],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x4 @5 + single @5",
                  "Ramp 3x4 @5.5 + single @5",
                  "Ramp 3x4 @6 + single @6",
                  "Ramp 3x4 @6.5 + single @6.5",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [3, 3.5, 4, 4.5],
                    relevance: [3, 3, 3, 3],
                    weight: [205, 220, 220, 225],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [4, 4.5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [215, 225, 235, 240],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 6, 6.5],
                    relevance: [1, 1, 1, 1],
                    weight: [225, 240, 250, 255],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [5, 5, 6, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [240, 250, 260, 270],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x5x130 + 4x7x120",
                  "1x5x135 + 1x7x125 + 3x7x120",
                  "1x5x140 + 4x7x125",
                  "1x5x145 + 1x7x130 + 3x7x120",
                ],
                series: [
                  {
                    targetWeight: [130, 135, 140, 145],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [130, 135, 140, 145],
                    rpe: [4, 4, 5, 6.5],
                  },
                  {
                    targetWeight: [120, 125, 125, 130],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 2, 3, 2],
                    weight: [120, 125, 125, 130],
                    rpe: [4, 5, 4, 5],
                  },
                  {
                    targetWeight: [120, 120, 125, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [120, 120, 125, 120],
                    rpe: [4, 4, 4, 4],
                  },
                  {
                    targetWeight: [120, 120, 125, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [120, 120, 125, 120],
                    rpe: [4, 4, 4, 4],
                  },
                  {
                    targetWeight: [120, 120, 125, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [120, 120, 125, 120],
                    rpe: [4, 4, 4, 4],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "x4 @5 + Single @5 + 3x4x185",
                  "x4 @6.5 + Single @6 + 3x4x190",
                  "x4 @7.5 + Single @6 + 3x4x190",
                  "x3 @8.5 + 4x4x200",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 3],
                    targetRPE: [5, 6.5, 7.5, 8.5],
                    relevance: [2, 2, 2, 1],
                    weight: [210, 215, 222.5, 240],
                    rpe: [5, 6.5, 7.5, 9],
                  },
                  {
                    targetWeight: [0, 0, 0, 200],
                    targetReps: [1, 1, 1, 4],
                    targetRPE: [5, 6, 6, 0],
                    relevance: [1, 1, 1, 3],
                    weight: [220, 225, 235, 200],
                    rpe: [5, 6, 6, 4.5],
                  },
                  {
                    targetWeight: [185, 190, 190, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 190, 200],
                    rpe: [4, 4, 4, 4.5],
                  },
                  {
                    targetWeight: [185, 190, 190, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 190, 200],
                    rpe: [4, 4, 4, 4.5],
                  },
                  {
                    targetWeight: [185, 190, 190, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 190, 200],
                    rpe: [4, 4, 4, 4.5],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "x2 @6 + Single @4 + 5x3x135",
                  "x2 @7 + Single @6 + 5x3x140",
                  "x2 @7.5 + Single @5 + 5x3x145",
                  "Single @7.5 + 6x3x150",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 1],
                    targetRPE: [6, 7, 7.5, 7.5],
                    relevance: [2, 2, 2, 1],
                    weight: [150, 157.5, 162.5, 177.5],
                    rpe: [6, 7, 7.5, 7],
                  },
                  {
                    targetWeight: [0, 0, 0, 150],
                    targetReps: [1, 1, 1, 3],
                    targetRPE: [4, 6, 5, 0],
                    relevance: [1, 1, 1, 3],
                    weight: [160, 167.5, 172.5, 150],
                    rpe: [4, 6, 5, 6],
                  },
                  {
                    targetWeight: [135, 140, 145, 150],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 150],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 150],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 150],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 150],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 150],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 150],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 150],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 150],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 150],
                    rpe: [3, 4, 4, 6],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "x3 @5 + Single @4 + 4x3x225",
                  "x3 @6.5 + Single @5 + 4x3x235",
                  "x3 @7.5 + Single @6 + 4x3x245",
                  "1x1x295 + Single @8 + 4x3x255",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 295],
                    targetReps: [3, 3, 3, 1],
                    targetRPE: [5, 6.5, 7.5, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [240, 255, 270, 295],
                    rpe: [5, 6.5, 7.5, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 6, 8],
                    relevance: [1, 1, 1, 1],
                    weight: [255, 270, 285, 305],
                    rpe: [4, 5, 6.5, 8],
                  },
                  {
                    targetWeight: [225, 235, 245, 255],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [225, 235, 245, 255],
                    rpe: [3, 4, 4, 4.5],
                  },
                  {
                    targetWeight: [225, 235, 245, 255],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [225, 235, 245, 255],
                    rpe: [3, 4, 4, 4.5],
                  },
                  {
                    targetWeight: [225, 235, 245, 255],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [225, 235, 245, 255],
                    rpe: [3, 4, 4, 4.5],
                  },
                  {
                    targetWeight: [225, 235, 245, 255],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [225, 235, 245, 255],
                    rpe: [3, 4, 4, 4.5],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const buildBlock2Junior = new Block(
        uuidv4(),
        "Build Block 2",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        4, // totalWeeks
        4,
        "Build Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 3; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              20 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              buildBlock2Junior,
              []
            );

            buildBlock2JuniorExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            buildBlock2Junior.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(buildBlock2Junior);

      const meetBlock1ExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 3x4 @6 + 2x4 @5",
                  "Ramp 2x4 @6.5 + 3x4x135",
                  "Ramp 2x4 @7 + 3x4x137.5",
                  "Ramp 2x4 @7.5 + 3x4x140",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [4, 5.5, 6, 6.5],
                    relevance: [3, 2, 2, 2],
                    weight: [120, 135, 140, 147.5],
                    rpe: [4, 5.5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 6.5, 7, 7.5],
                    relevance: [2, 1, 1, 1],
                    weight: [130, 145, 150, 155],
                    rpe: [5, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 135, 137.5, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [6, 0, 0, 0],
                    relevance: [1, 3, 3, 3],
                    weight: [140, 135, 137.5, 140],
                    rpe: [6, 4, 4, 5],
                  },
                  {
                    targetWeight: [0, 135, 137.5, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 0, 0, 0],
                    relevance: [2, 3, 3, 3],
                    weight: [130, 135, 137.5, 140],
                    rpe: [5, 4, 4, 5],
                  },
                  {
                    targetWeight: [0, 135, 137.5, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 0, 0, 0],
                    relevance: [2, 3, 3, 3],
                    weight: [130, 135, 137.5, 140],
                    rpe: [5, 4, 4, 5],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["4x3 @5", "4x3 @5", "4x3 @5", "4x3 @5"],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                attributes: [
                  {
                    name: "Low2ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x4 @5 + Single @5",
                  "Ramp 3x4 @5 + Single @5",
                  "Ramp 3x4 @6 + Single @6",
                  "Ramp 3x5 @6.5 + Single @6",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [3, 3, 4, 4.5],
                    relevance: [3, 3, 3, 3],
                    weight: [200, 200, 210, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [4, 4, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [210, 215, 225, 235],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5, 6, 6.5],
                    relevance: [1, 1, 1, 1],
                    weight: [220, 230, 240, 250],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [5, 5, 6, 6],
                    relevance: [2, 2, 2, 2],
                    weight: [235, 245, 255, 265],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x4x125 + 4x6x115",
                  "1x4x130 + 1x6x120 + 3x6x115",
                  "1x4x132.5 + 1x6x122.5 + 3x6x117.5",
                  "1x4x135 + 1x6x125 + 3x6x120",
                ],
                series: [
                  {
                    targetWeight: [125, 130, 132.5, 135],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [125, 130, 132.5, 135],
                    rpe: [4, 4, 5, 4],
                  },
                  {
                    targetWeight: [115, 120, 122.5, 125],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 2, 2, 2],
                    weight: [115, 120, 122.5, 125],
                    rpe: [4, 2, 3, 3],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [4, 2, 3, 2],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [4, 2, 3, 2],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [4, 2, 3, 2],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "Ramp 2x4 @5.5 + Single @6 + 3x4x185",
                  "Ramp 2x4 @6.5 + Single @6 + 3x4x190",
                  "Ramp 2x4 @7 + Single @6 + 3x4x195",
                  "Ramp 2x4 @7.5 + Single @6.5 + 3x4x200",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [4.5, 5.5, 6, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [190, 202.5, 207.5, 215],
                    rpe: [4.5, 5.5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5.5, 6.5, 7, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [205, 212.5, 220, 225],
                    rpe: [5.5, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [6, 6, 6, 6.5],
                    relevance: [1, 1, 1, 1],
                    weight: [220, 227.5, 235, 240],
                    rpe: [6, 6, 6, 6.5],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [4, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [4, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [4, 3, 3, 3],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "2x2 @5.5 + Single @5 + 3x3x132.5",
                  "2x2 @6.5 + Single @5 + 3x3x137.5",
                  "2x2 @7 + Single @5.5 + 3x3x140",
                  "2x2 @7.5 + Single @8 + 3x2x145",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5.5, 6.5, 7, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [150, 155, 160, 165],
                    rpe: [5.5, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5.5, 6.5, 7, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [150, 155, 160, 165],
                    rpe: [5.5, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [5, 5, 5.5, 8],
                    relevance: [2, 2, 2, 1],
                    weight: [155, 160, 165, 175],
                    rpe: [5, 5, 5.5, 9],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [3, 3, 3, 4],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [3, 3, 3, 4],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [3, 3, 3, 4],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "x2 @5 + Single @6 + 3x3x220",
                  "x2 @6 + Single @5 + 3x3x230",
                  "x2 @7 + Single @5 + 3x3x235",
                  "Ramp 2x2 @7.5 + 3x2x245",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5, 6, 7, 6.5],
                    relevance: [1, 1, 1, 2],
                    weight: [240, 250, 260, 265],
                    rpe: [5, 6, 7, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 2],
                    targetRPE: [6, 5, 5, 7.5],
                    relevance: [2, 2, 2, 1],
                    weight: [255, 265, 275, 280],
                    rpe: [6, 5, 5, 7.5],
                  },
                  {
                    targetWeight: [220, 230, 235, 245],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 230, 235, 245],
                    rpe: [5, 4, 3, 3],
                  },
                  {
                    targetWeight: [220, 230, 235, 245],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 230, 235, 245],
                    rpe: [5, 4, 3, 3],
                  },
                  {
                    targetWeight: [220, 230, 235, 245],
                    targetReps: [3, 3, 3, 2],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 230, 235, 245],
                    rpe: [5, 4, 3, 3],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const meetBlock1 = new Block(
        uuidv4(),
        "Build Block 1",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        4, // totalWeeks
        4,
        "Build Macro 2",
        new Date()
      );

      runInAction(() => {
        for (let week = 3; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              16 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              meetBlock1,
              []
            );

            meetBlock1ExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            meetBlock1.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(meetBlock1);

      const meetBlock2ExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x3 @6 + 3x3x132.5",
                  "Ramp 2x3 @6.5 + 3x3x135",
                  "Ramp 2x3 @7 + 3x3x137.5",
                  "Ramp 2x3 @7.5 + 3x3x140",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5.5, 6, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [132.5, 135, 140, 145],
                    rpe: [5, 5.5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [6, 6.5, 7, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [137.5, 142.5, 147.5, 152.5],
                    rpe: [6, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [132.5, 135, 137.5, 140],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 135, 137.5, 140],
                    rpe: [4, 3, 3, 3],
                  },
                  {
                    targetWeight: [132.5, 135, 137.5, 140],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 135, 137.5, 140],
                    rpe: [4, 3, 3, 3],
                  },
                  {
                    targetWeight: [132.5, 135, 137.5, 140],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 135, 137.5, 140],
                    rpe: [4, 3, 3, 3],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["4x4 @5", "4x4 @5", "4x4 @5", "4x3 @5"],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5, 5, 5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 220],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                attributes: [
                  {
                    name: "Low2ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x5 @5 + Single @5",
                  "Ramp 3x5 @5.5 + Single @5",
                  "Ramp 3x5 @6 + Single @5",
                  "Ramp 3x4 @6.5 + Single @5",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [3, 3.5, 4, 4.5],
                    relevance: [3, 3, 3, 3],
                    weight: [205, 205, 220, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [4, 4.5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [215, 220, 232.5, 235],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 5.5, 6, 6.5],
                    relevance: [1, 1, 1, 1],
                    weight: [225, 235, 245, 250],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [5, 5, 6, 6],
                    relevance: [2, 2, 2, 2],
                    weight: [240, 250, 255, 275],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x5x125 + 4x7x115",
                  "1x5x127.5 + 1x7x120 + 3x7x115",
                  "1x5x132.5 + 1x7x122.5 + 3x7x117.5",
                  "1x5x135 + 1x7x125 + 3x7x120",
                ],
                series: [
                  {
                    targetWeight: [125, 127.5, 132.5, 135],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [125, 127.5, 132.5, 135],
                    rpe: [3, 4, 3, 4],
                  },
                  {
                    targetWeight: [115, 120, 122.5, 125],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 2, 2, 2],
                    weight: [115, 120, 122.5, 125],
                    rpe: [2, 3, 3, 3],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [2, 3, 3, 2],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [2, 3, 3, 2],
                  },
                  {
                    targetWeight: [115, 115, 117.5, 120],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 115, 117.5, 120],
                    rpe: [2, 3, 3, 2],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "x3 @5 + Single @4 + 3x3x185",
                  "x3 @6 + Single @5 + 3x3x190",
                  "x3 @6.5 + Single @5 + 3x3x195",
                  "1x1x232.5 + Single @7 + 3x3x200",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 232.5],
                    targetReps: [3, 3, 3, 1],
                    targetRPE: [5, 6, 6.5, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [210, 217.5, 225, 232.5],
                    rpe: [5, 6, 5, 5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 5, 7],
                    relevance: [1, 1, 1, 1],
                    weight: [220, 227.5, 235, 247.5],
                    rpe: [4, 5, 5, 7],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [3, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [3, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 200],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 200],
                    rpe: [3, 3, 3, 3],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x2 @6 + Single @4 + 3x3x132.5",
                  "Ramp 2x2 @6.5 + Single @5 + 3x3x137.5",
                  "Ramp 2x2 @7 + Single @6 + 3x3x140",
                  "1x1x165 + 1x1x172.5 + 4x3x145",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 165],
                    targetReps: [2, 2, 2, 1],
                    targetRPE: [5, 5.5, 6, 0],
                    relevance: [2, 2, 2, 1],
                    weight: [140, 147.5, 152.5, 165],
                    rpe: [5, 5.5, 6, 6],
                  },
                  {
                    targetWeight: [0, 0, 0, 172.5],
                    targetReps: [2, 2, 2, 1],
                    targetRPE: [6, 6.5, 7, 0],
                    relevance: [1, 1, 1, 1],
                    weight: [150, 155, 160, 172.5],
                    rpe: [6, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 145],
                    targetReps: [1, 1, 1, 3],
                    targetRPE: [4, 5, 6, 0],
                    relevance: [1, 1, 1, 3],
                    weight: [157.5, 162.5, 167.5, 145],
                    rpe: [4, 5, 6, 4],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [1, 3, 1, 4],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [1, 3, 1, 3],
                  },
                  {
                    targetWeight: [132.5, 137.5, 140, 145],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [132.5, 137.5, 140, 145],
                    rpe: [1, 3, 1, 3],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "x2 @5 + Single @4 + 3x3x220",
                  "x2 @6 + Single @4 + 3x3x225",
                  "x2 @6.5 + Single @5 + 3x3x230",
                  "1x1x285 + 4x3x235",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 285],
                    targetReps: [2, 2, 2, 1],
                    targetRPE: [5, 6, 6.5, 0],
                    relevance: [1, 1, 1, 1],
                    weight: [245, 255, 265, 285],
                    rpe: [5, 4, 5, 5],
                  },
                  {
                    targetWeight: [0, 0, 0, 235],
                    targetReps: [1, 1, 1, 3],
                    targetRPE: [4, 4, 5, 0],
                    relevance: [1, 1, 1, 3],
                    weight: [270, 270, 275, 235],
                    rpe: [4, 4, 5, 3],
                  },
                  {
                    targetWeight: [220, 225, 230, 235],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 225, 230, 235],
                    rpe: [2, 1, 2, 3],
                  },
                  {
                    targetWeight: [220, 225, 230, 235],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 225, 230, 235],
                    rpe: [2, 1, 1, 3],
                  },
                  {
                    targetWeight: [220, 225, 230, 235],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 225, 230, 235],
                    rpe: [2, 1, 1, 3],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const meetBlock2 = new Block(
        uuidv4(),
        "Build Block 2",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        4, // totalWeeks
        4,
        "Build Macro 2",
        new Date()
      );

      runInAction(() => {
        for (let week = 3; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(
              12 - week,
              weekdays[day]
            );

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              meetBlock2,
              []
            );

            meetBlock2ExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            meetBlock2.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(meetBlock2);

      const deloadBlockExercisePlan = [
        {
          workouts: [
            [
              {
                name: "2ct Pause Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: ["4x3x135"],
                series: [
                  {
                    targetWeight: [135],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [135],
                    rpe: [3],
                  },
                  {
                    targetWeight: [135],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [135],
                    rpe: [3],
                  },
                  {
                    targetWeight: [135],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [135],
                    rpe: [3],
                  },
                  {
                    targetWeight: [135],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [135],
                    rpe: [3],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["3x2 @5"],
                series: [
                  {
                    targetWeight: [0],
                    targetReps: [2],
                    targetRPE: [5],
                    relevance: [2],
                    weight: [190],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [2],
                    targetRPE: [5],
                    relevance: [2],
                    weight: [190],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [2],
                    targetRPE: [5],
                    relevance: [2],
                    weight: [190],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: ["Ramp 3x6 @5"],
                series: [
                  {
                    targetWeight: [0],
                    targetReps: [6],
                    targetRPE: [3],
                    relevance: [3],
                    weight: [200],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [6],
                    targetRPE: [4],
                    relevance: [3],
                    weight: [210],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [6],
                    targetRPE: [5],
                    relevance: [3],
                    weight: [220],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: ["4x5x120"],
                series: [
                  {
                    targetWeight: [120],
                    targetReps: [5],
                    targetRPE: [0],
                    relevance: [2],
                    weight: [120],
                    rpe: [4],
                  },
                  {
                    targetWeight: [120],
                    targetReps: [5],
                    targetRPE: [0],
                    relevance: [2],
                    weight: [120],
                    rpe: [4],
                  },
                  {
                    targetWeight: [120],
                    targetReps: [5],
                    targetRPE: [0],
                    relevance: [2],
                    weight: [120],
                    rpe: [4],
                  },
                  {
                    targetWeight: [120],
                    targetReps: [5],
                    targetRPE: [0],
                    relevance: [2],
                    weight: [120],
                    rpe: [4],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: ["3x3x185 + Single @5"],
                series: [
                  {
                    targetWeight: [185],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [185],
                    rpe: [4],
                  },
                  {
                    targetWeight: [185],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [185],
                    rpe: [4],
                  },
                  {
                    targetWeight: [185],
                    targetReps: [3],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [185],
                    rpe: [4],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [1],
                    targetRPE: [5],
                    relevance: [3],
                    weight: [205],
                    rpe: [5],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: ["2x1 @5 + 4x2x130"],
                series: [
                  {
                    targetWeight: [0],
                    targetReps: [1],
                    targetRPE: [5],
                    relevance: [2],
                    weight: [150],
                    rpe: [5],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [1],
                    targetRPE: [5],
                    relevance: [2],
                    weight: [150],
                    rpe: [5],
                  },
                  {
                    targetWeight: [130],
                    targetReps: [2],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [130],
                    rpe: [3],
                  },
                  {
                    targetWeight: [130],
                    targetReps: [2],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [130],
                    rpe: [3],
                  },
                  {
                    targetWeight: [130],
                    targetReps: [2],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [130],
                    rpe: [3],
                  },
                  {
                    targetWeight: [130],
                    targetReps: [2],
                    targetRPE: [0],
                    relevance: [3],
                    weight: [130],
                    rpe: [3],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: ["Ramp 3x4 @5 + Single @4"],
                series: [
                  {
                    targetWeight: [0],
                    targetReps: [4],
                    targetRPE: [3],
                    relevance: [3],
                    weight: [200],
                    rpe: [3],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [4],
                    targetRPE: [4],
                    relevance: [3],
                    weight: [215],
                    rpe: [4],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [4],
                    targetRPE: [5],
                    relevance: [3],
                    weight: [230],
                    rpe: [5],
                  },
                  {
                    targetWeight: [0],
                    targetReps: [1],
                    targetRPE: [4],
                    relevance: [3],
                    weight: [250],
                    rpe: [4],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const deloadBlock = new Block(
        uuidv4(),
        "Deload Block",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        1, // totalWeeks
        1,
        "Meet Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 0; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(8 - week, weekdays[day]);

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              deloadBlock,
              []
            );

            deloadBlockExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            deloadBlock.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(deloadBlock);

      const worldsBlock1ExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 3x5 @6 + 2x5x130",
                  "Ramp 2x5 @6.5 + 3x5x132.5",
                  "Ramp 2x5 @7 + 3x5x135",
                  "Ramp 2x5 @7.5 + 3x5x140",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [4, 5.5, 6, 6.5],
                    relevance: [3, 2, 2, 2],
                    weight: [120, 130, 135, 140],
                    rpe: [4, 5.5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 6.5, 7, 7.5],
                    relevance: [2, 1, 1, 1],
                    weight: [127.5, 140, 145, 150],
                    rpe: [5, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 132.5, 135, 140],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [6, 0, 0, 0],
                    relevance: [1, 3, 3, 3],
                    weight: [135, 132.5, 135, 140],
                    rpe: [6, 4, 4, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140],
                    rpe: [3, 4, 4, 5],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["3x4 @5", "3x5 @5", "3x5 @5.5", "3x5 @5.5"],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 5, 5, 5],
                    targetRPE: [5, 5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [195, 200, 205, 210],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 5, 5, 5],
                    targetRPE: [5, 5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [195, 200, 205, 210],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [195, 200, 205, 210],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                attributes: [
                  {
                    name: "Low2ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x6 @5 + Single @4",
                  "Ramp 3x6 @5.5 + Single @5",
                  "Ramp 3x6 @6 + Single @5",
                  "Ramp 3x6 @6.5 + Single @7",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [3, 3.5, 4, 4.5],
                    relevance: [3, 3, 3, 3],
                    weight: [190, 200, 210, 220],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [4, 4.5, 5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [205, 215, 225, 235],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [5, 5.5, 6, 6.5],
                    relevance: [1, 1, 1, 1],
                    weight: [220, 230, 240, 250],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 5, 7],
                    relevance: [2, 2, 2, 1],
                    weight: [240, 255, 255, 280],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x6x125 + 4x8x115",
                  "1x6x127.5 + 4x8x117.5",
                  "1x6x130 + 4x8x120",
                  "1x6x135 + 1x8x125 + 3x8x120",
                ],
                series: [
                  {
                    targetWeight: [125, 127.5, 130, 135],
                    targetReps: [6, 6, 6, 6],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [125, 127.5, 130, 135],
                    rpe: [4, 5, 4, 5],
                  },
                  {
                    targetWeight: [115, 117.5, 120, 125],
                    targetReps: [8, 8, 8, 8],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 2],
                    weight: [115, 117.5, 120, 125],
                    rpe: [4, 3, 4, 5],
                  },
                  {
                    targetWeight: [115, 117.5, 120, 120],
                    targetReps: [8, 8, 8, 8],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 117.5, 120, 120],
                    rpe: [4, 3, 4, 4],
                  },
                  {
                    targetWeight: [115, 117.5, 120, 120],
                    targetReps: [8, 8, 8, 8],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 117.5, 120, 120],
                    rpe: [4, 3, 4, 4],
                  },
                  {
                    targetWeight: [115, 117.5, 120, 120],
                    targetReps: [8, 8, 8, 8],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [115, 117.5, 120, 120],
                    rpe: [4, 3, 4, 4],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "x5 @5 + x2 @4 + 3x5x180",
                  "x5 @6 + x2 @5 + 3x5x185",
                  "x5 @6.5 + x2 @5 + 3x5x190",
                  "x5 @7.5 + x2 @6 + 3x5x195",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 6, 6.5, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [200, 207.5, 215, 225],
                    rpe: [5, 6, 6.5, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [4, 5, 5, 6],
                    relevance: [2, 2, 2, 2],
                    weight: [210, 217.5, 225, 242.5],
                    rpe: [4, 5, 5, 6],
                  },
                  {
                    targetWeight: [180, 185, 190, 195],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [180, 185, 190, 195],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [180, 185, 190, 195],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [180, 185, 190, 195],
                    rpe: [3, 4, 4, 5],
                  },
                  {
                    targetWeight: [180, 185, 190, 195],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [180, 185, 190, 195],
                    rpe: [3, 4, 4, 5],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x3 @6 + Single @4 + 3x4x130",
                  "Ramp 2x3 @6.5 + Single @5 + 3x4x132.5",
                  "Ramp 2x3 @7 + Single @5 + 3x4x135",
                  "Ramp 2x3 @7.5 + Single @7 + 3x4x140",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 5.5, 6, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [137.5, 140, 145, 150],
                    rpe: [5, 5.5, 6, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [6, 6.5, 7, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [145, 150, 155, 160],
                    rpe: [6, 6.5, 7, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 5, 7],
                    relevance: [1, 1, 1, 1],
                    weight: [152.5, 157.5, 162.5, 172.5],
                    rpe: [4, 5, 5, 7],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140],
                    rpe: [3, 3, 3, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140],
                    rpe: [3, 3, 3, 5],
                  },
                  {
                    targetWeight: [130, 132.5, 135, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [130, 132.5, 135, 140],
                    rpe: [3, 3, 3, 5],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "Ramp 2x4 @5 + Single @4 + 2x4x215",
                  "Ramp 2x4 @5.5 + Single @5 + 2x4x225",
                  "Ramp 2x4 @6 + Single @5 + 2x4x230",
                  "Ramp 2x4 @7.5 + Single @6 + 2x4x235",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [4, 4.5, 5, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [220, 230, 240, 260],
                    rpe: [4, 4.5, 5, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 6, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [235, 245, 255, 275],
                    rpe: [5, 5.5, 6, 7.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 5, 6],
                    relevance: [2, 2, 2, 2],
                    weight: [265, 260, 270, 280],
                    rpe: [4, 5, 5, 6],
                  },
                  {
                    targetWeight: [215, 225, 230, 235],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [215, 225, 230, 235],
                    rpe: [2, 2, 4, 4],
                  },
                  {
                    targetWeight: [215, 225, 230, 235],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [215, 225, 230, 235],
                    rpe: [2, 2, 4, 4],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const worldsBlock1 = new Block(
        uuidv4(),
        "Meet Block 1",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        4, // totalWeeks
        4,
        "Meet Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 3; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(7 - week, weekdays[day]);

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              worldsBlock1,
              []
            );

            worldsBlock1ExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            worldsBlock1.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(worldsBlock1);

      const worldsBlock2ExercisePlan = [
        {
          workouts: [
            [
              {
                name: "Press Banca Pausa 2ct",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "2ctPause-string",
                    title: "tempo-string",
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x5 @6 + 3x5x135",
                  "Ramp 2x5 @7 + 3x5x137.5",
                  "Ramp 2x5 @7.5 + 3x5x140",
                  "Ramp 2x5 @7.5 + 3x5x145",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 6, 6.5, 6.5],
                    relevance: [2, 2, 2, 2],
                    weight: [130, 137.5, 140, 145],
                    rpe: [5, 6, 6.5, 6.5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [6, 7, 7.5, 7.5],
                    relevance: [1, 1, 1, 1],
                    weight: [140, 145, 150, 155],
                    rpe: [6, 7, 7.5, 7.5],
                  },
                  {
                    targetWeight: [135, 137.5, 140, 145],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 137.5, 140, 145],
                    rpe: [4, 5, 5, 5],
                  },
                  {
                    targetWeight: [135, 137.5, 140, 145],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 137.5, 140, 145],
                    rpe: [4, 5, 5, 5],
                  },
                  {
                    targetWeight: [135, 137.5, 140, 145],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 137.5, 140, 145],
                    rpe: [4, 5, 5, 5],
                  },
                ],
              },
              {
                name: "Romanian Deadlift",
                originalName: "romanianDeadlift-string",
                series: [
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [4, 4, 4, 4, 4, 4, 4],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [185, 185, 185, 190, 190, 190, 195],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [5, 5, 5, 5, 5, 5, 5],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [195, 195, 195, 200, 200, 200, 205],
                  },
                  {
                    targetReps: [6, 6, 6, 6, 6, 6, 6],
                    targetRPE: [6, 6, 6, 6, 6, 6, 6],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [205, 205, 205, 210, 210, 210, 215],
                  },
                ],
              },
              {
                name: "Machine Press",
                originalName: "machinePress-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 62.5, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Lateral Raise",
                originalName: "lateralRaise-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [12, 12, 12, 12, 12, 12, 12],
                  },
                ],
              },
            ],

            [
              {
                name: "Sentadilla",
                originalName: "squat-string",
                attributes: [],
                protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
                protocolText: ["4x4 @5", "4x4 @5.5", "4x4 @5.5", "4x4 @5.5"],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [5, 5.5, 5.5, 5.5],
                    relevance: [2, 2, 2, 2],
                    weight: [200, 205, 210, 215],
                  },
                ],
              },
              {
                name: "Sumo Deadlift",
                originalName: "sumoDeadlift-string",
                attributes: [
                  {
                    name: "Low2ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                ],
                protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
                protocolText: [
                  "Ramp 3x5 @5 + Single @4",
                  "Ramp 3x5 @6 + Single @6",
                  "Ramp 3x5 @6.5 + Single @6.5",
                  "Ramp 3x5 @7 + Single @7",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [3, 4, 4.5, 5],
                    relevance: [3, 3, 3, 3],
                    weight: [200, 210, 220, 230],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [4, 5, 5.5, 6],
                    relevance: [2, 2, 2, 2],
                    weight: [215, 225, 235, 245],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [5, 5, 5, 5],
                    targetRPE: [5, 6, 6.5, 7],
                    relevance: [1, 1, 1, 1],
                    weight: [230, 240, 250, 260],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 6, 6.5, 7],
                    relevance: [2, 2, 2, 1],
                    weight: [250, 250, 262.5, 270],
                  },
                ],
              },
              {
                name: "Dumbbell Bench Press",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "Dumbbell-string",
                    title: "equipment-string",
                    subtitle: "",
                  },
                  ,
                ],
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [8, 8, 8, 8, 8, 8, 8],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [38, 40, 40, 42, 42, 44, 44],
                  },
                ],
              },
              {
                name: "Leg Curls",
                originalName: "legCurls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [80, 80, 80, 80, 85, 85, 85],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [0, 0, 0, 0, 0, 0, 0],
                  },
                ],
              },
              {
                name: "Pull Down",
                originalName: "pullDown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 65, 65, 65, 70, 70],
                  },
                ],
              },
              {
                name: "Cable Row",
                originalName: "cableRow-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [60, 60, 60, 60, 65, 65, 65],
                  },
                ],
              },
              {
                name: "Tricep Pushdown",
                originalName: "tricepPushdown-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                  },
                ],
              },
              {
                name: "Curls",
                originalName: "curls-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [16, 16, 16, 16, 16, 18, 18],
                  },
                ],
              },
            ],

            [
              {
                name: "Press Banca Pausa 1ct Spoto Agarre Cerrado",
                originalName: "benchPress-string",
                attributes: [
                  {
                    name: "1ctPause-string",
                    title: Attribute.AttributeTitles.tempo,
                    subtitle: "",
                  },
                  {
                    name: "Spoto-string",
                    title: Attribute.AttributeTitles.rom,
                    subtitle: "",
                  },
                  {
                    name: "CloseGrip-string",
                    title: Attribute.AttributeTitles.stanceGrip,
                    subtitle: "grip-string",
                  },
                ],
                protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
                protocolText: [
                  "1x7x125 + 4x7x117.5",
                  "1x7x127.5 + 4x7x120",
                  "1x7x132.5 + 4x7x122.5",
                  "1x7x135 + 4x7x125",
                ],
                series: [
                  {
                    targetWeight: [125, 127.5, 132.5, 135],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [2, 2, 2, 2],
                    weight: [125, 127.5, 132.5, 135],
                    rpe: [3, 3, 4, 5],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125],
                    rpe: [2, 2, 3, 3],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125],
                    rpe: [2, 2, 3, 3],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125],
                    rpe: [2, 2, 3, 3],
                  },
                  {
                    targetWeight: [117.5, 120, 122.5, 125],
                    targetReps: [7, 7, 7, 7],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [117.5, 120, 122.5, 125],
                    rpe: [2, 2, 3, 3],
                  },
                ],
              },
              {
                name: "Bulgarian Split Squat",
                originalName: "bulgarianSplitSquat-string",
                series: [
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                  {
                    targetReps: [8, 8, 8, 8, 8, 8, 8],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [36, 36, 36, 38, 38, 38, 40],
                  },
                ],
              },
              {
                name: "Leg Extension",
                originalName: "legExtension-string",
                series: [
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                  {
                    targetReps: [10, 10, 10, 10, 10, 10, 10],
                    targetRPE: [9, 9, 9, 9, 9, 9, 9],
                    relevance: [3, 3, 3, 3, 3, 3, 3],
                    weight: [85, 85, 85, 90, 90, 90, 90],
                  },
                ],
              },
            ],

            [
              {
                name: "Competition Squat",
                originalName: "Competition Squat",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
                protocolText: [
                  "x4 @5 + x2 @5 + 3x4x185",
                  "x4 @6 + x2 @6 + 3x4x190",
                  "x4 @7 + x2 @7 + 3x4x195",
                  "Ramp 2x2 @7.5 + 3x4x185",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5, 6, 7, 6.5],
                    relevance: [1, 1, 1, 2],
                    weight: [210, 217.5, 227.5, 232.5],
                    rpe: [5, 6, 7, 5],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5, 6, 7, 7.5],
                    relevance: [2, 2, 2, 1],
                    weight: [220, 230, 242.5, 245],
                    rpe: [5, 6, 7, 6.5],
                  },
                  {
                    targetWeight: [185, 190, 195, 185],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 185],
                    rpe: [3, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 185],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 185],
                    rpe: [3, 3, 3, 3],
                  },
                  {
                    targetWeight: [185, 190, 195, 185],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [185, 190, 195, 185],
                    rpe: [3, 3, 3, 3],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
                protocolText: [
                  "Ramp 2x2 @6 + Single @4 + 3x4x135",
                  "Ramp 2x2 @7 + Single @5 + 3x4x140",
                  "Ramp 2x2 @7.5 + Single @7 + 3x4x145",
                  "Ramp 2x2 @8 + Single @7 + 3x4x140",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [5, 6, 6.5, 7],
                    relevance: [2, 2, 2, 2],
                    weight: [140, 147.5, 157.5, 167.5],
                    rpe: [5, 6, 6.5, 7],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [2, 2, 2, 2],
                    targetRPE: [6, 7, 7.5, 8],
                    relevance: [1, 1, 1, 1],
                    weight: [150, 157.5, 165, 175],
                    rpe: [6, 7, 7.5, 8],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [1, 1, 1, 1],
                    targetRPE: [4, 5, 7, 7],
                    relevance: [1, 1, 1, 1],
                    weight: [155, 162.5, 170, 172.5],
                    rpe: [4, 5, 7, 7],
                  },
                  {
                    targetWeight: [135, 140, 145, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 140],
                    rpe: [3, 3, 3, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 140],
                    rpe: [3, 3, 3, 5],
                  },
                  {
                    targetWeight: [135, 140, 145, 140],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [135, 140, 145, 140],
                    rpe: [3, 3, 3, 5],
                  },
                ],
              },
              {
                name: "Competition Bench Press",
                originalName: "Competition Bench Press",
                attributes: [],
                protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
                protocolText: [
                  "Ramp 2x3 @5 + Single @5 + 2x4x220",
                  "Ramp 2x3 @6 + Single @6 + 2x4x230",
                  "Ramp 2x3 @7 + Single @8 + 2x4x230",
                  "Ramp 2x3 @8 + 3x4x240",
                ],
                series: [
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [4, 5, 6, 7],
                    relevance: [2, 2, 2, 2],
                    weight: [230, 240, 245, 270],
                    rpe: [4, 5, 6, 7],
                  },
                  {
                    targetWeight: [0, 0, 0, 0],
                    targetReps: [3, 3, 3, 3],
                    targetRPE: [5, 6, 7, 8],
                    relevance: [1, 1, 1, 1],
                    weight: [240, 245, 255, 280],
                    rpe: [5, 6, 7, 9],
                  },
                  {
                    targetWeight: [0, 0, 0, 240],
                    targetReps: [1, 1, 1, 4],
                    targetRPE: [5, 6, 8, 0],
                    relevance: [2, 2, 1, 3],
                    weight: [260, 270, 280, 240],
                    rpe: [5, 6, 8, 4],
                  },
                  {
                    targetWeight: [220, 230, 230, 240],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 230, 230, 240],
                    rpe: [3, 3, 3, 4],
                  },
                  {
                    targetWeight: [220, 230, 230, 240],
                    targetReps: [4, 4, 4, 4],
                    targetRPE: [0, 0, 0, 0],
                    relevance: [3, 3, 3, 3],
                    weight: [220, 230, 230, 240],
                    rpe: [3, 3, 3, 4],
                  },
                ],
              },
            ],
          ],
        },
      ];

      const worldsBlock2 = new Block(
        uuidv4(),
        "Meet Block 2",
        "", // notes
        [], // workouts
        false,
        false,
        demoClient,
        4, // totalWeeks
        4,
        "Meet Macro",
        new Date()
      );

      runInAction(() => {
        for (let week = 3; week >= 0; week--) {
          for (let day = 0; day < 4; day++) {
            const startDate = getDateOfSpecificWeekday(3 - week, weekdays[day]);

            if (!firstWorkoutDate || startDate < firstWorkoutDate) {
              firstWorkoutDate = startDate;
            }
            if (!lastWorkoutDate || startDate > lastWorkoutDate) {
              lastWorkoutDate = startDate;
            }

            const newWorkout = new Workout(
              undefined,
              startDate,
              this.coachLanguage === "en" ? `Day ${day + 1}` : `Día ${day + 1}`,
              day + 1, // order
              week + 1, // week
              false,
              worldsBlock2,
              []
            );

            worldsBlock2ExercisePlan[0].workouts[day].forEach(
              (exerciseInfo, index) => {
                const {
                  originalName,
                  name,
                  attributes,
                  series,
                  protocolSlot,
                  protocolText,
                } = exerciseInfo;
                const newExercise = new Exercise(
                  undefined,
                  name,
                  originalName,
                  "", // notes
                  index, // order
                  undefined,
                  index === 0,
                  newWorkout,
                  [],
                  []
                );
                if (protocolSlot) {
                  newExercise.protocolSlot = protocolSlot;
                }
                if (protocolText?.length > 0) {
                  newExercise.protocolText = protocolText[week];
                }

                if (series) {
                  // Add sets/series
                  for (let setIndex = 0; setIndex < series.length; setIndex++) {
                    const serieInfo = series[setIndex];
                    if (serieInfo) {
                      const newSerie = new Serie(
                        undefined,
                        setIndex + 1, // order
                        serieInfo.targetReps[week],
                        serieInfo.rpe
                          ? serieInfo.rpe[week]
                          : serieInfo.targetRPE[week], // Specific targetRPE for this week
                        serieInfo.weight[week], // Specific weight for this week
                        serieInfo.targetReps[week],
                        serieInfo.targetRPE[week], // target_rpe
                        serieInfo.targetWeight
                          ? serieInfo.targetWeight[week]
                          : serieInfo.weight[week], // target_weight
                        newExercise,
                        serieInfo.relevance[week],
                        serieInfo.relevance[week] === 1,
                        serieInfo.relevance[week] === 1
                      );
                      newExercise.series.push(newSerie);
                    }
                  }
                }
                // Add attributes if present
                if (attributes) {
                  attributes.forEach((attribute, attrIndex) => {
                    const newAttribute = new Attribute(
                      String(uuidv4()),
                      attribute.name,
                      attribute.title,
                      attribute.subtitle,
                      attrIndex, // order
                      false, // isPredefined
                      false,
                      null, // date
                      newExercise
                    );
                    newExercise.attributes.push(newAttribute);
                  });
                }

                newWorkout.exercises.push(newExercise);
              }
            );

            worldsBlock2.workouts.push(newWorkout);
          }
        }
      });

      demoClient.blocks.push(worldsBlock2);

      const blocksRef = collection(db, "users", demoClient.id, "blocks");

      // Delete all the blocks in the collection and upload all the blocks the user has in demoClient.blocks
      const existingBlocks = await getDocs(blocksRef);
      const deletePromises = existingBlocks.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // Upload all the new blocks
      const uploadPromises = demoClient.blocks.map(async (block) => {
        const blockRef = doc(blocksRef, block.id);
        await setDoc(blockRef, block.uploadToCloud(new Date()));
      });

      await Promise.all(uploadPromises);

      const demoClientArrayDocRef = doc(
        db,
        "users",
        demoClient.id,
        "arrays",
        "arrays"
      );

      await setDoc(
        demoClientArrayDocRef,
        {
          bodyweights: demoClient.bodyweights.map((bw) => bw.uploadToCloud()),
          tracs: demoClient.tracs.map((trac) => trac.uploadToCloud()),
        },
        { merge: true }
      );
    }

    clientsToChange.forEach((client) => {
      const totalWeeks = randInt(1, 6);
      const selectedWeek = randInt(1, totalWeeks);

      client.setProperty("blockWeeks", totalWeeks);
      client.setProperty("blockCurrentWeek", selectedWeek);
    });
  }
}
